import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Bot, ChevronRight, ChevronLeft, ChevronDown, Check, Minus, Sparkles, Building2, Users, Wrench, MessageSquare, Mail, Plus, Trash2, CalendarDays, Car, MapPin, Upload, AlertTriangle, CreditCard, type LucideIcon } from 'lucide-react';
import { useBusiness } from '../contexts/BusinessContext';
import { getSiteUrl } from '../lib/siteConfig';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../lib/supabaseClient';
import BusinessLogoUpload from './BusinessLogoUpload';
import ResourceModal from './ResourceModal';
import { getTaxRates, createTaxRate, deleteTaxRate, type TaxRate } from '../lib/tax-rates';
import { addPlatformTeamSeat } from '../lib/platformTeamSeats';
import CreateServiceModal from './CreateServiceModal';
import CustomerImportPreviewModal from './CustomerImportPreviewModal';
import CityAutocompleteInput from './CityAutocompleteInput';
import ResourceAvailability from './resources/ResourceAvailability';
import OnlineBookings from '../pages/marketing/OnlineBookings';
import { fetchPostalCodesByCity } from '../services/postalCodeService';
import {
  buildPublicOnlineBookingUrl,
  getOnlineBookingPublicOriginFromPortalRow,
} from '../lib/onlineBookingUrls';
import { createCustomer, updateCustomerTags } from '../lib/customers';
import { activateManualTwilio } from '../lib/twilioManualSetup';
import { normalizePhoneNumber } from '../services/secondaryContactLists';
import {
  getServiceCategories,
  getServices,
  createServiceCategory,
  type ServiceCategory as SvcCategory,
  type Service as SvcService,
} from '../lib/services';

interface AISetupAssistantModalProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * Deep-link target used when an OAuth round-trip (e.g. Gmail connect)
   * navigated the tab away: reopen the wizard directly on this item instead
   * of the generic resume point.
   */
  resumeTarget?: { categoryId: string; itemId: string } | null;
}

/**
 * Nexus AI Set Up Assistant — fresh workflow (v2).
 *
 * Step 0 re-affirms the choice made on the last step of the signup setup
 * wizard (stored in user_setup_status.setup_data.setupChoice):
 *   'self'         → assistant introduces itself as their guide.
 *   'consultation' → a specialist will reach out; they can still be walked
 *                    through everything in the meantime, or close and wait.
 *
 * Subsequent guided steps are added incrementally after step 0.
 */

type SetupChoice = 'self' | 'consultation' | null;

/** Shape returned by the parse-csv-customers edge function (matches the
 *  Customers page import flow and CustomerImportPreviewModal). */
interface CsvCustomer {
  first_name: string;
  last_name: string;
  email: string | null;
  phone: string | null;
  address: string | null;
  notes: string | null;
  status: 'active' | 'inactive';
}

const OPENING_MESSAGES: Record<'self' | 'consultation' | 'unknown', string> = {
  self: `It looks like you chose the self-guided setup — great choice! I'm your setup assistant, and I'll guide you along the way so you're never stuck. We'll get your system running one step at a time.`,
  consultation: `It looks like you chose to have the Nexus team set you up — a setup specialist will be reaching out to you shortly.\n\nWhile you wait, I can still walk you through everything so you can get a head start. Or, if you'd prefer, just close this and wait to be contacted.`,
  unknown: `Welcome to Nexus! I'm your setup assistant, and I'm here to help you get your entire system running — one step at a time.`,
};

/** Display name for a plan app id (strip suffixes + known renames). */
function appDisplayName(appId: string): string {
  const slug = appId.replace('-core-app', '').replace('-addon-app', '');
  const special: Record<string, string> = {
    crm: 'CRM',
    products: 'Store',
    payroll: 'T-Sheets',
    automations: 'Automate',
    'mobile-calendar': 'Field-Pro',
    // Tasks was rebranded to Docs (slug/app id unchanged).
    tasks: 'Docs',
  };
  if (special[slug]) return special[slug];
  return slug.split('-').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

// Apps the assistant must never show or promote: Field-Pro is retired,
// Que and Projects are being discontinued.
const HIDDEN_APP_SLUGS = ['mobile-calendar', 'm-calendar', 'que', 'projects'];
const isHiddenApp = (appId: string) => {
  const slug = appId.replace('-core-app', '').replace('-addon-app', '');
  return HIDDEN_APP_SLUGS.includes(slug);
};

/**
 * Step 2 (the setup hub): configuration categories shown as a timeline in
 * priority order. Users work linearly but can skip and revisit any category.
 * Each category will hold configuration items ("prompts") — the tracker shows
 * completed/total per category. Categories for installed apps get appended
 * here as they're built.
 */
interface SetupCategoryItem {
  id: string;
  title: string;
}

interface SetupCategory {
  id: string;
  title: string;
  description: string;
  icon: LucideIcon;
  items: SetupCategoryItem[];
  /** Show a pitch/intro screen before the first step when the category is untouched. */
  hasIntro?: boolean;
}

export const SETUP_CATEGORIES: SetupCategory[] = [
  {
    id: 'business-info',
    title: 'Business Info',
    description: 'Core details used across your invoices, bookings, and customer-facing pages',
    icon: Building2,
    items: [
      { id: 'confirm-details', title: 'Confirm business details' },
      { id: 'upload-logo', title: 'Upload business logo' },
      { id: 'select-currency', title: 'Select currency' },
      { id: 'tax-rates', title: 'Add tax rates' },
      { id: 'tax-numbers', title: 'Add GST / PST numbers' },
      { id: 'business-hours', title: 'Set business hours' },
    ],
  },
  {
    id: 'team-setup',
    title: 'Team Setup',
    description: 'Add your team members and control what each person can see and do',
    icon: Users,
    items: [
      { id: 'create-team', title: 'Create team members' },
      { id: 'team-hours', title: 'Set working hours' },
    ],
  },
  {
    id: 'services-booking',
    title: 'Services & Online Booking',
    description: 'Create your service menu and decide how customers book with you',
    icon: Wrench,
    items: [], // Populated by servicesBookingItems() — depends on the online-booking answer.
  },
  {
    id: 'payments-invoicing',
    title: 'Payments + Invoicing',
    description: 'Accept credit card payments and get invoices paid faster — powered by Stripe',
    icon: CreditCard,
    items: [
      { id: 'connect-stripe', title: 'Accept card payments' },
    ],
  },
  {
    id: 'messaging',
    title: 'Messaging',
    description: 'Connect your email and texting so every customer conversation lives in one place',
    icon: MessageSquare,
    hasIntro: true,
    items: [
      { id: 'connect-gmail', title: 'Connect Gmail' },
      { id: 'connect-twilio', title: 'Connect texting (SMS)' },
    ],
  },
  {
    id: 'calendar',
    title: 'Calendar',
    description: 'Confirm how you take bookings so your calendar and booking tools match how you work',
    icon: CalendarDays,
    items: [
      { id: 'booking-types', title: 'Confirm your booking types' },
      { id: 'enable-tasks', title: 'Enable events' },
      { id: 'field-quotes', title: 'Mobile estimates (field quotes)' },
    ],
  },
  {
    id: 'customers',
    title: 'Customers',
    description: 'Bring your existing customer list into Nexus so their history starts on day one',
    icon: Users,
    items: [{ id: 'import-customers', title: 'Import your customers' }],
  },
];

/**
 * The Services & Online Booking timeline depends on the yes/no answer to the
 * online-booking question: a "yes" expands the category into the full booking
 * setup path so each stage is its own tracked, resumable step; a "no" (or an
 * unanswered question) keeps the short path.
 */
export function servicesBookingItems(answer: 'yes' | 'no' | null): SetupCategoryItem[] {
  if (answer === 'yes') {
    return [
      { id: 'online-booking', title: 'Online booking' },
      { id: 'service-areas', title: 'Set up service areas' },
      { id: 'staff-booking', title: 'Staff online booking' },
      { id: 'booking-education', title: 'How online booking works' },
      // Separate id from the no-path services step: the assistant teaches a
      // booking-page-oriented way of structuring the menu here.
      { id: 'booking-services', title: 'Create your services' },
      { id: 'booking-flow', title: 'Build your booking page' },
      { id: 'booking-link', title: 'Save your booking link' },
    ];
  }
  return [
    { id: 'online-booking', title: 'Online booking' },
    { id: 'setup-services', title: 'Create your services' },
  ];
}

/** $/seat/mo for extra team seats beyond the plan (same source as My Apps). */
function extraSeatMonthlyUsd(): number {
  const raw = import.meta.env.VITE_PLATFORM_EXTRA_TEAM_SEAT_MONTHLY_USD;
  const n = typeof raw === 'string' ? Number.parseFloat(raw) : Number.NaN;
  return Number.isFinite(n) && n >= 0 ? n : 9.99;
}

interface TeamUsage {
  active: number;
  planIncluded: number;
  purchased: number;
  totalLimit: number;
  isUnlimited: boolean;
}

interface DayHours {
  enabled: boolean;
  open: string;
  close: string;
  closed: boolean;
}
type WeeklyHours = Record<string, DayHours>;

const WEEK_DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;

/** Same defaults as the Business Settings hours form. */
const DEFAULT_WEEKLY_HOURS: WeeklyHours = {
  monday: { enabled: true, open: '09:00', close: '17:00', closed: false },
  tuesday: { enabled: true, open: '09:00', close: '17:00', closed: false },
  wednesday: { enabled: true, open: '09:00', close: '17:00', closed: false },
  thursday: { enabled: true, open: '09:00', close: '17:00', closed: false },
  friday: { enabled: true, open: '09:00', close: '17:00', closed: false },
  saturday: { enabled: true, open: '10:00', close: '15:00', closed: false },
  sunday: { enabled: false, open: '09:00', close: '17:00', closed: true },
};

/** Same options as the Business Settings form. */
const CURRENCY_OPTIONS = [
  { value: 'CAD', label: 'CAD ($) — Canadian Dollar' },
  { value: 'USD', label: 'USD ($) — US Dollar' },
  { value: 'EUR', label: 'EUR (€) — Euro' },
  { value: 'GBP', label: 'GBP (£) — British Pound' },
];

interface PlanRecap {
  planName: string;
  planPrice: number | null;
  trialing: boolean;
  trialDays: number | null;
  includedApps: string[];
  /** Add-ons they don't have (the upsell list). */
  upsellAddons: { id: string; name: string; description: string | null; price: number | null }[];
  /** Apps only available by upgrading to a higher plan (and not sold as add-ons). */
  upgradeOnlyApps: string[];
}

/** One utterance at a time across all bubbles: starting a new one stops the last. */
let stopCurrentSpeech: (() => void) | null = null;

/** Assistant chat bubble with a play button that reads the text aloud. */
function AssistantMessage({ text }: { text: string }) {
  const [speaking, setSpeaking] = useState(false);
  const canSpeak = typeof window !== 'undefined' && 'speechSynthesis' in window;

  const stop = () => {
    window.speechSynthesis.cancel();
    setSpeaking(false);
    if (stopCurrentSpeech === stop) stopCurrentSpeech = null;
  };

  const play = () => {
    if (!canSpeak) return;
    stopCurrentSpeech?.();
    window.speechSynthesis.cancel();

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = 1;
    utterance.onend = stop;
    utterance.onerror = stop;
    stopCurrentSpeech = stop;
    setSpeaking(true);
    window.speechSynthesis.speak(utterance);
  };

  // Don't keep talking after the bubble unmounts (step change, modal close)
  useEffect(() => () => { if (speaking) stop(); }, [speaking]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="flex gap-3">
      <div className="shrink-0 w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
        <Bot className="h-5 w-5 text-white" />
      </div>
      <div className="flex-1 rounded-xl bg-gray-50 border border-gray-200 px-4 py-3 text-[15px] text-gray-800 leading-relaxed">
        <div className="flex items-start gap-2">
          <p className="flex-1 whitespace-pre-wrap">{text}</p>
          {canSpeak && (
            <button
              type="button"
              onClick={speaking ? stop : play}
              aria-label={speaking ? 'Stop reading aloud' : 'Read message aloud'}
              className={`shrink-0 -mr-1 -mt-0.5 h-7 w-7 flex items-center justify-center rounded-full transition-colors touch-manipulation ${
                speaking
                  ? 'bg-blue-100 text-blue-600'
                  : 'text-gray-400 hover:text-blue-600 hover:bg-blue-50'
              }`}
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              {speaking ? (
                <span className="block h-2.5 w-2.5 rounded-[2px] bg-current" />
              ) : (
                <svg viewBox="0 0 24 24" fill="currentColor" className="h-3.5 w-3.5 ml-0.5" aria-hidden>
                  <path d="M8 5.14v13.72c0 .81.87 1.31 1.57.9l11.02-6.86a1.05 1.05 0 000-1.8L9.57 4.24A1.05 1.05 0 008 5.14z" />
                </svg>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AISetupAssistantModal({ isOpen, onClose, resumeTarget }: AISetupAssistantModalProps) {
  const [step, setStep] = useState(0);
  const [setupChoice, setSetupChoice] = useState<SetupChoice>(null);
  const [choiceLoaded, setChoiceLoaded] = useState(false);
  const [planRecap, setPlanRecap] = useState<PlanRecap | null>(null);
  const [recapLoading, setRecapLoading] = useState(false);
  const [selectedAddonIds, setSelectedAddonIds] = useState<Set<string>>(new Set());
  const [addingAddons, setAddingAddons] = useState(false);
  const [addonError, setAddonError] = useState<string | null>(null);
  // Setup hub (step 2): which category is open, and completed item ids per
  // category (persisted in ai_setup_wizard_progress.responses.completed_items).
  const [openCategoryId, setOpenCategoryId] = useState<string | null>(null);
  const [openItemId, setOpenItemId] = useState<string | null>(null);
  const [completedItems, setCompletedItems] = useState<Record<string, string[]>>({});
  const [skippedItems, setSkippedItems] = useState<Record<string, string[]>>({});
  // Synchronous mirrors of the progress maps. logResponse reads these instead
  // of the state so a write fired in the same tick as a state update (e.g. the
  // "category finished" log right after completing its last item) can't
  // overwrite the saved progress with a stale map.
  const completedItemsRef = useRef(completedItems);
  const skippedItemsRef = useRef(skippedItems);
  /** Timeline cards with their step list expanded. */
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  /** After basic setup completes, categories 1–6 collapse into one summary row;
   *  this expands them back out to revisit (e.g. skipped) steps. */
  const [basicListExpanded, setBasicListExpanded] = useState(false);
  /** Set when the user just finished every step in a category (the reward screen). */
  const [celebration, setCelebration] = useState<{ categoryId: string; skippedCount: number } | null>(null);
  // Business-info item: live-editable copy of the businesses row.
  const [bizForm, setBizForm] = useState({ name: '', email: '', phone: '', address: '' });
  const [bizFormLoading, setBizFormLoading] = useState(false);
  const [bizFormSaving, setBizFormSaving] = useState(false);
  const [bizFormError, setBizFormError] = useState<string | null>(null);
  // Currency item state (loaded fresh from the businesses row).
  const [currencyValue, setCurrencyValue] = useState('CAD');
  const [currencyLoading, setCurrencyLoading] = useState(false);
  const [currencySaving, setCurrencySaving] = useState(false);
  const [currencyError, setCurrencyError] = useState<string | null>(null);
  // Business hours item state (businesses.business_hours).
  const [weeklyHours, setWeeklyHours] = useState<WeeklyHours>(DEFAULT_WEEKLY_HOURS);
  const [hoursLoading, setHoursLoading] = useState(false);
  const [hoursSaving, setHoursSaving] = useState(false);
  const [hoursError, setHoursError] = useState<string | null>(null);
  // GST/PST numbers item state (businesses.gst_number / pst_number).
  const [taxNumbers, setTaxNumbers] = useState({ gst_number: '', pst_number: '' });
  const [taxNumbersLoading, setTaxNumbersLoading] = useState(false);
  const [taxNumbersSaving, setTaxNumbersSaving] = useState(false);
  const [taxNumbersError, setTaxNumbersError] = useState<string | null>(null);
  // Create-team item phases: team size (+ seats) → bookable-self question
  // (multiple path only) → account creation.
  const [teamPhase, setTeamPhase] = useState<'size' | 'self-question' | 'create'>('size');
  const [isBookableSelf, setIsBookableSelf] = useState<boolean | null>(null);
  /** Last persisted snapshot of the create-team step, included in every progress write. */
  const [teamSaved, setTeamSaved] = useState<Record<string, unknown>>({});
  const [teamUsage, setTeamUsage] = useState<TeamUsage | null>(null);
  const [teamUsageLoading, setTeamUsageLoading] = useState(false);
  const [teamAnswer, setTeamAnswer] = useState<'just-me' | 'multiple' | null>(null);
  const [teamCount, setTeamCount] = useState(2);
  const [buyingSeats, setBuyingSeats] = useState(false);
  const [teamError, setTeamError] = useState<string | null>(null);
  // Add-team-members item state.
  const [resourceModalOpen, setResourceModalOpen] = useState(false);
  const [teamMembersCreated, setTeamMembersCreated] = useState(0);
  // Live team list — includes members created outside the wizard.
  const [wizardResources, setWizardResources] = useState<
    { id: string; first_name: string; last_name: string; email: string | null; auth_user_id: string | null }[]
  >([]);
  const [wizardResourcesLoading, setWizardResourcesLoading] = useState(false);
  // Online-booking answer — persisted with wizard progress because it decides
  // which steps exist in the Services & Online Booking timeline.
  const [onlineBookingAnswer, setOnlineBookingAnswer] = useState<'yes' | 'no' | null>(null);
  const onlineBookingAnswerRef = useRef<'yes' | 'no' | null>(null);
  // Staff phase: which team member's availability panel is expanded, plus
  // validation state for the "at least one bookable member" gate.
  const [staffExpandedId, setStaffExpandedId] = useState<string | null>(null);
  // Team-hours step (team setup): which member's schedule editor is expanded.
  const [hoursExpandedId, setHoursExpandedId] = useState<string | null>(null);
  const [staffValidating, setStaffValidating] = useState(false);
  const [staffError, setStaffError] = useState<string | null>(null);
  // Booking-flow step: completeness gate + AI audit results.
  const [flowValidating, setFlowValidating] = useState(false);
  const [flowError, setFlowError] = useState<string | null>(null);
  const [flowAudit, setFlowAudit] = useState<{
    issues: { severity: 'error' | 'warning'; title: string; detail: string }[];
    aiSkipped: boolean;
  } | null>(null);
  // Booking-link step: the live page URL for the user to save.
  const [bookingLinkUrl, setBookingLinkUrl] = useState<string | null>(null);
  const [bookingLinkLoading, setBookingLinkLoading] = useState(false);
  const [bookingLinkCopied, setBookingLinkCopied] = useState(false);
  // Inline service-area builder (automatic city lookup only — the manual
  // postal-code entry stays in the Calendar settings modal).
  const [svcAreas, setSvcAreas] = useState<
    { id: string; name: string; postal_codes: string[]; created_at: string | null }[]
  >([]);
  const [svcAreasFixing, setSvcAreasFixing] = useState(false);
  const [svcAreaFormOpen, setSvcAreaFormOpen] = useState(false);
  const [svcAreaCityValue, setSvcAreaCityValue] = useState('');
  const [svcAreaCity, setSvcAreaCity] = useState<{ city: string; state: string; country: 'US' | 'CA' } | null>(null);
  const [svcAreaFetching, setSvcAreaFetching] = useState(false);
  const [svcAreaCodes, setSvcAreaCodes] = useState<string[]>([]);
  const [svcAreaSaving, setSvcAreaSaving] = useState(false);
  const [svcAreaError, setSvcAreaError] = useState<string | null>(null);
  // Services setup state (same tables as the Services page).
  const [svcCategories, setSvcCategories] = useState<SvcCategory[]>([]);
  const [svcServices, setSvcServices] = useState<SvcService[]>([]);
  const [svcLoading, setSvcLoading] = useState(false);
  const [svcError, setSvcError] = useState<string | null>(null);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [savingCategory, setSavingCategory] = useState(false);
  const [serviceModalOpen, setServiceModalOpen] = useState(false);
  /** Category pre-selected in the create-service editor (set by the per-category buttons). */
  const [serviceModalCategoryId, setServiceModalCategoryId] = useState<string | null>(null);
  // Messaging: Gmail connections (same gmail_connections table as settings).
  const [gmailConnections, setGmailConnections] = useState<{ id: string; email: string }[]>([]);
  const [gmailLoading, setGmailLoading] = useState(false);
  // Messaging: Twilio via the one-click Connect flow ONLY (no manual creds in
  // the wizard — advanced users can use the Integrations tab for that).
  const [twilioStatus, setTwilioStatus] = useState<{
    method: string | null;
    accountSid: string | null;
    enabled: boolean;
    phoneNumber: string | null;
  } | null>(null);
  const [twilioLoading, setTwilioLoading] = useState(false);
  const [twilioConnecting, setTwilioConnecting] = useState(false);
  const [twilioAreaCode, setTwilioAreaCode] = useState('');
  const [twilioAvailableNumbers, setTwilioAvailableNumbers] = useState<
    { phoneNumber: string; locality?: string; region?: string }[]
  >([]);
  const [twilioExistingNumbers, setTwilioExistingNumbers] = useState<
    { sid: string; phoneNumber: string; friendlyName?: string; smsCapable?: boolean }[]
  >([]);
  const [twilioSearching, setTwilioSearching] = useState(false);
  const [twilioPurchasing, setTwilioPurchasing] = useState<string | null>(null);
  const [twilioError, setTwilioError] = useState<string | null>(null);
  // Manual credential flow (the automatic Connect flow is paused for now).
  const [twilioManualSid, setTwilioManualSid] = useState('');
  const [twilioManualToken, setTwilioManualToken] = useState('');
  const [twilioManualPhone, setTwilioManualPhone] = useState('');
  const [twilioActivating, setTwilioActivating] = useState(false);
  // Calendar: booking types (mobile / location) — same calendar app settings
  // the signup wizard writes and the FAB + booking modals read.
  const [bookingTypes, setBookingTypes] = useState<{ mobile: boolean; location: boolean } | null>(null);
  const [bookingTypesLoading, setBookingTypesLoading] = useState(false);
  const [bookingTypesSaving, setBookingTypesSaving] = useState(false);
  const [bookingTypesError, setBookingTypesError] = useState<string | null>(null);
  // Calendar: tasks toggle (show_tasks_in_fab calendar app setting).
  const [tasksEnabled, setTasksEnabled] = useState<boolean | null>(null);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [tasksSaving, setTasksSaving] = useState(false);
  const [tasksError, setTasksError] = useState<string | null>(null);
  // Calendar: field quotes toggle (show_field_quotes_in_fab calendar app setting).
  const [fieldQuotesEnabled, setFieldQuotesEnabled] = useState<boolean | null>(null);
  const [fieldQuotesLoading, setFieldQuotesLoading] = useState(false);
  const [fieldQuotesSaving, setFieldQuotesSaving] = useState(false);
  const [fieldQuotesError, setFieldQuotesError] = useState<string | null>(null);
  // Customers: CSV import (same AI parse + preview flow as the Customers page).
  const [custWantsImport, setCustWantsImport] = useState(false);
  const [custFile, setCustFile] = useState<File | null>(null);
  const [custParsing, setCustParsing] = useState(false);
  const [custParsed, setCustParsed] = useState<CsvCustomer[]>([]);
  const [custPreviewOpen, setCustPreviewOpen] = useState(false);
  const [custImportResult, setCustImportResult] = useState<{ imported: number; duplicates: number; errors: number } | null>(null);
  const [custError, setCustError] = useState<string | null>(null);
  // Tax rates item state (same tax_rates table as Business Settings).
  const [taxRates, setTaxRates] = useState<TaxRate[]>([]);
  const [taxLoading, setTaxLoading] = useState(false);
  const [taxSaving, setTaxSaving] = useState(false);
  const [taxError, setTaxError] = useState<string | null>(null);
  const [newTaxName, setNewTaxName] = useState('');
  const [newTaxRate, setNewTaxRate] = useState('');
  const { selectedBusiness, forceLoadBusinesses } = useBusiness();
  const { subscription } = useSubscription();
  const { user } = useAuth();
  const navigate = useNavigate();

  // The live category list: Services & Online Booking expands into the full
  // online-booking path once the user answers "yes" to the question, so the
  // timeline reflects (and resumes to) the exact step they're on.
  const setupCategories: SetupCategory[] = SETUP_CATEGORIES.map((c) =>
    c.id === 'services-booking' ? { ...c, items: servicesBookingItems(onlineBookingAnswer) } : c,
  );

  // Step 2 data: the business's plan + the computed upsell diff, all from live
  // DB data so the recap is always accurate for THIS user.
  useEffect(() => {
    if (!isOpen || step !== 2 || planRecap || !supabase) return;
    let cancelled = false;
    setRecapLoading(true);
    (async () => {
      try {
        const activePlanId = subscription?.active_plan_id || null;
        const [plansRes, addonsRes, activeAddonsRes] = await Promise.all([
          supabase.from('subscription_plans').select('plan_id, plan_name, plan_price, included_apps, trial_days'),
          supabase.from('addon_apps').select('id, name, description, app_id, price_monthly').eq('is_available', true),
          selectedBusiness?.id
            ? supabase
                .from('business_addon_subscriptions')
                .select('addon_app_id')
                .eq('business_id', selectedBusiness.id)
                .eq('status', 'active')
            : Promise.resolve({ data: [] as { addon_app_id: string }[] }),
        ]);
        if (cancelled) return;

        const plans = (plansRes.data || []).filter((p) => p.plan_id !== 'nexus-hosting');
        const plan = plans.find((p) => p.plan_id === activePlanId) || null;
        const includedApps = ((plan?.included_apps as string[]) || []).filter((a) => !isHiddenApp(a));
        const includedSet = new Set(includedApps);
        const ownedAddonIds = new Set((activeAddonsRes.data || []).map((a) => a.addon_app_id));

        // Upsell = available add-ons whose app isn't in their plan and isn't already owned.
        const upsellAddons = (addonsRes.data || [])
          .filter((a) => a.app_id && !includedSet.has(a.app_id) && !ownedAddonIds.has(a.id) && !isHiddenApp(a.app_id))
          .map((a) => {
            const slug = (a.app_id || '').replace('-core-app', '').replace('-addon-app', '');
            return {
              id: a.id,
              // Rebranded apps (e.g. Tasks → Docs) show their current name even
              // if the addon_apps row still has the old one.
              name: slug === 'tasks' ? 'Docs' : (a.name || '').replace(/ App$/, ''),
              description: a.description ?? null,
              price: typeof a.price_monthly === 'number' ? a.price_monthly : Number(a.price_monthly) || null,
            };
          })
          .sort((a, b) => (b.price || 0) - (a.price || 0));

        // Apps that only exist in pricier plans and aren't purchasable as add-ons.
        const addonAppIds = new Set((addonsRes.data || []).map((a) => a.app_id));
        const higherPlans = plans.filter((p) => (p.plan_price || 0) > (plan?.plan_price || 0));
        const upgradeOnlyApps = [
          ...new Set(
            higherPlans
              .flatMap((p) => (p.included_apps as string[]) || [])
              .filter((a) => !includedSet.has(a) && !addonAppIds.has(a) && !isHiddenApp(a)),
          ),
        ];

        setPlanRecap({
          planName: plan?.plan_name || 'your current plan',
          planPrice: plan?.plan_price ?? null,
          trialing: subscription?.subscription_status === 'trialing',
          trialDays: plan?.trial_days ?? null,
          includedApps,
          upsellAddons,
          upgradeOnlyApps,
        });
      } catch (err) {
        if (!cancelled) console.error('Failed to load plan recap:', err);
      } finally {
        if (!cancelled) setRecapLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, step, subscription?.active_plan_id, selectedBusiness?.id]);

  // Highest step index that exists in the current workflow (clamp for resumes
  // saved by older/newer versions).
  const MAX_STEP = 3;

  // On open: load the signup-wizard choice (for the step-0 message) AND the
  // saved assistant progress, so the wizard always reopens on the step the
  // business left off on.
  useEffect(() => {
    if (!isOpen) {
      setChoiceLoaded(false);
      setStep(0);
      setOpenCategoryId(null);
      setOpenItemId(null);
      setCelebration(null);
      // Once basic setup is 6/6, the wizard should always reopen with the
      // categories collapsed to the summary row (and step lists closed).
      setBasicListExpanded(false);
      setExpandedCategories(new Set());
      return;
    }
    if (!user?.id || !supabase) {
      setChoiceLoaded(true);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const [statusRes, progressRes] = await Promise.all([
          supabase.from('user_setup_status').select('setup_data').eq('user_id', user.id).maybeSingle(),
          selectedBusiness?.id
            ? supabase
                .from('ai_setup_wizard_progress')
                .select('current_step, completed, responses')
                .eq('business_id', selectedBusiness.id)
                .maybeSingle()
            : Promise.resolve({ data: null }),
        ]);
        if (cancelled) return;

        const choice = (statusRes.data?.setup_data as { setupChoice?: string } | null)?.setupChoice;
        setSetupChoice(choice === 'self' || choice === 'consultation' ? choice : null);

        // Restore per-category configuration progress for the setup hub.
        const savedResponses = progressRes.data?.responses as {
          completed_items?: Record<string, string[]>;
          skipped_items?: Record<string, string[]>;
          online_booking_answer?: 'yes' | 'no' | null;
          team_setup?: {
            answer?: 'just-me' | 'multiple' | null;
            count?: number;
            bookable_self?: boolean | null;
            phase?: 'size' | 'self-question' | 'create';
            members_created?: number;
          };
        } | null;
        if (savedResponses?.completed_items && typeof savedResponses.completed_items === 'object') {
          completedItemsRef.current = savedResponses.completed_items;
          setCompletedItems(savedResponses.completed_items);
        }
        if (savedResponses?.skipped_items && typeof savedResponses.skipped_items === 'object') {
          skippedItemsRef.current = savedResponses.skipped_items;
          setSkippedItems(savedResponses.skipped_items);
        }
        // Restore the online-booking answer — it shapes the services timeline.
        if (savedResponses?.online_booking_answer === 'yes' || savedResponses?.online_booking_answer === 'no') {
          onlineBookingAnswerRef.current = savedResponses.online_booking_answer;
          setOnlineBookingAnswer(savedResponses.online_booking_answer);
        }
        // Rehydrate the create-team step so reopening resumes mid-flow (e.g.
        // "multiple" chosen, self already created → back to adding the team).
        const savedTeam = savedResponses?.team_setup;
        if (savedTeam && typeof savedTeam === 'object' && savedTeam.phase) {
          setTeamSaved(savedTeam as Record<string, unknown>);
          if (savedTeam.answer === 'just-me' || savedTeam.answer === 'multiple') setTeamAnswer(savedTeam.answer);
          if (typeof savedTeam.count === 'number') setTeamCount(savedTeam.count);
          if (typeof savedTeam.bookable_self === 'boolean') setIsBookableSelf(savedTeam.bookable_self);
          if (typeof savedTeam.members_created === 'number') setTeamMembersCreated(savedTeam.members_created);
          setTeamPhase(savedTeam.phase);
        }

        // Resume where they left off (completed wizards restart at the top).
        const saved = progressRes.data;
        if (saved && !saved.completed && typeof saved.current_step === 'number') {
          setStep(Math.min(Math.max(0, saved.current_step), MAX_STEP));
        } else {
          setStep(0);
        }

        // OAuth deep-link resume: jump straight back to the exact item the
        // user was on when the OAuth redirect navigated the tab away.
        if (resumeTarget) {
          const targetCategory = setupCategories.find((c) => c.id === resumeTarget.categoryId);
          const targetItem = targetCategory?.items.find((i) => i.id === resumeTarget.itemId);
          if (targetCategory && targetItem) {
            setStep(3);
            setOpenCategoryId(targetCategory.id);
            setOpenItemId(targetItem.id);
          }
        }
      } catch (err) {
        if (!cancelled) console.error('Failed to load setup assistant state:', err);
      } finally {
        if (!cancelled) setChoiceLoaded(true);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, user?.id, selectedBusiness?.id]);

  // Log each response so the assistant (and future AI steps) have context.
  const logResponse = async (fromStep: number, responseKey: string, extra: Record<string, unknown> = {}) => {
    if (!selectedBusiness?.id || !supabase) return;
    try {
      // Every write carries the full progress maps so no single button press
      // can overwrite previously saved completion/skip state. Read the refs
      // (not the state) so writes issued in the same tick as an update still
      // carry the freshest maps.
      const responses = {
        setup_choice: setupChoice,
        completed_items: completedItemsRef.current,
        skipped_items: skippedItemsRef.current,
        team_setup: teamSaved,
        online_booking_answer: onlineBookingAnswerRef.current,
        ...extra,
      };
      await supabase.from('ai_setup_wizard_responses').insert({
        business_id: selectedBusiness.id,
        user_id: user?.id ?? null,
        step: fromStep,
        response_key: responseKey,
        response_value: responses,
      });
      await supabase.from('ai_setup_wizard_progress').upsert(
        {
          business_id: selectedBusiness.id,
          current_step: fromStep + 1,
          completed: false,
          responses,
          updated_at: new Date().toISOString(),
        },
        { onConflict: 'business_id' }
      );
    } catch (err) {
      console.error('Failed to save AI setup progress:', err);
    }
  };

  // Load the business row fresh whenever the confirm-details item opens, so
  // the fields always reflect (and edit) the live database values.
  useEffect(() => {
    if (openItemId !== 'confirm-details' || !selectedBusiness?.id || !supabase) return;
    let cancelled = false;
    setBizFormLoading(true);
    setBizFormError(null);
    (async () => {
      try {
        const { data, error } = await supabase
          .from('businesses')
          .select('name, email, phone, address')
          .eq('id', selectedBusiness.id)
          .single();
        if (cancelled) return;
        if (error) throw error;
        setBizForm({
          name: data?.name || '',
          email: data?.email || '',
          phone: data?.phone || '',
          address: data?.address || '',
        });
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load business info:', err);
          setBizFormError('Could not load your business info. Close and reopen this step to retry.');
        }
      } finally {
        if (!cancelled) setBizFormLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [openItemId, selectedBusiness?.id]);

  // Load the saved currency fresh when the currency item opens.
  useEffect(() => {
    if (openItemId !== 'select-currency' || !selectedBusiness?.id || !supabase) return;
    let cancelled = false;
    setCurrencyLoading(true);
    setCurrencyError(null);
    (async () => {
      try {
        const { data, error } = await supabase
          .from('businesses')
          .select('currency')
          .eq('id', selectedBusiness.id)
          .single();
        if (cancelled) return;
        if (error) throw error;
        setCurrencyValue(data?.currency || 'CAD');
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load currency:', err);
          setCurrencyError('Could not load your current currency. Close and reopen this step to retry.');
        }
      } finally {
        if (!cancelled) setCurrencyLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [openItemId, selectedBusiness?.id]);

  const handleSaveCurrency = async () => {
    if (!selectedBusiness?.id || !supabase) return;
    setCurrencySaving(true);
    setCurrencyError(null);
    try {
      const { error } = await supabase
        .from('businesses')
        .update({ currency: currencyValue, updated_at: new Date().toISOString() })
        .eq('id', selectedBusiness.id);
      if (error) throw error;
      await markItemComplete('business-info', 'select-currency');
      advanceAfterItem(businessInfoCategory, 'select-currency');
    } catch (err) {
      console.error('Failed to save currency:', err);
      setCurrencyError('Could not save. Please try again.');
    } finally {
      setCurrencySaving(false);
    }
  };

  // Load existing tax rates fresh when the tax item opens.
  useEffect(() => {
    if (openItemId !== 'tax-rates' || !selectedBusiness?.id) return;
    let cancelled = false;
    setTaxLoading(true);
    setTaxError(null);
    (async () => {
      try {
        const rates = await getTaxRates(selectedBusiness.id);
        if (!cancelled) setTaxRates(rates);
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load tax rates:', err);
          setTaxError('Could not load your tax rates. Close and reopen this step to retry.');
        }
      } finally {
        if (!cancelled) setTaxLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [openItemId, selectedBusiness?.id]);

  const handleAddTaxRate = async () => {
    if (!selectedBusiness?.id) return;
    const name = newTaxName.trim();
    const rate = Number.parseFloat(newTaxRate);
    if (!name) {
      setTaxError('Enter a tax name (e.g. GST)');
      return;
    }
    if (!Number.isFinite(rate) || rate <= 0 || rate > 100) {
      setTaxError('Enter a rate between 0 and 100 (e.g. 5 for 5%)');
      return;
    }
    setTaxSaving(true);
    setTaxError(null);
    try {
      const created = await createTaxRate({
        business_id: selectedBusiness.id,
        name,
        description: '',
        rate,
        is_compound: false,
        // First rate becomes the default, matching Business Settings behavior.
        is_default: taxRates.length === 0,
      });
      setTaxRates((prev) => [...prev, created]);
      setNewTaxName('');
      setNewTaxRate('');
    } catch (err) {
      console.error('Failed to add tax rate:', err);
      setTaxError('Could not add the tax rate. Please try again.');
    } finally {
      setTaxSaving(false);
    }
  };

  const handleDeleteTaxRate = async (rate: TaxRate) => {
    setTaxSaving(true);
    setTaxError(null);
    try {
      await deleteTaxRate(rate.id);
      setTaxRates((prev) => prev.filter((r) => r.id !== rate.id));
    } catch (err) {
      console.error('Failed to delete tax rate:', err);
      setTaxError('Could not remove the tax rate. Please try again.');
    } finally {
      setTaxSaving(false);
    }
  };

  // Load the saved GST/PST numbers fresh when the tax-numbers item opens.
  useEffect(() => {
    if (openItemId !== 'tax-numbers' || !selectedBusiness?.id || !supabase) return;
    let cancelled = false;
    setTaxNumbersLoading(true);
    setTaxNumbersError(null);
    (async () => {
      try {
        const { data, error } = await supabase
          .from('businesses')
          .select('gst_number, pst_number')
          .eq('id', selectedBusiness.id)
          .single();
        if (cancelled) return;
        if (error) throw error;
        setTaxNumbers({ gst_number: data?.gst_number || '', pst_number: data?.pst_number || '' });
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load tax numbers:', err);
          setTaxNumbersError('Could not load your tax numbers. Close and reopen this step to retry.');
        }
      } finally {
        if (!cancelled) setTaxNumbersLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [openItemId, selectedBusiness?.id]);

  const handleSaveTaxNumbers = async () => {
    if (!selectedBusiness?.id || !supabase) return;
    if (!taxNumbers.gst_number.trim() && !taxNumbers.pst_number.trim()) {
      setTaxNumbersError('Enter at least one number, or use "Skip for now" below.');
      return;
    }
    setTaxNumbersSaving(true);
    setTaxNumbersError(null);
    try {
      const { error } = await supabase
        .from('businesses')
        .update({
          gst_number: taxNumbers.gst_number.trim() || null,
          pst_number: taxNumbers.pst_number.trim() || null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', selectedBusiness.id);
      if (error) throw error;
      await markItemComplete('business-info', 'tax-numbers');
      advanceAfterItem(businessInfoCategory, 'tax-numbers');
    } catch (err) {
      console.error('Failed to save tax numbers:', err);
      setTaxNumbersError('Could not save. Please try again.');
    } finally {
      setTaxNumbersSaving(false);
    }
  };

  // Load business hours fresh when the hours item opens.
  useEffect(() => {
    if (openItemId !== 'business-hours' || !selectedBusiness?.id || !supabase) return;
    let cancelled = false;
    setHoursLoading(true);
    setHoursError(null);
    (async () => {
      try {
        const { data, error } = await supabase
          .from('businesses')
          .select('business_hours')
          .eq('id', selectedBusiness.id)
          .single();
        if (cancelled) return;
        if (error) throw error;
        setWeeklyHours({ ...DEFAULT_WEEKLY_HOURS, ...((data?.business_hours as WeeklyHours) || {}) });
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load business hours:', err);
          setHoursError('Could not load your business hours. Close and reopen this step to retry.');
        }
      } finally {
        if (!cancelled) setHoursLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [openItemId, selectedBusiness?.id]);

  const handleSaveBusinessHours = async () => {
    if (!selectedBusiness?.id || !supabase) return;
    const openDays = WEEK_DAYS.filter((d) => !weeklyHours[d]?.closed);
    if (openDays.length === 0) {
      setHoursError('At least one day needs to be open, or your calendar will have no bookable slots.');
      return;
    }
    for (const day of openDays) {
      const h = weeklyHours[day];
      if (h.open >= h.close) {
        setHoursError(`${day.charAt(0).toUpperCase() + day.slice(1)}: closing time must be after opening time.`);
        return;
      }
    }
    setHoursSaving(true);
    setHoursError(null);
    try {
      const { error } = await supabase
        .from('businesses')
        .update({ business_hours: weeklyHours, updated_at: new Date().toISOString() })
        .eq('id', selectedBusiness.id);
      if (error) throw error;
      await markItemComplete('business-info', 'business-hours');
      advanceAfterItem(businessInfoCategory, 'business-hours');
    } catch (err) {
      console.error('Failed to save business hours:', err);
      setHoursError('Could not save. Please try again.');
    } finally {
      setHoursSaving(false);
    }
  };

  const isItemCompleted = (categoryId: string, itemId: string) =>
    (completedItems[categoryId] || []).includes(itemId);
  const isItemSkipped = (categoryId: string, itemId: string) =>
    !isItemCompleted(categoryId, itemId) && (skippedItems[categoryId] || []).includes(itemId);

  /** Where to pick up in a category: first pending step (never a skipped one), then first skipped, then the first item for review. */
  const firstActionableItem = (category: SetupCategory) =>
    category.items.find((i) => !isItemCompleted(category.id, i.id) && !isItemSkipped(category.id, i.id)) ||
    category.items.find((i) => !isItemCompleted(category.id, i.id)) ||
    category.items[0] ||
    null;

  /** Per-category progress rollup used by the hub timeline. */
  const categoryProgress = (category: SetupCategory) => {
    const doneIds = new Set(
      (completedItems[category.id] || []).filter((id) => category.items.some((item) => item.id === id)),
    );
    const done = doneIds.size;
    const total = category.items.length;
    const skippedCount = category.items.filter(
      (item) => !doneIds.has(item.id) && (skippedItems[category.id] || []).includes(item.id),
    ).length;
    const allDone = total > 0 && done >= total;
    // Every step handled, but some were skipped: "finished, worth revisiting".
    const doneWithSkips = total > 0 && !allDone && done + skippedCount >= total;
    return { doneIds, done, total, skippedCount, allDone, doneWithSkips, handled: allDone || doneWithSkips };
  };

  /** True once every basic category (1–6) has all steps completed or skipped. */
  const basicSetupDone = setupCategories.every((c) => categoryProgress(c).handled);
  /** Total skipped (not completed) steps across the basic categories. */
  const totalSkippedSteps = setupCategories.reduce((sum, c) => sum + categoryProgress(c).skippedCount, 0);

  /** Open a category straight into the step the user left off on. Categories
   *  with an intro show it first while the category is untouched. */
  const openCategory = (category: SetupCategory) => {
    setOpenCategoryId(category.id);
    const untouched =
      (completedItems[category.id] || []).length === 0 && (skippedItems[category.id] || []).length === 0;
    if (category.hasIntro && untouched) {
      setOpenItemId(null); // renders the category intro screen
      return;
    }
    setOpenItemId(firstActionableItem(category)?.id ?? null);
  };

  /**
   * After completing/skipping a step: open the next pending step, or return to
   * the timeline — with a celebration screen when the whole category was just
   * finished (every step completed or skipped).
   */
  const advanceAfterItem = (
    category: SetupCategory,
    afterItemId: string,
    opts?: { skippedCurrent?: boolean },
  ) => {
    const index = category.items.findIndex((i) => i.id === afterItemId);
    const next = category.items
      .slice(index + 1)
      .find((i) => !isItemCompleted(category.id, i.id) && !isItemSkipped(category.id, i.id));
    if (next) {
      setOpenItemId(next.id);
      return;
    }

    // The state maps may not include the just-handled item yet (React state is
    // async) — evaluate the category excluding it, then account for it via opts.
    const others = category.items.filter((i) => i.id !== afterItemId);
    const pendingOthers = others.filter(
      (i) => !isItemCompleted(category.id, i.id) && !isItemSkipped(category.id, i.id),
    );
    if (pendingOthers.length === 0) {
      const skippedCount =
        others.filter((i) => isItemSkipped(category.id, i.id)).length + (opts?.skippedCurrent ? 1 : 0);
      setCelebration({ categoryId: category.id, skippedCount });
      void logResponse(2, `${category.id}:finished`, { skipped_count: skippedCount });
    }
    setOpenItemId(null);
    setOpenCategoryId(null);
  };

  // Load plan seat usage when the create-team item opens (fresh from the DB
  // RPC), and always start at the team-size question.
  useEffect(() => {
    if (openItemId !== 'create-team' || !selectedBusiness?.id || !supabase) return;
    // Only start from the size question when there's no saved mid-flow state.
    if (!teamSaved.phase) {
      setTeamPhase('size');
      setIsBookableSelf(null);
    }
    let cancelled = false;
    setTeamUsageLoading(true);
    setTeamError(null);
    (async () => {
      try {
        const { data, error } = await supabase.rpc('get_resource_usage', {
          p_business_id: selectedBusiness.id,
        });
        if (cancelled) return;
        if (error) throw error;
        const row = (Array.isArray(data) ? data[0] : data) as {
          active_count?: number;
          total_limit?: number;
          plan_included_limit?: number;
          purchased_additional_seats?: number;
        } | null;
        const planIncluded = row?.plan_included_limit ?? 0;
        const purchased = row?.purchased_additional_seats ?? 0;
        const totalLimit = row?.total_limit ?? planIncluded + purchased;
        setTeamUsage({
          active: row?.active_count ?? 0,
          planIncluded,
          purchased,
          totalLimit,
          // resource_limit 0 = unlimited (RPC reports a huge number).
          isUnlimited: planIncluded === 0 || totalLimit > 1_000_000,
        });
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load team usage:', err);
          setTeamError('Could not load your plan limits. Close and reopen this step to retry.');
        }
      } finally {
        if (!cancelled) setTeamUsageLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [openItemId, selectedBusiness?.id]);

  /** Load the business's active team members (fresh — includes ones created outside the wizard). */
  const loadWizardResources = async () => {
    if (!selectedBusiness?.id || !supabase) return;
    setWizardResourcesLoading(true);
    try {
      const { data } = await supabase
        .from('resources')
        .select('id, first_name, last_name, email, auth_user_id')
        .eq('business_id', selectedBusiness.id)
        .eq('is_active', true)
        .order('created_at');
      setWizardResources(data || []);
    } catch (err) {
      console.error('Failed to load team members:', err);
    } finally {
      setWizardResourcesLoading(false);
    }
  };

  // Refresh the team list whenever the create-team or set-hours item opens.
  useEffect(() => {
    if (openItemId === 'create-team' || openItemId === 'team-hours') {
      if (openItemId === 'team-hours') setHoursExpandedId(null);
      void loadWizardResources();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openItemId, selectedBusiness?.id]);

  /** A resource linked to the owner's login already exists (maybe made outside the wizard). */
  const adminLinkedExists = wizardResources.some(
    (r) => r.auth_user_id && r.auth_user_id === selectedBusiness?.owner_id,
  );

  /** Seats they'd need beyond the plan for the chosen team size. */
  const extraSeatsNeeded = (() => {
    if (!teamUsage || teamUsage.isUnlimited || teamAnswer !== 'multiple') return 0;
    return Math.max(0, teamCount - teamUsage.totalLimit);
  })();

  const teamSetupCategory = setupCategories.find((c) => c.id === 'team-setup')!;

  /** Confirm the team size — buying extra seats first when needed. */
  const handleConfirmTeamSize = async () => {
    if (!selectedBusiness?.id || !teamAnswer) return;
    setTeamError(null);

    if (extraSeatsNeeded > 0) {
      setBuyingSeats(true);
      try {
        // Seats are added one Stripe call at a time (same flow as My Apps).
        // On a trial subscription they bill when the trial converts.
        for (let i = 0; i < extraSeatsNeeded; i++) {
          const result = await addPlatformTeamSeat(selectedBusiness.id);
          if (!result.success) {
            setTeamError(
              `Added ${i} of ${extraSeatsNeeded} extra seats, then hit a problem: ${result.error}. You can retry, or continue and add seats later from Nexus Connect.`,
            );
            setBuyingSeats(false);
            return;
          }
        }
      } finally {
        setBuyingSeats(false);
      }
    }

    // Multiple → first ask whether the owner is bookable themselves — unless a
    // resource linked to their login already exists (set up outside the wizard);
    // just-me → straight to creating their linked profile.
    const nextPhase = teamAnswer === 'multiple' && !adminLinkedExists ? 'self-question' : 'create';
    setTeamPhase(nextPhase);
    const snapshot = {
      answer: teamAnswer,
      count: teamAnswer === 'just-me' ? 1 : teamCount,
      bookable_self: isBookableSelf,
      phase: nextPhase,
      members_created: teamMembersCreated,
    };
    setTeamSaved(snapshot);
    await logResponse(2, 'team-size:answered', {
      extra_seats_purchased: extraSeatsNeeded,
      team_setup: snapshot,
    });
  };

  const handleBookableSelfAnswer = async (bookable: boolean) => {
    setIsBookableSelf(bookable);
    setTeamPhase('create');
    const snapshot = {
      answer: teamAnswer,
      count: teamCount,
      bookable_self: bookable,
      phase: 'create',
      members_created: teamMembersCreated,
    };
    setTeamSaved(snapshot);
    await logResponse(2, 'team-self-bookable:answered', { team_setup: snapshot });
  };

  /** In the multiple+bookable path the FIRST profile created is the owner's own (linked) —
   *  never when their linked profile already exists. */
  const creatingSelfProfile =
    !adminLinkedExists &&
    (teamAnswer === 'just-me' || (isBookableSelf === true && teamMembersCreated === 0));

  const servicesCategory = setupCategories.find((c) => c.id === 'services-booking')!;

  /** Load categories + services fresh (includes anything created outside the wizard). */
  const loadWizardServices = async () => {
    if (!selectedBusiness?.id) return;
    setSvcLoading(true);
    setSvcError(null);
    try {
      const [cats, svcs] = await Promise.all([
        getServiceCategories(selectedBusiness.id),
        getServices(selectedBusiness.id),
      ]);
      setSvcCategories(cats);
      setSvcServices(svcs);
    } catch (err) {
      console.error('Failed to load services:', err);
      setSvcError('Could not load your services. Close and reopen this step to retry.');
    } finally {
      setSvcLoading(false);
    }
  };

  /** Load the business's service areas (drives the online-booking gate UI). */
  const loadServiceAreas = async () => {
    if (!selectedBusiness?.id || !supabase) return;
    try {
      const { data, error } = await supabase
        .from('service_areas')
        .select('id, name, postal_codes, created_at')
        .eq('business_id', selectedBusiness.id)
        .order('created_at', { ascending: false });
      if (error) throw error;
      setSvcAreas(data || []);
    } catch (err) {
      console.error('Failed to load service areas:', err);
    }
  };

  /** Postal codes that appear in more than one service area (same logic as settings). */
  const svcAreaOverlaps = (() => {
    const counts = new Map<string, number>();
    svcAreas.forEach((area) => area.postal_codes.forEach((code) => counts.set(code, (counts.get(code) || 0) + 1)));
    const overlapping = new Set<string>();
    counts.forEach((count, code) => {
      if (count > 1) overlapping.add(code);
    });
    return overlapping;
  })();

  /**
   * Remove duplicated postal codes, keeping each one in the oldest area that
   * has it (same rule as the settings modal). Areas left with no codes are
   * deleted so no useless empty area lingers in the wizard list.
   */
  const handleFixSvcAreaOverlaps = async () => {
    if (!supabase || svcAreaOverlaps.size === 0) return;
    setSvcAreasFixing(true);
    try {
      const sorted = [...svcAreas].sort((a, b) => {
        const aDate = a.created_at ? new Date(a.created_at).getTime() : 0;
        const bDate = b.created_at ? new Date(b.created_at).getTime() : 0;
        return aDate - bDate;
      });
      for (const area of sorted) {
        const updatedCodes = area.postal_codes.filter((code) => {
          if (!svcAreaOverlaps.has(code)) return true;
          const firstAreaWithCode = sorted.find((a) => a.postal_codes.includes(code));
          return firstAreaWithCode?.id === area.id;
        });
        if (updatedCodes.length === area.postal_codes.length) continue;
        if (updatedCodes.length === 0) {
          const { error } = await supabase.from('service_areas').delete().eq('id', area.id);
          if (error) throw error;
        } else {
          const { error } = await supabase
            .from('service_areas')
            .update({ postal_codes: updatedCodes })
            .eq('id', area.id);
          if (error) throw error;
        }
      }
      await loadServiceAreas();
    } catch (err) {
      console.error('Failed to fix overlapping postal codes:', err);
      setSvcAreaError('Could not fix the overlaps. Please try again.');
    } finally {
      setSvcAreasFixing(false);
    }
  };

  const resetSvcAreaForm = () => {
    setSvcAreaFormOpen(false);
    setSvcAreaCityValue('');
    setSvcAreaCity(null);
    setSvcAreaCodes([]);
    setSvcAreaFetching(false);
    setSvcAreaError(null);
  };

  useEffect(() => {
    if (openItemId === 'setup-services' || openItemId === 'booking-services') {
      void loadWizardServices();
    }
    if (openItemId === 'service-areas') {
      resetSvcAreaForm();
      void loadServiceAreas();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openItemId, selectedBusiness?.id]);

  /** City → postal codes, trying alternative locality names like the settings modal does. */
  const fetchSvcAreaCodes = async (
    primaryCity: string,
    alternativeCities: string[],
    state: string,
    country: 'US' | 'CA',
  ) => {
    setSvcAreaFetching(true);
    setSvcAreaError(null);
    setSvcAreaCodes([]);
    const citiesToTry = [primaryCity, ...alternativeCities.filter((c) => c !== primaryCity)];
    for (const cityName of citiesToTry) {
      try {
        const cleanedCity = cityName.trim().replace(/^(Metro|Greater|Greater Metro)\s+/i, '');
        const result = await fetchPostalCodesByCity(cleanedCity, state, country);
        if (result.success && result.postalCodes.length > 0) {
          setSvcAreaCodes(result.postalCodes);
          setSvcAreaCity({ city: cleanedCity, state, country });
          setSvcAreaFetching(false);
          return;
        }
      } catch (err) {
        console.error(`Postal code lookup failed for ${cityName}:`, err);
      }
    }
    setSvcAreaError(`No postal codes found for ${primaryCity}, ${state}. Try a more specific city name.`);
    setSvcAreaFetching(false);
  };

  /** Save the looked-up city as a service area (same table as settings). */
  const handleCreateServiceArea = async () => {
    if (!selectedBusiness?.id || !supabase || !svcAreaCity || svcAreaCodes.length === 0) return;
    setSvcAreaSaving(true);
    setSvcAreaError(null);
    try {
      const { error } = await supabase.from('service_areas').insert({
        business_id: selectedBusiness.id,
        name: `${svcAreaCity.city}, ${svcAreaCity.state}`,
        postal_codes: svcAreaCodes,
      });
      if (error) throw error;
      resetSvcAreaForm();
      await loadServiceAreas();
    } catch (err) {
      console.error('Failed to create service area:', err);
      setSvcAreaError('Could not save the service area. Please try again.');
    } finally {
      setSvcAreaSaving(false);
    }
  };

  const handleDeleteServiceArea = async (areaId: string) => {
    if (!supabase) return;
    try {
      const { error } = await supabase.from('service_areas').delete().eq('id', areaId);
      if (error) throw error;
      setSvcAreas((prev) => prev.filter((a) => a.id !== areaId));
    } catch (err) {
      console.error('Failed to delete service area:', err);
    }
  };

  // Refresh the team list when the staff online-booking step opens.
  useEffect(() => {
    if (openItemId === 'staff-booking') {
      setStaffError(null);
      setStaffExpandedId(null);
      void loadWizardResources();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openItemId, selectedBusiness?.id]);

  /**
   * Gate for leaving the staff phase: at least one team member must have
   * online booking on with a day that has BOTH hours and service areas —
   * otherwise the booking page would never offer a single time slot.
   */
  const validateStaffOnlineBooking = async (): Promise<boolean> => {
    if (!selectedBusiness?.id || !supabase) return false;
    const { data, error } = await supabase
      .from('mobile_availability')
      .select('resource_id, working_hours')
      .eq('business_id', selectedBusiness.id);
    if (error) {
      console.error('Failed to check staff online-booking readiness:', error);
      return false;
    }
    return (data || []).some((row) => {
      const workingHours = (row.working_hours || {}) as Record<
        string,
        { enabled?: boolean; shifts?: unknown[]; areas?: unknown[] } | undefined
      >;
      return Object.values(workingHours).some(
        (day) => day?.enabled && (day.shifts?.length || 0) > 0 && (day.areas?.length || 0) > 0,
      );
    });
  };

  const completeFlowStep = async () => {
    setFlowAudit(null);
    await markItemComplete('services-booking', 'booking-flow');
    advanceAfterItem(servicesCategory, 'booking-flow');
  };

  // Load the public booking URL when the save-your-link step opens (same
  // origin/slug rules as the Marketing page's Preview button).
  useEffect(() => {
    if (openItemId !== 'booking-link' || !selectedBusiness?.id || !supabase) return;
    let cancelled = false;
    setBookingLinkLoading(true);
    setBookingLinkCopied(false);
    (async () => {
      try {
        const { data: portals } = await supabase
          .from('online_booking_portals')
          .select('id, is_default, public_slug, custom_hostname, custom_host_verified_at')
          .eq('business_id', selectedBusiness.id)
          .order('is_default', { ascending: false })
          .order('sort_order', { ascending: true });
        if (cancelled) return;
        const portal = (portals || [])[0] ?? null;
        const slug = (selectedBusiness as { slug?: string }).slug;
        if (portal && slug) {
          setBookingLinkUrl(
            buildPublicOnlineBookingUrl(getOnlineBookingPublicOriginFromPortalRow(portal), slug, portal),
          );
        } else {
          setBookingLinkUrl(null);
        }
      } catch (err) {
        console.error('Failed to load booking link:', err);
        setBookingLinkUrl(null);
      } finally {
        if (!cancelled) setBookingLinkLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openItemId, selectedBusiness?.id]);

  /**
   * Booking-flow gate: the page needs at least one COMPLETE path (category →
   * sub-category → assigned service) before finishing. When that passes, an
   * AI audit reviews the whole flow for mistakes (dead-end branches, missing
   * pattern matches across categories, wrong price options) and the user
   * chooses to keep editing or continue anyway.
   */
  const handleFlowContinue = async () => {
    if (!selectedBusiness?.id || !supabase) return;
    setFlowValidating(true);
    setFlowError(null);
    setFlowAudit(null);
    try {
      const { data: rows, error } = await supabase
        .from('online_booking_flow')
        .select('id, category_title, sub_categories')
        .eq('business_id', selectedBusiness.id);
      if (error) throw error;
      const cats = (rows || []) as { sub_categories?: { services?: unknown[] }[] }[];
      const hasCompleteBranch = cats.some((c) =>
        (c.sub_categories || []).some((s) => (s.services || []).length > 0),
      );
      if (cats.length === 0 || !hasCompleteBranch) {
        setFlowError(
          'Your booking page needs at least one complete path — a category with a sub-category that has a service assigned — before you can finish.',
        );
        return;
      }

      // AI audit of the whole flow. If the audit infra is unavailable, don't
      // block the user on it — the hard completeness check above already ran.
      const { data, error: auditError } = await supabase.functions.invoke('ai-audit-booking-flow', {
        body: { businessId: selectedBusiness.id },
      });
      if (auditError || !data?.success) {
        console.error('Flow audit unavailable:', auditError || data?.error);
        await completeFlowStep();
        return;
      }
      const issues = (data.issues || []) as { severity: 'error' | 'warning'; title: string; detail: string }[];
      if (issues.length === 0) {
        await completeFlowStep();
        return;
      }
      setFlowAudit({ issues, aiSkipped: !!data.aiSkipped });
    } catch (err) {
      console.error('Failed to check booking flow:', err);
      setFlowError('Could not verify your booking page. Please try again.');
    } finally {
      setFlowValidating(false);
    }
  };

  const handleStaffContinue = async () => {
    setStaffValidating(true);
    setStaffError(null);
    try {
      const ready = await validateStaffOnlineBooking();
      if (ready) {
        await markItemComplete('services-booking', 'staff-booking');
        advanceAfterItem(servicesCategory, 'staff-booking');
      } else {
        setStaffError(
          'Not quite ready — at least one team member needs online booking toggled ON with a working day that has both hours and service areas assigned.',
        );
      }
    } finally {
      setStaffValidating(false);
    }
  };

  /**
   * Answering the online-booking question completes that step and reshapes the
   * category timeline (yes → the full booking path; no → straight to services).
   * The advance uses the NEW answer's items, since React state won't have
   * re-rendered yet inside this handler.
   */
  const handleBookingAnswer = async (answer: 'yes' | 'no') => {
    onlineBookingAnswerRef.current = answer;
    setOnlineBookingAnswer(answer);
    await logResponse(2, 'online-booking:answered', { online_booking_answer: answer });
    await markItemComplete('services-booking', 'online-booking');
    const nextCategory = { ...servicesCategory, items: servicesBookingItems(answer) };
    advanceAfterItem(nextCategory, 'online-booking');
  };

  const handleAddServiceCategory = async () => {
    if (!selectedBusiness?.id) return;
    const name = newCategoryName.trim();
    if (!name) {
      setSvcError('Enter a category name (e.g. Detailing Packages)');
      return;
    }
    setSavingCategory(true);
    setSvcError(null);
    try {
      const created = await createServiceCategory({
        business_id: selectedBusiness.id,
        name,
        description: '',
        order_index: svcCategories.length,
      });
      setSvcCategories((prev) => [...prev, created]);
      setNewCategoryName('');
    } catch (err) {
      console.error('Failed to create category:', err);
      setSvcError('Could not create the category. Please try again.');
    } finally {
      setSavingCategory(false);
    }
  };

  const messagingCategory = setupCategories.find((c) => c.id === 'messaging')!;

  /** Load Gmail connections fresh (recognizes accounts connected outside the wizard). */
  const loadGmailConnections = async () => {
    if (!selectedBusiness?.id || !supabase) return;
    setGmailLoading(true);
    try {
      const { data } = await supabase
        .from('gmail_connections')
        .select('id, email')
        .eq('business_id', selectedBusiness.id)
        .order('created_at', { ascending: false });
      setGmailConnections(data || []);
    } catch (err) {
      console.error('Failed to load Gmail connections:', err);
    } finally {
      setGmailLoading(false);
    }
  };

  useEffect(() => {
    if (openItemId === 'connect-gmail') void loadGmailConnections();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openItemId, selectedBusiness?.id]);

  /** Load the business's Twilio connection state fresh from the DB. */
  const loadTwilioStatus = async () => {
    if (!selectedBusiness?.id || !supabase) return;
    setTwilioLoading(true);
    try {
      const { data } = await supabase
        .from('businesses')
        .select('twilio_connection_method, twilio_account_sid, twilio_enabled, VITE_TWILIO_PHONE_NUMBER')
        .eq('id', selectedBusiness.id)
        .single();
      setTwilioStatus({
        method: data?.twilio_connection_method ?? null,
        accountSid: data?.twilio_account_sid ?? null,
        enabled: !!data?.twilio_enabled,
        phoneNumber: data?.VITE_TWILIO_PHONE_NUMBER ?? null,
      });
    } catch (err) {
      console.error('Failed to load Twilio status:', err);
    } finally {
      setTwilioLoading(false);
    }
  };

  useEffect(() => {
    if (openItemId === 'connect-twilio') {
      setTwilioError(null);
      void loadTwilioStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openItemId, selectedBusiness?.id]);

  /** Twilio Connect authorized but no number yet → offer the account's numbers. */
  const twilioAwaitingNumber =
    !!twilioStatus && !twilioStatus.enabled && twilioStatus.method === 'connect' && !!twilioStatus.accountSid;

  useEffect(() => {
    if (openItemId !== 'connect-twilio' || !twilioAwaitingNumber || !selectedBusiness?.id || !supabase) return;
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase.functions.invoke('twilio-list-numbers', {
          body: { businessId: selectedBusiness.id },
        });
        if (cancelled) return;
        if (error || data?.error) throw new Error(data?.error || 'Could not list numbers');
        setTwilioExistingNumbers(data?.numbers || []);
      } catch (err) {
        if (!cancelled) {
          console.error('Could not list existing Twilio numbers:', err);
          setTwilioExistingNumbers([]);
        }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openItemId, twilioAwaitingNumber, selectedBusiness?.id]);

  /**
   * NOTE: the one-click Twilio Connect authorization is paused (see
   * twilioManualSetup.ts — the wizard now uses the manual credential flow).
   * Kept for when the automatic flow returns; referenced nowhere in prod UI.
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleConnectTwilio = async () => {
    if (!selectedBusiness?.id || !supabase) return;
    setTwilioConnecting(true);
    setTwilioError(null);
    try {
      const returnUrl = new URL(window.location.href);
      returnUrl.searchParams.set('aiSetup', 'messaging.connect-twilio');
      const { data, error } = await supabase.functions.invoke('twilio-connect-start', {
        body: { businessId: selectedBusiness.id, returnUrl: returnUrl.toString() },
      });
      if (error) throw error;
      if (!data?.authorizeUrl) throw new Error('No authorize URL returned');
      window.location.href = data.authorizeUrl;
    } catch (err) {
      console.error('Error starting Twilio Connect:', err);
      setTwilioError('Could not start the Twilio connection. Please try again.');
      setTwilioConnecting(false);
    }
  };
  void twilioConnecting; // state retained for the paused Connect flow

  const handleTwilioSearchNumbers = async () => {
    if (!selectedBusiness?.id || !supabase) return;
    setTwilioSearching(true);
    setTwilioError(null);
    try {
      const { data, error } = await supabase.functions.invoke('twilio-search-numbers', {
        body: { businessId: selectedBusiness.id, areaCode: twilioAreaCode || undefined },
      });
      if (error || data?.error) throw new Error(data?.error || 'Number search failed');
      setTwilioAvailableNumbers(data?.numbers || []);
      if ((data?.numbers || []).length === 0) {
        setTwilioError('No numbers found — try a different area code.');
      }
    } catch (err) {
      console.error('Twilio number search failed:', err);
      setTwilioError(err instanceof Error ? err.message : 'Number search failed');
    } finally {
      setTwilioSearching(false);
    }
  };

  // Load the saved booking types whenever the calendar item opens, so the
  // cards reflect what was chosen at signup (or changed since).
  useEffect(() => {
    if (openItemId !== 'booking-types' || !selectedBusiness?.id || !supabase) return;
    let cancelled = false;
    setBookingTypesLoading(true);
    setBookingTypesError(null);
    (async () => {
      try {
        const [mobileRes, locationRes] = await Promise.all([
          supabase.rpc('get_app_setting', {
            p_business_id: selectedBusiness.id,
            p_app_slug: 'calendar',
            p_setting_key: 'show_mobile_booking_in_fab',
            p_default_value: true,
          }),
          supabase.rpc('get_app_setting', {
            p_business_id: selectedBusiness.id,
            p_app_slug: 'calendar',
            p_setting_key: 'show_location_booking_in_fab',
            p_default_value: true,
          }),
        ]);
        if (cancelled) return;
        if (mobileRes.error || locationRes.error) throw mobileRes.error || locationRes.error;
        setBookingTypes({
          mobile: mobileRes.data ?? true,
          location: locationRes.data ?? true,
        });
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load booking types:', err);
          setBookingTypesError('Could not load your booking types. Close and reopen this step to retry.');
        }
      } finally {
        if (!cancelled) setBookingTypesLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openItemId, selectedBusiness?.id]);

  /**
   * Persist the booking-type choice using the same set_app_setting keys the
   * signup wizard writes, and broadcast the same events the Calendar settings
   * modal fires so the FAB / booking modals update without a reload.
   */
  const saveBookingTypes = async (): Promise<boolean> => {
    if (!selectedBusiness?.id || !supabase || !bookingTypes) return false;
    setBookingTypesSaving(true);
    setBookingTypesError(null);
    try {
      const [mobileRes, locationRes] = await Promise.all([
        supabase.rpc('set_app_setting', {
          p_business_id: selectedBusiness.id,
          p_app_slug: 'calendar',
          p_setting_key: 'show_mobile_booking_in_fab',
          p_setting_value: bookingTypes.mobile,
        }),
        supabase.rpc('set_app_setting', {
          p_business_id: selectedBusiness.id,
          p_app_slug: 'calendar',
          p_setting_key: 'show_location_booking_in_fab',
          p_setting_value: bookingTypes.location,
        }),
      ]);
      if (mobileRes.error || locationRes.error) throw mobileRes.error || locationRes.error;
      window.dispatchEvent(
        new CustomEvent('mobileCalendarSettingChanged', {
          detail: { key: 'show_mobile_booking_in_fab', value: bookingTypes.mobile },
        }),
      );
      window.dispatchEvent(
        new CustomEvent('calendarSettingChanged', {
          detail: { key: 'show_location_booking_in_fab', value: bookingTypes.location },
        }),
      );
      return true;
    } catch (err) {
      console.error('Failed to save booking types:', err);
      setBookingTypesError('Could not save your booking types. Please try again.');
      return false;
    } finally {
      setBookingTypesSaving(false);
    }
  };

  // Load the current tasks setting whenever the enable-tasks item opens.
  useEffect(() => {
    if (openItemId !== 'enable-tasks' || !selectedBusiness?.id || !supabase) return;
    let cancelled = false;
    setTasksLoading(true);
    setTasksError(null);
    (async () => {
      try {
        const { data, error } = await supabase.rpc('get_app_setting', {
          p_business_id: selectedBusiness.id,
          p_app_slug: 'calendar',
          p_setting_key: 'show_tasks_in_fab',
          p_default_value: false,
        });
        if (cancelled) return;
        if (error) throw error;
        setTasksEnabled(Boolean(data));
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load tasks setting:', err);
          setTasksEnabled(false);
        }
      } finally {
        if (!cancelled) setTasksLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openItemId, selectedBusiness?.id]);

  /** Save the tasks toggle (same setting + event as the Calendar settings modal). */
  const saveTasksEnabled = async (enable: boolean): Promise<boolean> => {
    if (!selectedBusiness?.id || !supabase) return false;
    setTasksSaving(true);
    setTasksError(null);
    try {
      const { error } = await supabase.rpc('set_app_setting', {
        p_business_id: selectedBusiness.id,
        p_app_slug: 'calendar',
        p_setting_key: 'show_tasks_in_fab',
        p_setting_value: enable,
      });
      if (error) throw error;
      setTasksEnabled(enable);
      window.dispatchEvent(
        new CustomEvent('calendarSettingChanged', {
          detail: { key: 'show_tasks_in_fab', value: enable },
        }),
      );
      return true;
    } catch (err) {
      console.error('Failed to save tasks setting:', err);
      setTasksError('Could not save your choice. Please try again.');
      return false;
    } finally {
      setTasksSaving(false);
    }
  };

  // Load the current field-quotes setting whenever the field-quotes item opens.
  useEffect(() => {
    if (openItemId !== 'field-quotes' || !selectedBusiness?.id || !supabase) return;
    let cancelled = false;
    setFieldQuotesLoading(true);
    setFieldQuotesError(null);
    (async () => {
      try {
        const { data, error } = await supabase.rpc('get_app_setting', {
          p_business_id: selectedBusiness.id,
          p_app_slug: 'calendar',
          p_setting_key: 'show_field_quotes_in_fab',
          p_default_value: false,
        });
        if (cancelled) return;
        if (error) throw error;
        setFieldQuotesEnabled(Boolean(data));
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load field quotes setting:', err);
          setFieldQuotesEnabled(false);
        }
      } finally {
        if (!cancelled) setFieldQuotesLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openItemId, selectedBusiness?.id]);

  /** Save the field-quotes toggle (same setting + event as Calendar settings). */
  const saveFieldQuotesEnabled = async (enable: boolean): Promise<boolean> => {
    if (!selectedBusiness?.id || !supabase) return false;
    setFieldQuotesSaving(true);
    setFieldQuotesError(null);
    try {
      const { error } = await supabase.rpc('set_app_setting', {
        p_business_id: selectedBusiness.id,
        p_app_slug: 'calendar',
        p_setting_key: 'show_field_quotes_in_fab',
        p_setting_value: enable,
      });
      if (error) throw error;
      setFieldQuotesEnabled(enable);
      window.dispatchEvent(
        new CustomEvent('calendarSettingChanged', {
          detail: { key: 'show_field_quotes_in_fab', value: enable },
        }),
      );
      return true;
    } catch (err) {
      console.error('Failed to save field quotes setting:', err);
      setFieldQuotesError('Could not save your choice. Please try again.');
      return false;
    } finally {
      setFieldQuotesSaving(false);
    }
  };

  // Reset the customer-import step's transient state each time it opens.
  useEffect(() => {
    if (openItemId !== 'import-customers') return;
    setCustWantsImport(false);
    setCustFile(null);
    setCustParsed([]);
    setCustPreviewOpen(false);
    setCustImportResult(null);
    setCustError(null);
  }, [openItemId]);

  /** Parse the chosen CSV with the same AI edge function as the Customers page. */
  const handleParseCustomerCsv = async () => {
    if (!custFile || !selectedBusiness?.id || !supabase) return;
    setCustParsing(true);
    setCustError(null);
    try {
      const fileContent = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (event) =>
          event.target?.result ? resolve(event.target.result as string) : reject(new Error('Failed to read file content'));
        reader.onerror = () => reject(new Error('Error reading file'));
        reader.readAsText(custFile, 'UTF-8');
      });
      if (fileContent.trim().length === 0) throw new Error('The CSV file is empty.');

      const { data, error } = await supabase.functions.invoke('parse-csv-customers', {
        body: { businessId: selectedBusiness.id, csvContent: fileContent },
      });
      if (error) {
        // FunctionsHttpError hides the server message — pull it from the body.
        let serverMessage = error.message;
        try {
          const ctx = (error as { context?: { clone?: () => Response } }).context;
          const body = await ctx?.clone?.()?.json?.().catch(() => null);
          if (body?.error) serverMessage = body.error;
        } catch { /* ignore body read errors */ }
        throw new Error(serverMessage);
      }
      if (data?.insufficientTokens) {
        throw new Error(`Not enough AI tokens to parse the file (balance: ${data.currentBalance}).`);
      }
      if (!data?.success || !data?.customers) throw new Error(data?.error || 'Failed to parse CSV');
      if (data.customers.length === 0) {
        throw new Error('No valid customers found in the CSV — please check the file format.');
      }
      setCustParsed(data.customers);
      setCustPreviewOpen(true);
    } catch (err) {
      console.error('CSV parse failed:', err);
      setCustError(err instanceof Error ? err.message : 'Failed to parse the CSV file');
    } finally {
      setCustParsing(false);
    }
  };

  /** Create the reviewed customers (same per-row insert as the Customers page). */
  const handleImportCustomers = async (customersToImport: CsvCustomer[], tagIds: string[] = []) => {
    if (!selectedBusiness?.id || customersToImport.length === 0) return;
    let imported = 0;
    let duplicates = 0;
    let errors = 0;
    for (const customer of customersToImport) {
      try {
        const phoneRaw = customer.phone?.trim();
        const newCustomer = await createCustomer({
          business_id: selectedBusiness.id,
          type: 'person',
          first_name: customer.first_name,
          last_name: customer.last_name,
          email: customer.email || null,
          phone: phoneRaw ? normalizePhoneNumber(phoneRaw) : null,
          address: customer.address || null,
          status: customer.status || 'active',
          notes: customer.notes || null,
          amount_spent: 0,
          credit_balance: 0,
          tags: [],
          preferences: {},
          tax_exempt: false,
          workflow_exempt: false,
          carfax_exempt: false,
          receive_reminders: true,
          receive_offers: true,
          is_supplier: false,
          lifetime_value: 0,
          source: null,
          last_interaction: null,
          next_service_date: null,
          referral_source: null,
          repeat: false,
          lat: null,
          lng: null,
        });
        if (tagIds.length > 0 && newCustomer?.id) {
          try {
            await updateCustomerTags(newCustomer.id, tagIds);
          } catch (tagErr) {
            console.error('Failed to apply tags to imported customer:', tagErr);
          }
        }
        imported++;
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : '';
        if (message.includes('duplicate') || message.includes('already exists')) {
          duplicates++;
        } else {
          errors++;
          console.error('Error importing customer:', err);
        }
      }
    }
    setCustImportResult({ imported, duplicates, errors });
    setCustPreviewOpen(false);
  };

  /** Buy a new number (phoneNumber) or point an existing one (sid) at Nexus. */
  const handleTwilioPickNumber = async (pick: { phoneNumber: string; sid?: string }) => {
    if (!selectedBusiness?.id || !supabase) return;
    setTwilioPurchasing(pick.phoneNumber);
    setTwilioError(null);
    try {
      const { data, error } = await supabase.functions.invoke('twilio-purchase-number', {
        body: pick.sid
          ? { businessId: selectedBusiness.id, phoneNumberSid: pick.sid }
          : { businessId: selectedBusiness.id, phoneNumber: pick.phoneNumber },
      });
      if (error || data?.error) throw new Error(data?.error || 'Could not set up the number');
      setTwilioAvailableNumbers([]);
      await loadTwilioStatus();
    } catch (err) {
      console.error('Twilio number setup failed:', err);
      setTwilioError(err instanceof Error ? err.message : 'Could not set up the number');
    } finally {
      setTwilioPurchasing(null);
    }
  };

  /**
   * Same OAuth flow as the Gmail settings panel, but resume-aware: the OAuth
   * return URL carries an `aiSetup=messaging.connect-gmail` marker. If the
   * round-trip ends up navigating this tab (popup blocked / PWA), the app
   * shell reads that marker on load and reopens the wizard on this exact step
   * instead of dumping the user on whatever page the return URL points at.
   */
  const handleConnectGmail = () => {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
    const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined;
    if (!clientId) {
      console.error('Google Client ID not configured');
      return;
    }
    const redirectUrl = `${supabaseUrl.replace('/rest/v1', '').replace(/\/$/, '')}/functions/v1/gmail-oauth-callback`;
    const scope =
      'https://www.googleapis.com/auth/gmail.send https://www.googleapis.com/auth/gmail.readonly https://www.googleapis.com/auth/userinfo.email';
    const returnUrl = new URL(window.location.href);
    returnUrl.searchParams.set('aiSetup', 'messaging.connect-gmail');
    const encodedState = btoa(JSON.stringify({ businessId: selectedBusiness?.id, returnUrl: returnUrl.toString() }));
    const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${clientId}&redirect_uri=${encodeURIComponent(redirectUrl)}&response_type=code&scope=${encodeURIComponent(scope)}&access_type=offline&prompt=consent&state=${encodeURIComponent(encodedState)}`;

    const popup = window.open(authUrl, '_blank', 'width=500,height=600');
    if (!popup) {
      // Popup blocked (common in PWA / native shells): run OAuth in this tab.
      // The aiSetup marker brings the user straight back to this step.
      window.location.href = authUrl;
      return;
    }

    // Watch the popup: once it lands back on our domain with success, close it
    // and refresh the list. The wizard in this window never navigated away.
    const checkPopup = window.setInterval(() => {
      if (popup.closed) {
        window.clearInterval(checkPopup);
        void loadGmailConnections();
        return;
      }
      try {
        const popupUrl = popup.location.href;
        if (popupUrl.includes(window.location.origin) && popupUrl.includes('success=gmail_connected')) {
          popup.close();
          window.clearInterval(checkPopup);
          void loadGmailConnections();
        }
      } catch {
        // Cross-origin while the popup is on Google's domain — keep polling.
      }
    }, 500);

    // Safety timeout so a forgotten popup doesn't poll forever.
    window.setTimeout(() => {
      window.clearInterval(checkPopup);
      if (!popup.closed) popup.close();
    }, 300000);
  };

  // ── Payments + Invoicing: Stripe Connect ────────────────────
  const [stripePhase, setStripePhase] = useState<'card-question' | 'stripe-question' | 'connect'>('card-question');
  const [stripeHasAccount, setStripeHasAccount] = useState<boolean | null>(null);
  const [stripeConnecting, setStripeConnecting] = useState(false);
  const [stripeStatusChecking, setStripeStatusChecking] = useState(false);
  const [stripeError, setStripeError] = useState<string | null>(null);

  const stripeAccountId = (selectedBusiness as any)?.stripe_connect_account_id as string | undefined;
  const stripeFullyConnected = Boolean(
    stripeAccountId &&
      (selectedBusiness as any)?.stripe_connect_onboarding_completed &&
      (selectedBusiness as any)?.stripe_connect_charges_enabled,
  );

  /** Re-pull Connect status from Stripe, then refresh the business row. */
  const checkStripeStatus = async () => {
    if (!selectedBusiness?.id || !supabase) return;
    setStripeStatusChecking(true);
    try {
      await supabase.functions.invoke('check-stripe-connect-status', {
        body: { businessId: selectedBusiness.id },
      });
      forceLoadBusinesses();
    } catch (err) {
      console.error('Stripe status check failed:', err);
    } finally {
      setStripeStatusChecking(false);
    }
  };

  // Opening the item: jump straight to the connect screen when onboarding was
  // already started (also covers returning from Stripe via the aiSetup marker),
  // otherwise begin at the card-payments question.
  useEffect(() => {
    if (openItemId !== 'connect-stripe') return;
    setStripeError(null);
    if (stripeAccountId) {
      setStripePhase('connect');
      if (!stripeFullyConnected) void checkStripeStatus();
    } else {
      setStripePhase('card-question');
      setStripeHasAccount(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openItemId]);

  /**
   * Same Stripe Connect flow as Settings → Integrations, but resume-aware: the
   * return URL carries the `aiSetup=payments-invoicing.connect-stripe` marker
   * so the app shell reopens the wizard on this exact step after onboarding.
   */
  const handleConnectStripe = async () => {
    if (!selectedBusiness?.id || !supabase) return;
    setStripeConnecting(true);
    setStripeError(null);
    try {
      // Stripe account links require a real http(s) URL — on native (Capacitor)
      // window.location is capacitor://localhost, which Stripe rejects as
      // "Invalid URL". getSiteUrl() falls back to the public web app URL.
      const returnUrl = new URL(getSiteUrl());
      returnUrl.searchParams.set('aiSetup', 'payments-invoicing.connect-stripe');
      const { data, error } = await supabase.functions.invoke('create-stripe-connect-account', {
        body: {
          businessId: selectedBusiness.id,
          businessEmail: selectedBusiness.email || '',
          businessName: selectedBusiness.name || '',
          returnUrl: returnUrl.toString(),
          refreshUrl: returnUrl.toString(),
        },
      });
      if (error) {
        let detail = '';
        try {
          detail = (await (error as { context?: Response }).context?.json())?.error || '';
        } catch { /* keep generic */ }
        throw new Error(detail || (error as Error).message);
      }
      if (data?.error) throw new Error(data.error);
      if (!data?.onboardingUrl) throw new Error('Stripe did not return an onboarding link');
      // Full-page redirect — the aiSetup marker brings the user back here.
      window.location.href = data.onboardingUrl;
    } catch (err) {
      console.error('Error connecting Stripe:', err);
      setStripeError(err instanceof Error ? err.message : 'Could not start Stripe setup. Please try again.');
      setStripeConnecting(false);
    }
  };

  /** Skip the current item (marked as skipped, not completed) and advance. */
  const skipToNextItem = async (category: SetupCategory) => {
    const itemId = openItemId;
    if (!itemId) return;
    const alreadyCompleted = (completedItemsRef.current[category.id] || []).includes(itemId);

    // Record the skip FIRST (state + ref) so the "category finished" write
    // fired by advanceAfterItem carries it and can't save a stale map.
    if (!alreadyCompleted) {
      const nextSkipped = {
        ...skippedItemsRef.current,
        [category.id]: [...new Set([...(skippedItemsRef.current[category.id] || []), itemId])],
      };
      skippedItemsRef.current = nextSkipped;
      setSkippedItems(nextSkipped);
    }

    advanceAfterItem(category, itemId, { skippedCurrent: !alreadyCompleted });

    if (!alreadyCompleted) {
      await logResponse(2, `${category.id}:${itemId}:skipped`, {
        completed_items: completedItemsRef.current,
        skipped_items: skippedItemsRef.current,
      });
    }
  };

  /** The Business Info category (all current items live in it). */
  const businessInfoCategory = setupCategories.find((c) => c.id === 'business-info')!;

  const markItemComplete = async (categoryId: string, itemId: string) => {
    const next = {
      ...completedItemsRef.current,
      [categoryId]: [...new Set([...(completedItemsRef.current[categoryId] || []), itemId])],
    };
    // Completing an item clears any earlier skip.
    const nextSkipped = {
      ...skippedItemsRef.current,
      [categoryId]: (skippedItemsRef.current[categoryId] || []).filter((id) => id !== itemId),
    };
    completedItemsRef.current = next;
    skippedItemsRef.current = nextSkipped;
    setCompletedItems(next);
    setSkippedItems(nextSkipped);
    await logResponse(2, `${categoryId}:${itemId}:completed`, {
      completed_items: next,
      skipped_items: nextSkipped,
    });
  };

  const handleSaveBusinessInfo = async () => {
    if (!selectedBusiness?.id || !supabase) return;
    if (!bizForm.name.trim()) {
      setBizFormError('Business name is required');
      return;
    }
    setBizFormSaving(true);
    setBizFormError(null);
    try {
      const { error } = await supabase
        .from('businesses')
        .update({
          name: bizForm.name.trim(),
          email: bizForm.email.trim() || null,
          phone: bizForm.phone.trim() || null,
          address: bizForm.address.trim() || null,
        })
        .eq('id', selectedBusiness.id);
      if (error) throw error;
      await markItemComplete('business-info', 'confirm-details');
      advanceAfterItem(businessInfoCategory, 'confirm-details');
    } catch (err) {
      console.error('Failed to save business info:', err);
      setBizFormError('Could not save. Please try again.');
    } finally {
      setBizFormSaving(false);
    }
  };

  const handleBeginGuidedSetup = async () => {
    await logResponse(0, 'begin_guided_setup');
    setStep(1);
  };

  const handleOrientationContinue = async () => {
    await logResponse(1, 'orientation_continue');
    setStep(2);
  };

  const handleCloseAndWait = async () => {
    await logResponse(0, 'wait_for_specialist');
    onClose();
  };

  const handleExploreAddons = async () => {
    await logResponse(2, 'explore_addons');
    onClose();
    navigate('/app-store');
  };

  const handleRecapContinue = async () => {
    await logResponse(2, 'recap_continue');
    setStep(3);
  };

  const toggleAddon = (addonId: string) => {
    setAddonError(null);
    setSelectedAddonIds((prev) => {
      const next = new Set(prev);
      if (next.has(addonId)) next.delete(addonId);
      else next.add(addonId);
      return next;
    });
  };

  /** Add every selected add-on to the subscription (same flow as the App Store), then continue. */
  const handleAddSelectedAndContinue = async () => {
    if (!selectedBusiness?.id || !supabase || selectedAddonIds.size === 0) return;
    setAddingAddons(true);
    setAddonError(null);

    const added: string[] = [];
    const failed: { id: string; name: string }[] = [];

    for (const addonId of selectedAddonIds) {
      const addon = planRecap?.upsellAddons.find((a) => a.id === addonId);
      try {
        const { data, error } = await supabase.functions.invoke('stripe-subscription', {
          body: { action: 'add-addon-app', businessId: selectedBusiness.id, addonAppId: addonId },
        });
        if (error || !data?.success) {
          failed.push({ id: addonId, name: addon?.name || addonId });
        } else {
          added.push(addonId);
        }
      } catch {
        failed.push({ id: addonId, name: addon?.name || addonId });
      }
    }

    await logResponse(2, 'addons_added', { added, failed: failed.map((f) => f.id) });

    // Successfully added add-ons leave the upsell list and selection.
    if (added.length > 0 && planRecap) {
      setPlanRecap({
        ...planRecap,
        upsellAddons: planRecap.upsellAddons.filter((a) => !added.includes(a.id)),
      });
      setSelectedAddonIds(new Set(failed.map((f) => f.id)));
    }
    setAddingAddons(false);

    if (failed.length > 0) {
      setAddonError(
        `Couldn't add ${failed.map((f) => f.name).join(', ')}. You can try again or continue and add them later from the App Store.`,
      );
      return;
    }
    setStep(3);
  };

  const recapMessage = planRecap
    ? [
        planRecap.trialing
          ? `You're set up on the ${planRecap.planName}${planRecap.planPrice != null ? ` ($${planRecap.planPrice}/mo)` : ''} — currently on your free trial${planRecap.trialDays ? ` (${planRecap.trialDays} days)` : ''}, so you have full access to everything in the plan right now.`
          : `You're set up on the ${planRecap.planName}${planRecap.planPrice != null ? ` ($${planRecap.planPrice}/mo)` : ''}.`,
        `Your plan includes ${planRecap.includedApps.length} apps — here's what you've got:`,
      ].join('\n\n')
    : '';

  const openingMessage = OPENING_MESSAGES[setupChoice ?? 'unknown'];

  return (
    <>
      <div
        className={`fixed inset-0 bg-black transition-opacity duration-300 z-[10000] ${
          isOpen ? 'bg-opacity-50' : 'bg-opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        className={`fixed inset-y-0 right-0 w-full md:w-[600px] bg-white transform transition-transform duration-300 ease-out shadow-2xl z-[10001] ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{
          top: 'env(safe-area-inset-top, 0px)',
          bottom: 'env(safe-area-inset-bottom, 0px)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="h-full flex flex-col">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                <Bot className="h-5 w-5 text-white" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900">Nexus AI Set Up Assistant</h2>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6 min-h-0 flex flex-col">
            {!choiceLoaded ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent" />
              </div>
            ) : step === 0 ? (
              /* ── Step 0: re-affirm the setup-wizard choice ── */
              <>
                <AssistantMessage text={openingMessage} />
                <div className="mt-6 flex flex-col gap-3">
                  <button
                    type="button"
                    onClick={handleBeginGuidedSetup}
                    className="w-full flex items-center justify-between px-4 py-3 rounded-lg border-2 border-gray-200 bg-white hover:border-blue-500 hover:bg-blue-50 transition-colors font-medium text-gray-900"
                  >
                    {setupChoice === 'consultation' ? "Walk me through it while I wait" : "Let's get started"}
                    <ChevronRight className="h-4 w-4 text-gray-400" />
                  </button>
                  {setupChoice === 'consultation' && (
                    <button
                      type="button"
                      onClick={handleCloseAndWait}
                      className="w-full text-left px-4 py-3 rounded-lg border-2 border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50 transition-colors font-medium text-gray-500"
                    >
                      Close — I'll wait to be contacted
                    </button>
                  )}
                </div>
              </>
            ) : step === 1 ? (
              /* ── Step 1: orientation — where help lives when they need it ── */
              <>
                <AssistantMessage
                  text={`Getting started on Nexus can feel like a lot — that's exactly why I'm here. Follow along with this setup guide and you'll be up and running in no time.\n\nAnd don't worry about losing your place: you can reopen this Setup Wizard anytime by tapping the help icon in your top navigation bar.`}
                />
                <div className="mt-3 ml-12">
                  <img
                    src="/setup-wizard/help-icon-nav.png"
                    alt="The help icon in the top navigation bar"
                    className="w-full max-w-md rounded-xl border border-gray-200 shadow-sm"
                  />
                </div>

                <div className="mt-5">
                  <AssistantMessage
                    text={`That icon opens the Help Centre. The three buttons at the top are your lifelines: Setup Wizard brings you right back to this guide, Support Request reaches the Nexus team directly, and Report a Bug lets us know if something ever looks off.`}
                  />
                </div>
                <div className="mt-3 ml-12">
                  <img
                    src="/setup-wizard/help-centre-buttons.png"
                    alt="The Help Centre with Setup Wizard, Support Request, and Report a Bug buttons"
                    className="w-full max-w-md rounded-xl border border-gray-200 shadow-sm"
                  />
                </div>

                <div className="mt-5">
                  <AssistantMessage
                    text={`You've also got AI Help — type any question about setting up or using Nexus and it'll walk you through it step by step.\n\nEven better: tap Voice Mode and you can talk to Nexus AI live while you move around the app — like having an expert on the phone, guiding you hands-free.`}
                  />
                </div>
                <div className="mt-3 ml-12">
                  <img
                    src="/setup-wizard/ai-help-voice.png"
                    alt="AI Help chat with the Voice Mode button"
                    className="w-full max-w-md rounded-xl border border-gray-200 shadow-sm"
                  />
                </div>

                <div className="mt-6 flex flex-col gap-3">
                  <button
                    type="button"
                    onClick={handleOrientationContinue}
                    className="w-full flex items-center justify-between px-4 py-3 rounded-lg border-2 border-gray-200 bg-white hover:border-blue-500 hover:bg-blue-50 transition-colors font-medium text-gray-900"
                  >
                    Got it — let's set up my business
                    <ChevronRight className="h-4 w-4 text-gray-400" />
                  </button>
                </div>
              </>
            ) : step === 2 ? (
              /* ── Step 2: plan recap + what's not included (upsell) ── */
              recapLoading || !planRecap ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent" />
                </div>
              ) : (
                <>
                  <AssistantMessage text={recapMessage} />

                  {/* Included apps */}
                  <div className="mt-4 ml-12 flex flex-wrap gap-2">
                    {planRecap.includedApps.map((appId) => (
                      <span
                        key={appId}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-green-50 border border-green-200 text-[13px] font-medium text-green-800"
                      >
                        <Check className="h-3.5 w-3.5 text-green-500" />
                        {appDisplayName(appId)}
                      </span>
                    ))}
                  </div>

                  {/* Upsell: add-ons not in their plan */}
                  {(planRecap.upsellAddons.length > 0 || planRecap.upgradeOnlyApps.length > 0) && (
                    <>
                      <div className="mt-6">
                        <AssistantMessage
                          text={
                            planRecap.trialing
                              ? `Depending on how you run your business, these are features other companies like yours find valuable. They're not part of your plan, but you can add them now — they're included in your free trial, and billing only starts if you keep your subscription after the trial:`
                              : `Depending on how you run your business, these are features other companies like yours find valuable. They're not part of your plan, but you can add them anytime:`
                          }
                        />
                      </div>
                      <div className="mt-4 ml-12 space-y-2">
                        {planRecap.upsellAddons.map((addon) => {
                          const selected = selectedAddonIds.has(addon.id);
                          return (
                            <button
                              key={addon.id}
                              type="button"
                              onClick={() => toggleAddon(addon.id)}
                              disabled={addingAddons}
                              className={`w-full flex items-center gap-3 rounded-xl border-2 px-4 py-3 text-left transition-colors disabled:opacity-60 ${
                                selected
                                  ? 'border-blue-500 bg-blue-50'
                                  : 'border-gray-200 bg-white hover:border-gray-300'
                              }`}
                            >
                              <span
                                className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md border-2 transition-colors ${
                                  selected ? 'border-blue-500 bg-blue-500' : 'border-gray-300 bg-white'
                                }`}
                              >
                                {selected && <Check className="h-3.5 w-3.5 text-white" />}
                              </span>
                              <div className="flex-1 min-w-0">
                                <p className="text-[14px] font-semibold text-gray-900">{addon.name}</p>
                                {addon.description && (
                                  <p className="text-[12px] text-gray-500 truncate">{addon.description}</p>
                                )}
                              </div>
                              {addon.price != null && (
                                <span className="text-[13px] font-semibold text-gray-700 whitespace-nowrap">
                                  ${addon.price}/mo
                                </span>
                              )}
                            </button>
                          );
                        })}
                        {planRecap.upgradeOnlyApps.length > 0 && (
                          <p className="text-[12px] text-gray-500 pt-1">
                            Also available on higher plans:{' '}
                            {planRecap.upgradeOnlyApps.map(appDisplayName).join(', ')}
                          </p>
                        )}
                      </div>
                    </>
                  )}

                  {addonError && (
                    <div className="mt-4 ml-12 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-[13px] text-red-700">
                      {addonError}
                    </div>
                  )}

                  <div className="mt-6 flex flex-col gap-3">
                    {selectedAddonIds.size > 0 ? (
                      <button
                        type="button"
                        onClick={handleAddSelectedAndContinue}
                        disabled={addingAddons}
                        className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-blue-600 hover:bg-blue-700 transition-colors font-semibold text-white disabled:opacity-60"
                      >
                        {addingAddons ? (
                          <>
                            <span className="h-4 w-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                            Adding to your plan...
                          </>
                        ) : (
                          <>
                            Add {selectedAddonIds.size} to plan & continue
                            <ChevronRight className="h-4 w-4" />
                          </>
                        )}
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={handleRecapContinue}
                        className="w-full flex items-center justify-between px-4 py-3 rounded-lg border-2 border-gray-200 bg-white hover:border-blue-500 hover:bg-blue-50 transition-colors font-medium text-gray-900"
                      >
                        Looks good — continue setup
                        <ChevronRight className="h-4 w-4 text-gray-400" />
                      </button>
                    )}
                    {planRecap.upsellAddons.length > 0 && (
                      <button
                        type="button"
                        onClick={handleExploreAddons}
                        disabled={addingAddons}
                        className="w-full flex items-center justify-between px-4 py-3 rounded-lg border-2 border-gray-200 bg-white hover:border-gray-300 transition-colors font-medium text-gray-500 disabled:opacity-60"
                      >
                        Browse details in the App Store
                        <ChevronRight className="h-4 w-4 text-gray-400" />
                      </button>
                    )}
                  </div>
                </>
              )
            ) : celebration ? (
              /* ── Step 3: category-finished celebration ── */
              (() => {
                const category = setupCategories.find((c) => c.id === celebration.categoryId);
                const fullyComplete = celebration.skippedCount === 0;
                return (
                  <div className="flex-1 flex flex-col items-center justify-center text-center py-8 relative overflow-hidden">
                    <style>{`
                      @keyframes celebratePop {
                        0% { transform: scale(0); opacity: 0; }
                        60% { transform: scale(1.15); opacity: 1; }
                        100% { transform: scale(1); opacity: 1; }
                      }
                      @keyframes confettiFall {
                        0% { transform: translateY(-10px) rotate(0deg); opacity: 1; }
                        100% { transform: translateY(340px) rotate(540deg); opacity: 0; }
                      }
                    `}</style>
                    {/* Confetti burst */}
                    <div className="absolute inset-x-0 top-0 pointer-events-none" aria-hidden>
                      {Array.from({ length: 18 }).map((_, i) => (
                        <span
                          key={i}
                          className="absolute h-2 w-2 rounded-sm"
                          style={{
                            left: `${(i * 53) % 100}%`,
                            backgroundColor: ['#22c55e', '#3b82f6', '#f59e0b', '#ec4899', '#8b5cf6', '#06b6d4'][i % 6],
                            animation: `confettiFall ${1.6 + (i % 5) * 0.3}s ease-in ${(i % 7) * 0.12}s forwards`,
                          }}
                        />
                      ))}
                    </div>

                    <div
                      className={`flex h-20 w-20 items-center justify-center rounded-full shadow-lg ${
                        fullyComplete ? 'bg-green-500 shadow-green-500/30' : 'bg-amber-400 shadow-amber-400/30'
                      }`}
                      style={{ animation: 'celebratePop 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)' }}
                    >
                      <Check className="h-10 w-10 text-white" />
                    </div>

                    <h3 className="mt-6 text-[22px] font-bold text-gray-900">
                      {fullyComplete
                        ? `${category?.title || 'Category'} complete! 🎉`
                        : `${category?.title || 'Category'} finished!`}
                    </h3>
                    <p className="mt-2 max-w-sm text-[15px] text-gray-500">
                      {fullyComplete
                        ? `Amazing work — every step is done. Your business foundation just got stronger.`
                        : `Nice work! You skipped ${celebration.skippedCount} step${
                            celebration.skippedCount === 1 ? '' : 's'
                          } — ${celebration.skippedCount === 1 ? "it'll" : "they'll"} be waiting on the timeline whenever you're ready.`}
                    </p>

                    <button
                      type="button"
                      onClick={() => setCelebration(null)}
                      className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-blue-600 hover:bg-blue-700 transition-colors font-semibold text-white"
                    >
                      Continue setup
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>
                );
              })()
            ) : openCategoryId ? (
              /* ── Step 2: inside a category ── */
              (() => {
                const category = setupCategories.find((c) => c.id === openCategoryId);
                if (!category) return null;
                return (
                  <>
                    <button
                      type="button"
                      onClick={() => {
                        setOpenItemId(null);
                        setOpenCategoryId(null);
                      }}
                      className="inline-flex items-center gap-1 text-sm font-medium text-gray-500 hover:text-gray-800 mb-4 self-start"
                    >
                      <ChevronLeft className="h-4 w-4" />
                      All setup steps
                    </button>
                    {openItemId === 'confirm-details' ? (
                      /* Item: confirm business details (live from the businesses row) */
                      <>
                        <AssistantMessage
                          text={`Please confirm or adjust any of the following info. This information displays on invoices and other public areas — input it exactly as you want it to display for your customers.`}
                        />
                        {bizFormLoading ? (
                          <div className="flex items-center justify-center py-10">
                            <div className="animate-spin rounded-full h-7 w-7 border-2 border-blue-500 border-t-transparent" />
                          </div>
                        ) : (
                          <div className="mt-5 ml-12 space-y-4">
                            {(
                              [
                                { key: 'name', label: 'Business Name', type: 'text', placeholder: 'Your business name' },
                                { key: 'email', label: 'Email', type: 'email', placeholder: 'business@example.com' },
                                { key: 'phone', label: 'Phone', type: 'tel', placeholder: '(555) 123-4567' },
                                { key: 'address', label: 'Address', type: 'text', placeholder: '123 Main St, City' },
                              ] as const
                            ).map((field) => (
                              <div key={field.key}>
                                <label className="block text-xs font-semibold text-gray-500 mb-1.5">
                                  {field.label}
                                </label>
                                <input
                                  type={field.type}
                                  value={bizForm[field.key]}
                                  placeholder={field.placeholder}
                                  onChange={(e) =>
                                    setBizForm((prev) => ({ ...prev, [field.key]: e.target.value }))
                                  }
                                  disabled={bizFormSaving}
                                  className="w-full rounded-lg border border-gray-200 px-3.5 py-2.5 text-[15px] text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 disabled:opacity-60"
                                />
                              </div>
                            ))}

                            {bizFormError && (
                              <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-[13px] text-red-700">
                                {bizFormError}
                              </div>
                            )}

                            <button
                              type="button"
                              onClick={handleSaveBusinessInfo}
                              disabled={bizFormSaving || bizFormLoading}
                              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-blue-600 hover:bg-blue-700 transition-colors font-semibold text-white disabled:opacity-60"
                            >
                              {bizFormSaving ? (
                                <>
                                  <span className="h-4 w-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                                  Saving...
                                </>
                              ) : (
                                <>
                                  <Check className="h-4 w-4" />
                                  Confirm & save
                                </>
                              )}
                            </button>
                          </div>
                        )}
                      </>
                    ) : openItemId === 'upload-logo' ? (
                      /* Item: business logo (central storage — same upload used across the app) */
                      <>
                        <AssistantMessage
                          text={`Let's add your logo — it appears on invoices, emails, and your booking pages.\n\nFor the best results, use a horizontal (landscape) logo around 600×180 px (roughly a 3:1 ratio) as a PNG with a transparent background, under 5 MB. Square logos work too, but they'll display smaller in headers.`}
                        />
                        <div className="mt-5 ml-12 space-y-4">
                          <BusinessLogoUpload size="large" />
                          <button
                            type="button"
                            onClick={async () => {
                              await markItemComplete('business-info', 'upload-logo');
                              advanceAfterItem(businessInfoCategory, 'upload-logo');
                            }}
                            disabled={!selectedBusiness?.booking_settings?.logo_url}
                            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-blue-600 hover:bg-blue-700 transition-colors font-semibold text-white disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <Check className="h-4 w-4" />
                            {selectedBusiness?.booking_settings?.logo_url
                              ? 'Logo looks good — continue'
                              : 'Upload a logo to continue'}
                          </button>
                          <button
                            type="button"
                            onClick={() => skipToNextItem(category)}
                            className="w-full px-4 py-2 text-sm font-medium text-gray-400 hover:text-gray-600 transition-colors"
                          >
                            Skip for now — I'll add it later
                          </button>
                        </div>
                      </>
                    ) : openItemId === 'select-currency' ? (
                      /* Item: currency (businesses.currency — same options as Business Settings) */
                      <>
                        <AssistantMessage
                          text={`What currency does your business charge in? This sets the currency for your prices, invoices, and payments.`}
                        />
                        {currencyLoading ? (
                          <div className="flex items-center justify-center py-10">
                            <div className="animate-spin rounded-full h-7 w-7 border-2 border-blue-500 border-t-transparent" />
                          </div>
                        ) : (
                          <div className="mt-5 ml-12 space-y-4">
                            <div className="space-y-2">
                              {CURRENCY_OPTIONS.map((option) => {
                                const selected = currencyValue === option.value;
                                return (
                                  <button
                                    key={option.value}
                                    type="button"
                                    onClick={() => setCurrencyValue(option.value)}
                                    disabled={currencySaving}
                                    className={`w-full flex items-center gap-3 rounded-xl border-2 px-4 py-3 text-left transition-colors disabled:opacity-60 ${
                                      selected
                                        ? 'border-blue-500 bg-blue-50'
                                        : 'border-gray-200 bg-white hover:border-gray-300'
                                    }`}
                                  >
                                    <span
                                      className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 ${
                                        selected ? 'border-blue-500 bg-blue-500' : 'border-gray-300 bg-white'
                                      }`}
                                    >
                                      {selected && <span className="h-2 w-2 rounded-full bg-white" />}
                                    </span>
                                    <span className="text-[14px] font-medium text-gray-900">{option.label}</span>
                                  </button>
                                );
                              })}
                            </div>

                            {currencyError && (
                              <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-[13px] text-red-700">
                                {currencyError}
                              </div>
                            )}

                            <button
                              type="button"
                              onClick={handleSaveCurrency}
                              disabled={currencySaving || currencyLoading}
                              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-blue-600 hover:bg-blue-700 transition-colors font-semibold text-white disabled:opacity-60"
                            >
                              {currencySaving ? (
                                <>
                                  <span className="h-4 w-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                                  Saving...
                                </>
                              ) : (
                                <>
                                  <Check className="h-4 w-4" />
                                  Confirm & save
                                </>
                              )}
                            </button>
                          </div>
                        )}
                      </>
                    ) : openItemId === 'tax-rates' ? (
                      /* Item: tax rates (same tax_rates table as Business Settings) */
                      <>
                        <AssistantMessage
                          text={`Let's set up the tax rates you charge — these get applied to your services and invoices (for example GST 5%, or PST 7%). Add each tax you collect — these tax rates will be applied to all services.\n\nIf you have any tax rates like PST that only apply to a few services, don't add them here. You can configure additional tax rates on specific services when creating your services.`}
                        />
                        {taxLoading ? (
                          <div className="flex items-center justify-center py-10">
                            <div className="animate-spin rounded-full h-7 w-7 border-2 border-blue-500 border-t-transparent" />
                          </div>
                        ) : (
                          <div className="mt-5 ml-12 space-y-4">
                            {/* Existing rates */}
                            {taxRates.length > 0 && (
                              <div className="space-y-2">
                                {taxRates.map((rate) => (
                                  <div
                                    key={rate.id}
                                    className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3"
                                  >
                                    <div className="flex-1 min-w-0">
                                      <p className="text-[14px] font-semibold text-gray-900">
                                        {rate.name}
                                        {rate.is_default && (
                                          <span className="ml-2 px-1.5 py-0.5 rounded bg-blue-50 text-blue-600 text-[10px] font-bold uppercase">
                                            Default
                                          </span>
                                        )}
                                      </p>
                                    </div>
                                    <span className="text-[14px] font-semibold text-gray-700">{rate.rate}%</span>
                                    <button
                                      type="button"
                                      onClick={() => handleDeleteTaxRate(rate)}
                                      disabled={taxSaving}
                                      className="p-1.5 text-gray-400 hover:text-red-500 transition-colors disabled:opacity-50"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* Add a rate */}
                            <div className="flex gap-2">
                              <input
                                type="text"
                                placeholder="Tax name (e.g. GST)"
                                value={newTaxName}
                                onChange={(e) => setNewTaxName(e.target.value)}
                                disabled={taxSaving}
                                className="flex-1 min-w-0 rounded-lg border border-gray-200 px-3.5 py-2.5 text-[14px] text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 disabled:opacity-60"
                              />
                              <div className="relative w-24 shrink-0">
                                <input
                                  type="number"
                                  min="0"
                                  max="100"
                                  step="0.001"
                                  placeholder="5"
                                  value={newTaxRate}
                                  onChange={(e) => setNewTaxRate(e.target.value)}
                                  disabled={taxSaving}
                                  className="w-full rounded-lg border border-gray-200 pl-3 pr-7 py-2.5 text-[14px] text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 disabled:opacity-60"
                                />
                                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[13px] text-gray-400">%</span>
                              </div>
                              <button
                                type="button"
                                onClick={handleAddTaxRate}
                                disabled={taxSaving}
                                className="shrink-0 inline-flex items-center justify-center h-[42px] w-[42px] rounded-lg bg-blue-600 hover:bg-blue-700 text-white transition-colors disabled:opacity-50"
                              >
                                <Plus className="h-4 w-4" />
                              </button>
                            </div>

                            {taxError && (
                              <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-[13px] text-red-700">
                                {taxError}
                              </div>
                            )}

                            <button
                              type="button"
                              onClick={async () => {
                                await markItemComplete('business-info', 'tax-rates');
                                advanceAfterItem(businessInfoCategory, 'tax-rates');
                              }}
                              disabled={taxSaving || taxRates.length === 0}
                              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-blue-600 hover:bg-blue-700 transition-colors font-semibold text-white disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <Check className="h-4 w-4" />
                              {taxRates.length === 0 ? 'Add a tax rate to continue' : 'Tax rates done — continue'}
                            </button>
                            <button
                              type="button"
                              onClick={() => skipToNextItem(category)}
                              className="w-full px-4 py-2 text-sm font-medium text-gray-400 hover:text-gray-600 transition-colors"
                            >
                              Skip for now — I don't charge tax / I'll add it later
                            </button>
                          </div>
                        )}
                      </>
                    ) : openItemId === 'tax-numbers' ? (
                      /* Item: GST/PST numbers (businesses.gst_number / pst_number) */
                      <>
                        <AssistantMessage
                          text={`If your business is registered for GST or PST, add your registration numbers here — they'll appear on your invoices, which is required in many regions once you're collecting tax.`}
                        />
                        {taxNumbersLoading ? (
                          <div className="flex items-center justify-center py-10">
                            <div className="animate-spin rounded-full h-7 w-7 border-2 border-blue-500 border-t-transparent" />
                          </div>
                        ) : (
                          <div className="mt-5 ml-12 space-y-4">
                            <div>
                              <label className="block text-xs font-semibold text-gray-500 mb-1.5">GST Number</label>
                              <input
                                type="text"
                                placeholder="e.g. 123456789 RT0001"
                                value={taxNumbers.gst_number}
                                onChange={(e) => setTaxNumbers((prev) => ({ ...prev, gst_number: e.target.value }))}
                                disabled={taxNumbersSaving}
                                className="w-full rounded-lg border border-gray-200 px-3.5 py-2.5 text-[15px] text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 disabled:opacity-60"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-semibold text-gray-500 mb-1.5">PST Number</label>
                              <input
                                type="text"
                                placeholder="e.g. PST-1234-5678"
                                value={taxNumbers.pst_number}
                                onChange={(e) => setTaxNumbers((prev) => ({ ...prev, pst_number: e.target.value }))}
                                disabled={taxNumbersSaving}
                                className="w-full rounded-lg border border-gray-200 px-3.5 py-2.5 text-[15px] text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 disabled:opacity-60"
                              />
                            </div>

                            {taxNumbersError && (
                              <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-[13px] text-red-700">
                                {taxNumbersError}
                              </div>
                            )}

                            <button
                              type="button"
                              onClick={handleSaveTaxNumbers}
                              disabled={taxNumbersSaving || taxNumbersLoading}
                              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-blue-600 hover:bg-blue-700 transition-colors font-semibold text-white disabled:opacity-60"
                            >
                              {taxNumbersSaving ? (
                                <>
                                  <span className="h-4 w-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                                  Saving...
                                </>
                              ) : (
                                <>
                                  <Check className="h-4 w-4" />
                                  Confirm & save
                                </>
                              )}
                            </button>
                            <button
                              type="button"
                              onClick={() => skipToNextItem(category)}
                              className="w-full px-4 py-2 text-sm font-medium text-gray-400 hover:text-gray-600 transition-colors"
                            >
                              Skip for now — I don't have these / I'll add them later
                            </button>
                          </div>
                        )}
                      </>
                    ) : openItemId === 'business-hours' ? (
                      /* Item: business hours (businesses.business_hours — drives calendar slots) */
                      <>
                        <AssistantMessage
                          text={`Now let's set your business hours. These directly control the available slots in your calendar — if you set 9am–5pm, the calendar will only allow bookings within those times. Mark the days you don't work as closed.`}
                        />
                        {hoursLoading ? (
                          <div className="flex items-center justify-center py-10">
                            <div className="animate-spin rounded-full h-7 w-7 border-2 border-blue-500 border-t-transparent" />
                          </div>
                        ) : (
                          <div className="mt-5 ml-12 space-y-4">
                            <div className="space-y-2">
                              {WEEK_DAYS.map((day) => {
                                const h = weeklyHours[day] || DEFAULT_WEEKLY_HOURS[day];
                                return (
                                  <div
                                    key={day}
                                    className={`flex items-center gap-3 rounded-xl border px-3.5 py-2.5 ${
                                      h.closed ? 'border-gray-200 bg-gray-50' : 'border-gray-200 bg-white'
                                    }`}
                                  >
                                    <button
                                      type="button"
                                      onClick={() =>
                                        setWeeklyHours((prev) => ({
                                          ...prev,
                                          [day]: { ...h, closed: !h.closed, enabled: h.closed },
                                        }))
                                      }
                                      disabled={hoursSaving}
                                      className={`relative inline-flex h-5 w-9 shrink-0 rounded-full border-2 border-transparent transition-colors ${
                                        h.closed ? 'bg-gray-300' : 'bg-blue-500'
                                      }`}
                                      title={h.closed ? 'Closed — tap to open' : 'Open — tap to close'}
                                    >
                                      <span
                                        className={`inline-block h-4 w-4 rounded-full bg-white shadow transform transition ${
                                          h.closed ? 'translate-x-0' : 'translate-x-4'
                                        }`}
                                      />
                                    </button>
                                    <span className="w-20 text-[13px] font-semibold text-gray-900 capitalize shrink-0">
                                      {day}
                                    </span>
                                    {h.closed ? (
                                      <span className="text-[13px] text-gray-400">Closed</span>
                                    ) : (
                                      <div className="flex items-center gap-1.5 flex-1 min-w-0">
                                        <input
                                          type="time"
                                          value={h.open}
                                          onChange={(e) =>
                                            setWeeklyHours((prev) => ({ ...prev, [day]: { ...h, open: e.target.value } }))
                                          }
                                          disabled={hoursSaving}
                                          className="rounded-lg border border-gray-200 px-2 py-1.5 text-[13px] text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500/30 disabled:opacity-60"
                                        />
                                        <span className="text-[12px] text-gray-400">to</span>
                                        <input
                                          type="time"
                                          value={h.close}
                                          onChange={(e) =>
                                            setWeeklyHours((prev) => ({ ...prev, [day]: { ...h, close: e.target.value } }))
                                          }
                                          disabled={hoursSaving}
                                          className="rounded-lg border border-gray-200 px-2 py-1.5 text-[13px] text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500/30 disabled:opacity-60"
                                        />
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>

                            {hoursError && (
                              <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-[13px] text-red-700">
                                {hoursError}
                              </div>
                            )}

                            <button
                              type="button"
                              onClick={handleSaveBusinessHours}
                              disabled={hoursSaving || hoursLoading}
                              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-blue-600 hover:bg-blue-700 transition-colors font-semibold text-white disabled:opacity-60"
                            >
                              {hoursSaving ? (
                                <>
                                  <span className="h-4 w-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                                  Saving...
                                </>
                              ) : (
                                <>
                                  <Check className="h-4 w-4" />
                                  Confirm & save
                                </>
                              )}
                            </button>
                          </div>
                        )}
                      </>
                    ) : openItemId === 'create-team' && teamPhase === 'size' ? (
                      /* Item: create team members — phase 1: how many (plan recap + seat purchase) */
                      <>
                        <AssistantMessage
                          text={
                            teamUsage
                              ? `Now it's time to set up your team! ${
                                  teamUsage.isUnlimited
                                    ? 'Your plan includes unlimited team members.'
                                    : `Your plan includes ${teamUsage.planIncluded} team member${
                                        teamUsage.planIncluded === 1 ? '' : 's'
                                      }${
                                        teamUsage.purchased > 0
                                          ? ` plus ${teamUsage.purchased} extra seat${teamUsage.purchased === 1 ? '' : 's'} you've added (${teamUsage.totalLimit} total)`
                                          : ''
                                      }.`
                                }\n\nHow many users will need accounts?`
                              : `Now it's time to set up your team!`
                          }
                        />
                        {teamUsageLoading ? (
                          <div className="flex items-center justify-center py-10">
                            <div className="animate-spin rounded-full h-7 w-7 border-2 border-blue-500 border-t-transparent" />
                          </div>
                        ) : (
                          <div className="mt-5 ml-12 space-y-3">
                            {/* Just me */}
                            <button
                              type="button"
                              onClick={() => setTeamAnswer('just-me')}
                              disabled={buyingSeats}
                              className={`w-full flex items-center gap-3 rounded-xl border-2 px-4 py-3.5 text-left transition-colors disabled:opacity-60 ${
                                teamAnswer === 'just-me'
                                  ? 'border-blue-500 bg-blue-50'
                                  : 'border-gray-200 bg-white hover:border-gray-300'
                              }`}
                            >
                              <span
                                className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 ${
                                  teamAnswer === 'just-me' ? 'border-blue-500 bg-blue-500' : 'border-gray-300 bg-white'
                                }`}
                              >
                                {teamAnswer === 'just-me' && <span className="h-2 w-2 rounded-full bg-white" />}
                              </span>
                              <div className="flex-1">
                                <p className="text-[14px] font-semibold text-gray-900">Just me</p>
                                <p className="text-[12px] text-gray-500">
                                  You'll be set up as a team member using your existing admin login.
                                </p>
                              </div>
                            </button>

                            {/* Multiple */}
                            <button
                              type="button"
                              onClick={() => setTeamAnswer('multiple')}
                              disabled={buyingSeats}
                              className={`w-full flex items-center gap-3 rounded-xl border-2 px-4 py-3.5 text-left transition-colors disabled:opacity-60 ${
                                teamAnswer === 'multiple'
                                  ? 'border-blue-500 bg-blue-50'
                                  : 'border-gray-200 bg-white hover:border-gray-300'
                              }`}
                            >
                              <span
                                className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 ${
                                  teamAnswer === 'multiple' ? 'border-blue-500 bg-blue-500' : 'border-gray-300 bg-white'
                                }`}
                              >
                                {teamAnswer === 'multiple' && <span className="h-2 w-2 rounded-full bg-white" />}
                              </span>
                              <div className="flex-1">
                                <p className="text-[14px] font-semibold text-gray-900">Multiple team members</p>
                                <p className="text-[12px] text-gray-500">Create accounts for your whole crew.</p>
                              </div>
                            </button>

                            {/* How many */}
                            {teamAnswer === 'multiple' && (
                              <div className="flex items-center gap-3 pl-2">
                                <label className="text-[13px] font-medium text-gray-600 flex-1">
                                  How many in total, including yourself if you are a bookable team member?
                                </label>
                                <select
                                  value={teamCount}
                                  onChange={(e) => setTeamCount(Number(e.target.value))}
                                  disabled={buyingSeats}
                                  className="rounded-lg border border-gray-200 px-3 py-2 text-[14px] text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500/30 disabled:opacity-60"
                                >
                                  {Array.from({ length: 29 }, (_, i) => i + 2).map((n) => (
                                    <option key={n} value={n}>{n}</option>
                                  ))}
                                </select>
                              </div>
                            )}

                            {/* Over-limit confirmation */}
                            {extraSeatsNeeded > 0 && teamUsage && (
                              <div className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 text-[13px] text-amber-900 space-y-1">
                                <p className="font-semibold">
                                  Your plan covers {teamUsage.totalLimit} team member{teamUsage.totalLimit === 1 ? '' : 's'} — {teamCount} needs {extraSeatsNeeded} extra seat{extraSeatsNeeded === 1 ? '' : 's'}.
                                </p>
                                <p>
                                  Extra seats are ${extraSeatMonthlyUsd().toFixed(2)}/seat/mo (+$
                                  {(extraSeatsNeeded * extraSeatMonthlyUsd()).toFixed(2)}/mo), added to your subscription.
                                  {subscription?.subscription_status === 'trialing'
                                    ? ' Included in your free trial — billing only starts if you keep your subscription after the trial.'
                                    : ''}
                                </p>
                              </div>
                            )}

                            {teamError && (
                              <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-[13px] text-red-700">
                                {teamError}
                              </div>
                            )}

                            <button
                              type="button"
                              onClick={handleConfirmTeamSize}
                              disabled={!teamAnswer || buyingSeats || teamUsageLoading}
                              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-blue-600 hover:bg-blue-700 transition-colors font-semibold text-white disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {buyingSeats ? (
                                <>
                                  <span className="h-4 w-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                                  Adding seats...
                                </>
                              ) : extraSeatsNeeded > 0 ? (
                                <>
                                  <Check className="h-4 w-4" />
                                  Add {extraSeatsNeeded} extra seat{extraSeatsNeeded === 1 ? '' : 's'} & continue
                                </>
                              ) : (
                                <>
                                  <Check className="h-4 w-4" />
                                  Continue
                                </>
                              )}
                            </button>
                          </div>
                        )}
                      </>
                    ) : openItemId === 'create-team' && teamPhase === 'self-question' ? (
                      /* Item: create team members — bookable-self question (multiple path) */
                      <>
                        <AssistantMessage
                          text={`One quick question first — are you a bookable team member? That means you take jobs yourself and should appear on the calendar alongside your team.`}
                        />
                        <div className="mt-5 ml-12 space-y-3">
                          <button
                            type="button"
                            onClick={() => handleBookableSelfAnswer(true)}
                            className="w-full flex items-center gap-3 rounded-xl border-2 border-gray-200 bg-white hover:border-blue-500 hover:bg-blue-50/50 px-4 py-3.5 text-left transition-colors"
                          >
                            <div className="flex-1">
                              <p className="text-[14px] font-semibold text-gray-900">Yes — I take bookings too</p>
                              <p className="text-[12px] text-gray-500">
                                We'll set up your own profile first (linked to your admin login), then the rest of the team.
                              </p>
                            </div>
                            <ChevronRight className="h-4 w-4 text-gray-400 shrink-0" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleBookableSelfAnswer(false)}
                            className="w-full flex items-center gap-3 rounded-xl border-2 border-gray-200 bg-white hover:border-blue-500 hover:bg-blue-50/50 px-4 py-3.5 text-left transition-colors"
                          >
                            <div className="flex-1">
                              <p className="text-[14px] font-semibold text-gray-900">No — I just manage the business</p>
                              <p className="text-[12px] text-gray-500">
                                We'll go straight to creating accounts for your team.
                              </p>
                            </div>
                            <ChevronRight className="h-4 w-4 text-gray-400 shrink-0" />
                          </button>
                        </div>
                      </>
                    ) : openItemId === 'create-team' ? (
                      /* Item: create team members — the accounts (reuses the real team-member modal) */
                      <>
                        <AssistantMessage
                          text={
                            teamAnswer === 'just-me' && adminLinkedExists
                              ? `Good news — you're already set up as a team member linked to your admin login. You can add more team members below, or just continue.`
                              : teamAnswer === 'just-me'
                                ? `Let's create your team member profile. I've pre-selected the option that links it to your admin login — no separate account needed.`
                                : creatingSelfProfile
                                  ? `Let's start with you! Create your own team member profile first — I've pre-selected linking it to your admin login, so no separate account is needed. Once you're set up, we'll add the rest of your team.`
                                  : adminLinkedExists || (isBookableSelf === true && teamMembersCreated > 0)
                                    ? `You're set up! Now add the rest of your team — use "Create & Invite" to email each person a signup link, or "Create" to add them without a login for now.`
                                    : `Let's create the accounts. Add each team member below — use "Create & Invite" to email them a signup link, or "Create" to add them without a login for now.`
                          }
                        />
                        <div className="mt-5 ml-12 space-y-3">
                          {/* Current team — live from the database, includes members created outside the wizard */}
                          {wizardResourcesLoading ? (
                            <div className="flex items-center justify-center py-4">
                              <div className="animate-spin rounded-full h-5 w-5 border-2 border-blue-500 border-t-transparent" />
                            </div>
                          ) : wizardResources.length > 0 ? (
                            <div className="space-y-2">
                              <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide">
                                Your team ({wizardResources.length})
                              </p>
                              {wizardResources.map((r) => (
                                <div key={r.id} className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white px-4 py-2.5">
                                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-100 text-[12px] font-semibold text-gray-600">
                                    {(r.first_name?.[0] || '')}{(r.last_name?.[0] || '')}
                                  </span>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-[13px] font-semibold text-gray-900 truncate">
                                      {r.first_name} {r.last_name}
                                    </p>
                                    {r.email && <p className="text-[11px] text-gray-400 truncate">{r.email}</p>}
                                  </div>
                                  {r.auth_user_id && r.auth_user_id === selectedBusiness?.owner_id && (
                                    <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-purple-100 text-purple-700 shrink-0">
                                      Admin
                                    </span>
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : null}
                          <button
                            type="button"
                            onClick={() => setResourceModalOpen(true)}
                            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 border-blue-200 bg-blue-50 hover:border-blue-500 transition-colors font-semibold text-blue-800"
                          >
                            <Plus className="h-4 w-4" />
                            {creatingSelfProfile ? 'Create my profile' : 'Add team member'}
                          </button>
                          <button
                            type="button"
                            onClick={async () => {
                              await markItemComplete('team-setup', 'create-team');
                              advanceAfterItem(teamSetupCategory, 'create-team');
                            }}
                            disabled={wizardResources.length === 0 && teamMembersCreated === 0}
                            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-blue-600 hover:bg-blue-700 transition-colors font-semibold text-white disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <Check className="h-4 w-4" />
                            {wizardResources.length === 0 && teamMembersCreated === 0
                              ? 'Add a team member to continue'
                              : 'Done — continue'}
                          </button>
                          <button
                            type="button"
                            onClick={() => skipToNextItem(category)}
                            className="w-full px-4 py-2 text-sm font-medium text-gray-400 hover:text-gray-600 transition-colors"
                          >
                            Skip for now — I'll add my team later
                          </button>
                        </div>
                        <ResourceModal
                          isOpen={resourceModalOpen}
                          onClose={() => setResourceModalOpen(false)}
                          onSuccess={() => {
                            setResourceModalOpen(false);
                            const created = teamMembersCreated + 1;
                            setTeamMembersCreated(created);
                            // Persist so a close/reopen resumes here (not back at the size question).
                            const snapshot = {
                              answer: teamAnswer,
                              count: teamCount,
                              bookable_self: isBookableSelf,
                              phase: 'create',
                              members_created: created,
                            };
                            setTeamSaved(snapshot);
                            void logResponse(2, 'team-member:created', { team_setup: snapshot });
                            void loadWizardResources();
                          }}
                          businessId={selectedBusiness?.id}
                          defaultLinkToAdmin={creatingSelfProfile}
                        />
                      </>
                    ) : openItemId === 'team-hours' ? (
                      /* Item: set each team member's working hours (schedule only —
                         no mobile toggle or service areas at this step) */
                      <>
                        <AssistantMessage
                          text={
                            !wizardResourcesLoading && wizardResources.length === 0
                              ? `You don't have any team members yet — head back to the previous step and create your first team member, then come back here to set their hours.`
                              : `Team created! Now let's set each person's working hours.\n\nThese hours are each team member's default weekly schedule — they control when that person can be booked on the calendar. Tap a team member, hit the pencil, and set the days and times they work. You can fine-tune these anytime later.`
                          }
                        />
                        <div className="mt-5 ml-12 space-y-3">
                          {wizardResourcesLoading ? (
                            <div className="flex items-center justify-center py-6">
                              <div className="animate-spin rounded-full h-5 w-5 border-2 border-blue-500 border-t-transparent" />
                            </div>
                          ) : wizardResources.length === 0 ? (
                            <button
                              type="button"
                              onClick={() => setOpenItemId('create-team')}
                              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-blue-600 hover:bg-blue-700 transition-colors font-semibold text-white"
                            >
                              <Users className="h-4 w-4" />
                              Back to create team members
                            </button>
                          ) : (
                            <div className="space-y-2">
                              {wizardResources.map((resource) => {
                                const expanded = hoursExpandedId === resource.id;
                                return (
                                  <div key={resource.id} className="rounded-xl border border-gray-200 bg-white overflow-hidden">
                                    <button
                                      type="button"
                                      onClick={() => setHoursExpandedId(expanded ? null : resource.id)}
                                      className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors"
                                    >
                                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 text-white text-[11px] font-bold">
                                        {(resource.first_name?.[0] || '') + (resource.last_name?.[0] || '')}
                                      </span>
                                      <span className="flex-1 min-w-0">
                                        <span className="block text-[13px] font-semibold text-gray-900 truncate">
                                          {resource.first_name} {resource.last_name}
                                        </span>
                                        <span className="block text-[11px] text-gray-400 truncate">
                                          {expanded ? 'Set the days and hours below' : 'Tap to set working hours'}
                                        </span>
                                      </span>
                                      <ChevronDown
                                        className={`h-4 w-4 text-gray-400 shrink-0 transition-transform ${expanded ? 'rotate-180' : ''}`}
                                      />
                                    </button>
                                    {expanded && selectedBusiness?.id && (
                                      <div className="border-t border-gray-100">
                                        <ResourceAvailability
                                          resourceId={resource.id}
                                          businessId={selectedBusiness.id}
                                          scheduleOnly
                                        />
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}

                          {wizardResources.length > 0 && (
                            <button
                              type="button"
                              onClick={async () => {
                                await markItemComplete('team-setup', 'team-hours');
                                advanceAfterItem(teamSetupCategory, 'team-hours');
                              }}
                              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-blue-600 hover:bg-blue-700 transition-colors font-semibold text-white"
                            >
                              <Check className="h-4 w-4" />
                              Done — hours are set
                            </button>
                          )}

                          <button
                            type="button"
                            onClick={() => skipToNextItem(category)}
                            className="w-full px-4 py-2 text-sm font-medium text-gray-400 hover:text-gray-600 transition-colors"
                          >
                            Skip for now — I'll set hours later
                          </button>
                        </div>
                      </>
                    ) : openItemId === 'service-areas' ? (
                        /* Item: service areas gate — controls who can book online */
                        <>
                          <AssistantMessage
                            text={`Perfect — customers will be able to book you 24/7 from your booking page.\n\nFirst, something important: online booking is controlled by your service areas. The very first thing your booking page asks a customer for is their address — and Nexus checks it against the areas you serve. Inside your service area? They keep booking. Outside it? They can't book, so you never get jobs you'd have to turn down.\n\nService areas are simply the postal codes you cover — you can group them into territories like "North Vancouver" or "Burnaby".\n\nReady to set up your service areas?`}
                          />
                          <div className="mt-5 ml-12 space-y-3">
                            {/* Overlapping postal codes warning (same rule as settings) */}
                            {svcAreaOverlaps.size > 0 && (
                              <div className="rounded-xl border border-red-200 bg-red-50 p-4">
                                <div className="flex items-start gap-2">
                                  <AlertTriangle className="h-4 w-4 text-red-600 mt-0.5 shrink-0" />
                                  <div className="flex-1">
                                    <p className="text-[13px] font-semibold text-red-900">
                                      Overlapping postal codes detected
                                    </p>
                                    <p className="text-[12px] text-red-700 mt-0.5 mb-2.5">
                                      {svcAreaOverlaps.size} postal code{svcAreaOverlaps.size === 1 ? ' appears' : 's appear'} in
                                      more than one area. I can clean that up — each code stays in the area that had it first.
                                    </p>
                                    <button
                                      type="button"
                                      onClick={handleFixSvcAreaOverlaps}
                                      disabled={svcAreasFixing}
                                      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-red-600 hover:bg-red-700 text-white text-[12px] font-semibold transition-colors disabled:opacity-50"
                                    >
                                      {svcAreasFixing ? (
                                        <>
                                          <div className="animate-spin rounded-full h-3 w-3 border-2 border-white border-t-transparent" />
                                          Fixing…
                                        </>
                                      ) : (
                                        'Fix overlaps'
                                      )}
                                    </button>
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Existing areas */}
                            {svcAreas.length > 0 && (
                              <div className="space-y-2">
                                {svcAreas.map((area) => {
                                  const overlapCount = area.postal_codes.filter((code) => svcAreaOverlaps.has(code)).length;
                                  return (
                                    <div
                                      key={area.id}
                                      className={`flex items-center gap-3 rounded-xl border px-4 py-3 ${
                                        overlapCount > 0 ? 'border-red-200 bg-red-50/60' : 'border-green-200 bg-green-50/60'
                                      }`}
                                    >
                                      <MapPin className={`h-4 w-4 shrink-0 ${overlapCount > 0 ? 'text-red-500' : 'text-green-600'}`} />
                                      <div className="flex-1 min-w-0">
                                        <p className="text-[13px] font-semibold text-gray-900 truncate">{area.name}</p>
                                        <p className="text-[11px] text-gray-500">
                                          {area.postal_codes.length} postal code{area.postal_codes.length === 1 ? '' : 's'} covered
                                        </p>
                                      </div>
                                      {overlapCount > 0 && (
                                        <span className="px-1.5 py-0.5 rounded bg-red-100 text-red-700 text-[10px] font-semibold uppercase whitespace-nowrap shrink-0">
                                          {overlapCount} overlap{overlapCount === 1 ? '' : 's'}
                                        </span>
                                      )}
                                      <button
                                        type="button"
                                        onClick={() => handleDeleteServiceArea(area.id)}
                                        className="p-1.5 text-gray-300 hover:text-red-500 transition-colors shrink-0"
                                        title="Remove this area"
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </button>
                                    </div>
                                  );
                                })}
                              </div>
                            )}

                            {/* Add-area entry point / inline lookup form */}
                            {!svcAreaFormOpen ? (
                              <button
                                type="button"
                                onClick={() => setSvcAreaFormOpen(true)}
                                className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 transition-colors font-semibold ${
                                  svcAreas.length === 0
                                    ? 'border-dashed border-blue-300 bg-blue-50/60 hover:border-blue-500 text-blue-800'
                                    : 'border-gray-200 bg-gray-50 hover:border-blue-300 text-gray-600'
                                }`}
                              >
                                <Plus className="h-4 w-4" />
                                {svcAreas.length === 0 ? 'Add your first area' : 'Add another area'}
                              </button>
                            ) : (
                              <div className="rounded-xl border border-blue-200 bg-blue-50/40 p-4 space-y-3">
                                <div>
                                  <label className="block text-[12px] font-semibold text-gray-700 mb-1.5">
                                    Which city do you serve?
                                  </label>
                                  <CityAutocompleteInput
                                    value={svcAreaCityValue}
                                    onChange={(value) => {
                                      setSvcAreaCityValue(value);
                                      if (!value) {
                                        setSvcAreaCity(null);
                                        setSvcAreaCodes([]);
                                      }
                                    }}
                                    onCitySelected={(sel) => {
                                      setSvcAreaCity({ city: sel.city, state: sel.state, country: sel.country });
                                      void fetchSvcAreaCodes(sel.city, sel.cityAlternatives, sel.state, sel.country);
                                    }}
                                    placeholder="Start typing a city (e.g. Vancouver)"
                                    className="w-full rounded-lg border border-gray-300 bg-white px-3.5 py-2.5 text-[14px] focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                                  />
                                  <p className="mt-1 text-[11px] text-gray-400">
                                    Pick your city from the suggestions — I'll find its postal codes automatically.
                                  </p>
                                </div>

                                {svcAreaFetching && (
                                  <div className="flex items-center gap-2 text-[13px] text-gray-500">
                                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-500 border-t-transparent" />
                                    Finding postal codes…
                                  </div>
                                )}

                                {svcAreaCodes.length > 0 && svcAreaCity && (
                                  <div className="rounded-lg border border-green-200 bg-green-50/70 p-3">
                                    <p className="text-[12px] font-semibold text-gray-900 mb-2">
                                      Found {svcAreaCodes.length} postal code{svcAreaCodes.length === 1 ? '' : 's'} for{' '}
                                      {svcAreaCity.city}, {svcAreaCity.state}
                                    </p>
                                    <div className="flex flex-wrap gap-1 max-h-24 overflow-y-auto">
                                      {svcAreaCodes.map((code) => (
                                        <span
                                          key={code}
                                          className="px-1.5 py-0.5 rounded bg-white border border-green-300 text-[10px] font-medium text-gray-700"
                                        >
                                          {code}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {svcAreaError && (
                                  <p className="text-[12px] font-medium text-red-600">{svcAreaError}</p>
                                )}

                                <div className="flex gap-2">
                                  <button
                                    type="button"
                                    onClick={handleCreateServiceArea}
                                    disabled={svcAreaCodes.length === 0 || svcAreaSaving}
                                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 transition-colors text-[13px] font-semibold text-white disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    {svcAreaSaving ? 'Saving…' : 'Create area'}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={resetSvcAreaForm}
                                    className="px-4 py-2.5 rounded-lg border border-gray-200 bg-white text-[13px] font-semibold text-gray-500 hover:text-gray-700 transition-colors"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            )}

                            <button
                              type="button"
                              onClick={async () => {
                                await markItemComplete('services-booking', 'service-areas');
                                advanceAfterItem(category, 'service-areas');
                              }}
                              disabled={svcAreas.length === 0}
                              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-blue-600 hover:bg-blue-700 transition-colors font-semibold text-white disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {svcAreas.length === 0 ? 'Add a service area to continue' : 'Continue — set up your staff'}
                              <ChevronRight className="h-4 w-4" />
                            </button>

                            <button
                              type="button"
                              onClick={() => skipToNextItem(category)}
                              className="w-full px-4 py-2 text-sm font-medium text-gray-400 hover:text-gray-600 transition-colors"
                            >
                              Skip for now — I'll set up online booking later
                            </button>
                          </div>
                        </>
                      ) : openItemId === 'staff-booking' ? (
                        /* Item: staff setup — toggle online booking per team member */
                        <>
                          <AssistantMessage
                            text={
                              !wizardResourcesLoading && wizardResources.length === 0
                                ? `One thing before we can continue: online bookings are always booked onto a team member's timeline — and you don't have any team members yet.\n\nHead back to Team Setup and create your first team member (that can be you!), then come back to this step to finish online booking.`
                                : `Service areas — done. Now let's set up who takes the bookings.\n\nToggle online booking ON for every team member who should accept online bookings. Here's the key: each team member has their OWN independent timeline. Their working days, hours, and service areas are theirs alone — customers book a specific person's open time, so two team members can serve different areas on different schedules at the same time.\n\nFor each person you toggle on: set at least one working day with hours, then assign the service areas they cover on each of those days.`
                            }
                          />
                          <div className="mt-5 ml-12 space-y-3">
                            {wizardResourcesLoading ? (
                              <div className="flex items-center justify-center py-6">
                                <div className="animate-spin rounded-full h-5 w-5 border-2 border-blue-500 border-t-transparent" />
                              </div>
                            ) : wizardResources.length === 0 ? (
                              <button
                                type="button"
                                onClick={() => {
                                  setOpenCategoryId('team-setup');
                                  setOpenItemId('create-team');
                                }}
                                className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-blue-600 hover:bg-blue-700 transition-colors font-semibold text-white"
                              >
                                <Users className="h-4 w-4" />
                                Go to Team Setup
                              </button>
                            ) : (
                              <div className="space-y-2">
                                {wizardResources.map((resource) => {
                                  const expanded = staffExpandedId === resource.id;
                                  return (
                                    <div key={resource.id} className="rounded-xl border border-gray-200 bg-white overflow-hidden">
                                      <button
                                        type="button"
                                        onClick={() => setStaffExpandedId(expanded ? null : resource.id)}
                                        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors"
                                      >
                                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 text-white text-[11px] font-bold">
                                          {(resource.first_name?.[0] || '') + (resource.last_name?.[0] || '')}
                                        </span>
                                        <span className="flex-1 min-w-0">
                                          <span className="block text-[13px] font-semibold text-gray-900 truncate">
                                            {resource.first_name} {resource.last_name}
                                          </span>
                                          <span className="block text-[11px] text-gray-400 truncate">
                                            {expanded ? 'Toggle online booking, set hours & areas below' : 'Tap to set up online booking'}
                                          </span>
                                        </span>
                                        <ChevronDown
                                          className={`h-4 w-4 text-gray-400 shrink-0 transition-transform ${expanded ? 'rotate-180' : ''}`}
                                        />
                                      </button>
                                      {expanded && selectedBusiness?.id && (
                                        <div className="border-t border-gray-100">
                                          <ResourceAvailability
                                            resourceId={resource.id}
                                            businessId={selectedBusiness.id}
                                            toggleLabel="Online Booking"
                                          />
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}

                            {staffError && (
                              <p className="text-[13px] font-medium text-red-600">{staffError}</p>
                            )}

                            {wizardResources.length > 0 && (
                              <button
                                type="button"
                                onClick={handleStaffContinue}
                                disabled={staffValidating || wizardResourcesLoading}
                                className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-blue-600 hover:bg-blue-700 transition-colors font-semibold text-white disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {staffValidating ? 'Checking your setup…' : 'Continue — how online booking works'}
                                <ChevronRight className="h-4 w-4" />
                              </button>
                            )}

                            <button
                              type="button"
                              onClick={() => setOpenItemId('service-areas')}
                              className="w-full px-4 py-2 text-sm font-medium text-gray-400 hover:text-gray-600 transition-colors"
                            >
                              Back to service areas
                            </button>
                            <button
                              type="button"
                              onClick={() => skipToNextItem(category)}
                              className="w-full px-4 py-2 text-sm font-medium text-gray-400 hover:text-gray-600 transition-colors"
                            >
                              Skip for now — I'll set up online booking later
                            </button>
                          </div>
                        </>
                      ) : openItemId === 'booking-education' ? (
                        /* Item: how online booking works (the click-path lesson) */
                        <>
                          <AssistantMessage
                            text={`Now for the key idea: your booking page is a linear click path that YOU design. The customer answers one simple question per screen — Step 1: a category, Step 2: a sub-category, Step 3: the service — and each answer narrows down the next screen until the right service (at the right price) is in front of them.\n\nLet me show you what that looks like for a detailer.`}
                          />

                          {/* Visual: the customer's linear click path */}
                          <div className="mt-5 ml-12 space-y-3">
                            <div className="rounded-2xl border border-gray-200 bg-white p-4 space-y-1">
                              <p className="text-[11px] font-bold uppercase tracking-wider text-gray-400 mb-2">
                                The click path your customer walks
                              </p>

                              {/* Step 1: category */}
                              <div className="rounded-xl bg-gray-50 border border-gray-200 p-3">
                                <p className="text-[11px] font-bold text-blue-600 mb-1.5">STEP 1 · Category — "what do you drive?"</p>
                                <div className="flex flex-wrap gap-1.5">
                                  <span className="px-2.5 py-1 rounded-lg bg-white border border-gray-200 text-[12px] text-gray-600">Car</span>
                                  <span className="px-2.5 py-1 rounded-lg bg-blue-600 text-white text-[12px] font-semibold">SUV ✓</span>
                                  <span className="px-2.5 py-1 rounded-lg bg-white border border-gray-200 text-[12px] text-gray-600">Truck</span>
                                </div>
                                <p className="text-[11px] text-gray-400 mt-1.5">The first question on the page. A detailer often splits by vehicle type.</p>
                              </div>

                              <div className="flex justify-center py-0.5">
                                <ChevronDown className="h-4 w-4 text-gray-300" />
                              </div>

                              {/* Step 2: sub-category */}
                              <div className="rounded-xl bg-gray-50 border border-gray-200 p-3">
                                <p className="text-[11px] font-bold text-blue-600 mb-1.5">STEP 2 · Sub-category — "what kind of work?"</p>
                                <div className="flex flex-wrap gap-1.5">
                                  <span className="px-2.5 py-1 rounded-lg bg-white border border-gray-200 text-[12px] text-gray-600">Interior</span>
                                  <span className="px-2.5 py-1 rounded-lg bg-white border border-gray-200 text-[12px] text-gray-600">Exterior</span>
                                  <span className="px-2.5 py-1 rounded-lg bg-blue-600 text-white text-[12px] font-semibold">Interior + Exterior ✓</span>
                                </div>
                                <p className="text-[11px] text-gray-400 mt-1.5">Each category has its own sub-categories — the second question.</p>
                              </div>

                              <div className="flex justify-center py-0.5">
                                <ChevronDown className="h-4 w-4 text-gray-300" />
                              </div>

                              {/* Step 3: service (with the price option assigned to this branch) */}
                              <div className="rounded-xl bg-gray-50 border border-gray-200 p-3">
                                <p className="text-[11px] font-bold text-blue-600 mb-1.5">STEP 3 · Service — the right one, at the right price</p>
                                <div className="space-y-1.5">
                                  <div className="rounded-lg bg-blue-600 px-3 py-2">
                                    <div className="flex items-center justify-between">
                                      <p className="text-[12px] font-semibold text-white">Full Detail ✓</p>
                                      <p className="text-[12px] text-blue-100 whitespace-nowrap">$189 · 3 h</p>
                                    </div>
                                    <p className="text-[11px] text-blue-100">Deep interior clean + wash, clay bar & wax</p>
                                  </div>
                                  <div className="rounded-lg bg-white border border-gray-200 px-3 py-2">
                                    <div className="flex items-center justify-between">
                                      <p className="text-[12px] font-semibold text-gray-700">Express Detail</p>
                                      <p className="text-[12px] text-gray-500 whitespace-nowrap">$129 · 1.5 h</p>
                                    </div>
                                    <p className="text-[11px] text-gray-400">Quick refresh inside and out</p>
                                  </div>
                                </div>
                                <p className="text-[11px] text-gray-400 mt-1.5">
                                  These services show the SUV price automatically — because that's the price option you assigned to this branch of the path.
                                </p>
                              </div>

                              <div className="flex justify-center py-0.5">
                                <ChevronDown className="h-4 w-4 text-gray-300" />
                              </div>

                              {/* Finish: time + details */}
                              <div className="rounded-xl bg-green-50 border border-green-200 p-3">
                                <p className="text-[12px] font-semibold text-green-800">
                                  Then add-ons (your upsells), an open time slot, their details — booked. The 3-hour duration blocks exactly 3 hours on your calendar.
                                </p>
                              </div>
                            </div>

                            {/* How the pieces fit together */}
                            <div className="rounded-2xl border border-blue-200 bg-blue-50/60 p-4 space-y-2.5 text-[13px] leading-relaxed text-gray-700">
                              <p className="text-sm font-bold text-gray-900">How you build this path</p>
                              <p>
                                <strong>Categories</strong> are the first question. Vehicle type works great for detailers:{' '}
                                <strong>Car / SUV / Truck</strong>.
                              </p>
                              <p>
                                <strong>Sub-categories</strong> are the second question, inside each category:{' '}
                                <strong>Interior / Exterior / Interior + Exterior</strong>.
                              </p>
                              <p>
                                <strong>Services get assigned to sub-categories</strong> — and when you assign one, you choose
                                which of its price options shows there. That's the trick that makes it all work:
                              </p>
                              <p className="pl-3 border-l-2 border-blue-300">
                                You create <strong>one</strong> "Full Detail" service with a price option per vehicle type —
                                Sedan $149 · 2h, SUV $189 · 3h, Truck $219 · 3.5h. Then the same service goes under{' '}
                                <strong>Car → Interior + Exterior</strong> with the Sedan price, under{' '}
                                <strong>SUV → Interior + Exterior</strong> with the SUV price, and so on. One service, priced
                                correctly in every branch.
                              </p>
                              <p>
                                Every price option also carries a <strong>duration</strong> — that's what the calendar uses to
                                offer only time slots where you actually have room.
                              </p>
                            </div>

                            <div className="rounded-xl border border-amber-200 bg-amber-50/70 px-4 py-3 text-[12px] text-amber-800">
                              <strong>So when you build services next:</strong> don't create "Full Detail — SUV" as its own
                              service. Create ONE "Full Detail" with Sedan / SUV / Truck price options — then it can slot into
                              every branch of your booking path with the right price and time.
                            </div>

                            <button
                              type="button"
                              onClick={async () => {
                                await markItemComplete('services-booking', 'booking-education');
                                advanceAfterItem(category, 'booking-education');
                              }}
                              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-blue-600 hover:bg-blue-700 transition-colors font-semibold text-white"
                            >
                              Got it — let's build my services
                              <ChevronRight className="h-4 w-4" />
                            </button>
                          </div>
                        </>
                      ) : openItemId === 'online-booking' ? (
                        /* Item: the online-booking yes/no question — the answer
                           shapes this category's remaining steps */
                        <>
                          <AssistantMessage
                            text={`Let's talk about how customers book with you. Do you want to set up online booking, so customers can book your services themselves from a booking page?`}
                          />
                          <div className="mt-5 ml-12 space-y-3">
                            <button
                              type="button"
                              onClick={() => handleBookingAnswer('yes')}
                              className="w-full flex items-center gap-3 rounded-xl border-2 border-gray-200 bg-white hover:border-blue-500 hover:bg-blue-50/50 px-4 py-3.5 text-left transition-colors"
                            >
                              <div className="flex-1">
                                <p className="text-[14px] font-semibold text-gray-900">Yes — set up online booking</p>
                                <p className="text-[12px] text-gray-500">Customers book themselves from your booking page.</p>
                              </div>
                              <ChevronRight className="h-4 w-4 text-gray-400 shrink-0" />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleBookingAnswer('no')}
                              className="w-full flex items-center gap-3 rounded-xl border-2 border-gray-200 bg-white hover:border-blue-500 hover:bg-blue-50/50 px-4 py-3.5 text-left transition-colors"
                            >
                              <div className="flex-1">
                                <p className="text-[14px] font-semibold text-gray-900">No — I'll book customers myself</p>
                                <p className="text-[12px] text-gray-500">You can always turn online booking on later.</p>
                              </div>
                              <ChevronRight className="h-4 w-4 text-gray-400 shrink-0" />
                            </button>
                          </div>
                        </>
                    ) : openItemId === 'booking-flow' ? (
                      /* Item: build the booking page click path — embeds the
                         existing Online Bookings flow editor */
                      <>
                        <AssistantMessage
                          text={`Last step: build your booking page's click path with the editor below.\n\n1. Create your categories (the customer's first pick — e.g. Car, SUV, Truck)\n2. Add sub-categories inside each (e.g. Interior, Exterior, Interior + Exterior)\n3. Assign your services to each sub-category — picking which price option shows on that branch\n\nYour services are ready to plug in, so this is mostly point-and-click. Tap your booking page link at the top of the editor anytime to open the live page and see exactly what customers will experience. (Page styling and more options live under Marketing later.)`}
                        />
                        <div className="mt-5 space-y-3">
                          <div className="rounded-2xl border border-gray-200 overflow-hidden">
                            <OnlineBookings embedded />
                          </div>

                          {flowError && (
                            <p className="ml-1 text-[13px] font-medium text-red-600">{flowError}</p>
                          )}

                          {/* AI audit results: flaws found in the click path */}
                          {flowAudit && (
                            <div className="rounded-2xl border border-amber-200 bg-amber-50/60 p-4 space-y-3">
                              <div className="flex items-start gap-2">
                                <Sparkles className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                                <div>
                                  <p className="text-[13px] font-bold text-gray-900">
                                    I reviewed your booking path and found{' '}
                                    {flowAudit.issues.length} thing{flowAudit.issues.length === 1 ? '' : 's'} worth a look
                                  </p>
                                  <p className="text-[12px] text-gray-500 mt-0.5">
                                    These won't stop you from finishing — but fixing them now means customers never hit them.
                                  </p>
                                </div>
                              </div>

                              <div className="space-y-2">
                                {flowAudit.issues.map((issue, i) => (
                                  <div
                                    key={i}
                                    className={`rounded-xl border px-3.5 py-2.5 ${
                                      issue.severity === 'error'
                                        ? 'border-red-200 bg-red-50/80'
                                        : 'border-amber-200 bg-white'
                                    }`}
                                  >
                                    <div className="flex items-center gap-1.5">
                                      <AlertTriangle
                                        className={`h-3.5 w-3.5 shrink-0 ${
                                          issue.severity === 'error' ? 'text-red-500' : 'text-amber-500'
                                        }`}
                                      />
                                      <p className="text-[13px] font-semibold text-gray-900">{issue.title}</p>
                                    </div>
                                    <p className="text-[12px] text-gray-600 mt-1 ml-5">{issue.detail}</p>
                                  </div>
                                ))}
                              </div>

                              {flowAudit.aiSkipped && (
                                <p className="text-[11px] text-gray-400">
                                  Structural checks only — the deeper AI review was skipped (needs AI tokens).
                                </p>
                              )}

                              <div className="flex gap-2">
                                <button
                                  type="button"
                                  onClick={() => setFlowAudit(null)}
                                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 transition-colors text-[13px] font-semibold text-white"
                                >
                                  Keep editing
                                </button>
                                <button
                                  type="button"
                                  onClick={() => void completeFlowStep()}
                                  className="flex-1 px-4 py-2.5 rounded-lg border border-gray-300 bg-white text-[13px] font-semibold text-gray-600 hover:text-gray-900 hover:border-gray-400 transition-colors"
                                >
                                  Continue anyway
                                </button>
                              </div>
                            </div>
                          )}

                          {!flowAudit && (
                            <button
                              type="button"
                              onClick={handleFlowContinue}
                              disabled={flowValidating}
                              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-blue-600 hover:bg-blue-700 transition-colors font-semibold text-white disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {flowValidating ? (
                                <>
                                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                                  Auditing your booking path…
                                </>
                              ) : (
                                <>
                                  <Check className="h-4 w-4" />
                                  Booking page done — finish
                                </>
                              )}
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => skipToNextItem(category)}
                            className="w-full px-4 py-2 text-sm font-medium text-gray-400 hover:text-gray-600 transition-colors"
                          >
                            Skip for now — I'll build my booking page later
                          </button>
                        </div>
                      </>
                    ) : openItemId === 'booking-link' ? (
                      /* Item: save the live booking page link — online booking is done */
                      <>
                        <AssistantMessage
                          text={`Your booking page is live! This is the link customers use to book you 24/7 — save it somewhere handy.\n\nPut it everywhere customers find you:\n• Your website's "Book Now" button\n• Instagram / Facebook bio\n• Your Google Business Profile\n• Text it to customers who call to book\n\nEvery booking made through it lands straight on your calendar with the right service, price, and time blocked.`}
                        />
                        <div className="mt-5 ml-12 space-y-3">
                          {bookingLinkLoading ? (
                            <div className="flex items-center justify-center py-4">
                              <div className="animate-spin rounded-full h-5 w-5 border-2 border-blue-500 border-t-transparent" />
                            </div>
                          ) : bookingLinkUrl ? (
                            <div className="rounded-2xl border border-blue-200 bg-blue-50/60 p-4 space-y-3">
                              <p className="text-[11px] font-bold uppercase tracking-wider text-gray-400">
                                Your online booking link
                              </p>
                              <a
                                href={bookingLinkUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="block rounded-xl border border-blue-200 bg-white px-4 py-3 text-[13px] font-semibold text-blue-600 break-all hover:text-blue-700 hover:border-blue-400 transition-colors"
                                title="Open your booking page in a new tab"
                              >
                                {bookingLinkUrl}
                              </a>
                              <div className="flex gap-2">
                                <button
                                  type="button"
                                  onClick={() => {
                                    navigator.clipboard.writeText(bookingLinkUrl);
                                    setBookingLinkCopied(true);
                                    setTimeout(() => setBookingLinkCopied(false), 2500);
                                  }}
                                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 transition-colors text-[13px] font-semibold text-white"
                                >
                                  {bookingLinkCopied ? (
                                    <>
                                      <Check className="h-4 w-4" />
                                      Copied!
                                    </>
                                  ) : (
                                    'Copy link'
                                  )}
                                </button>
                                <a
                                  href={bookingLinkUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="flex-1 flex items-center justify-center px-4 py-2.5 rounded-lg border border-blue-200 bg-white text-[13px] font-semibold text-blue-700 hover:border-blue-400 transition-colors"
                                >
                                  Open booking page
                                </a>
                              </div>
                            </div>
                          ) : (
                            <p className="text-[13px] font-medium text-amber-600">
                              Couldn't load your booking link — you can always find it under Marketing → Online Bookings.
                            </p>
                          )}

                          <button
                            type="button"
                            onClick={async () => {
                              await markItemComplete('services-booking', 'booking-link');
                              advanceAfterItem(category, 'booking-link');
                            }}
                            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-blue-600 hover:bg-blue-700 transition-colors font-semibold text-white"
                          >
                            <Check className="h-4 w-4" />
                            Online booking complete
                          </button>
                        </div>
                      </>
                    ) : openItemId === 'setup-services' || openItemId === 'booking-services' ? (
                      /* Item: create services (same tables as the Services page).
                         'booking-services' is the online-booking path — same
                         builder, but the guidance is about feeding the booking
                         click path. */
                      <>
                        <AssistantMessage
                          text={
                            openItemId === 'booking-services'
                              ? `Time to build the services that power your booking path.\n\nRemember the lesson: on your booking page, the customer's picks (like SUV → Interior + Exterior) lead them to a service — shown with the price option that matches their path. So build for that:\n\n1. Create a category for each kind of work — e.g. Interior, Exterior, Interior + Exterior. Keep it to the same groups you want as sub-categories on your booking page.\n\n2. Create ONE service per package — "Full Detail", "Express Detail" — not one per vehicle type.\n\n3. Inside each service, add a price option per vehicle type — Sedan $149 · 2h, SUV $189 · 3h, Truck $219 · 3.5h. The duration matters: it's how much calendar time an online booking blocks.\n\n4. Write the name & description for customers — that's exactly what they'll read when choosing.\n\nAdd add-ons for upsells too (like "Pet Hair Removal +$40"). Once your services exist, they plug straight into your booking page's click path.`
                              : `Now your services — this is what customers book and what shows on invoices.\n\nYou don't have to build everything now: services can easily be created as you go. But if you'd like, we can configure your menu right here. Services live inside categories, so start with a category, then add services to it.\n\nFor example, a detailing business might create 3 categories:\n• Inside Detailing\n• Outside Detailing\n• Inside & Outside Detailing\n\n…and then assign the corresponding services to each one.`
                          }
                        />
                        {svcLoading ? (
                          <div className="flex items-center justify-center py-10">
                            <div className="animate-spin rounded-full h-7 w-7 border-2 border-blue-500 border-t-transparent" />
                          </div>
                        ) : (
                          <div className="mt-5 ml-12 space-y-4">
                            {/* Categories + their services (live from the database) */}
                            {svcCategories.map((cat) => {
                              const catServices = svcServices.filter((s) => s.category_id === cat.id);
                              return (
                                <div key={cat.id} className="rounded-xl border border-gray-200 bg-white overflow-hidden">
                                  <div className="px-4 py-3 bg-gray-50 border-b border-gray-100 flex items-center justify-between">
                                    <p className="text-[13px] font-bold text-gray-900">{cat.name}</p>
                                    <span className="text-[11px] text-gray-400">
                                      {catServices.length} service{catServices.length === 1 ? '' : 's'}
                                    </span>
                                  </div>
                                  <div className="p-3 space-y-2">
                                    {catServices.length === 0 ? (
                                      <p className="text-[12px] text-gray-400 text-center py-1">No services yet</p>
                                    ) : (
                                      catServices.map((svc) => {
                                        const firstPrice = svc.price_options?.[0];
                                        return (
                                          <div key={svc.id} className="flex items-center gap-3 rounded-lg border border-gray-100 px-3 py-2">
                                            <span
                                              className="h-2.5 w-2.5 rounded-full shrink-0"
                                              style={{ backgroundColor: svc.color || '#3b82f6' }}
                                            />
                                            <p className="flex-1 text-[13px] font-medium text-gray-900 truncate">{svc.name}</p>
                                            {firstPrice && (
                                              <span className="text-[12px] text-gray-500 whitespace-nowrap">
                                                ${Number(firstPrice.price).toFixed(2)} · {firstPrice.duration_minutes}min
                                              </span>
                                            )}
                                          </div>
                                        );
                                      })
                                    )}
                                    {/* Create a service directly in THIS category */}
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setServiceModalCategoryId(cat.id);
                                        setServiceModalOpen(true);
                                      }}
                                      className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border-2 border-dashed border-blue-200 bg-blue-50/50 hover:border-blue-400 transition-colors text-[12px] font-semibold text-blue-700"
                                    >
                                      <Plus className="h-3.5 w-3.5" />
                                      Create service in {cat.name}
                                    </button>
                                  </div>
                                </div>
                              );
                            })}

                            {/* New category */}
                            <div className="flex gap-2">
                              <input
                                type="text"
                                placeholder={svcCategories.length === 0 ? 'First category (e.g. Detailing Packages)' : 'New category name'}
                                value={newCategoryName}
                                onChange={(e) => setNewCategoryName(e.target.value)}
                                disabled={savingCategory}
                                className="flex-1 min-w-0 rounded-lg border border-gray-200 px-3.5 py-2.5 text-[14px] focus:outline-none focus:ring-2 focus:ring-blue-500/30 disabled:opacity-60"
                              />
                              <button
                                type="button"
                                onClick={handleAddServiceCategory}
                                disabled={savingCategory}
                                className="shrink-0 inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg bg-gray-900 hover:bg-gray-800 text-white text-[13px] font-semibold disabled:opacity-60"
                              >
                                <Plus className="h-3.5 w-3.5" />
                                Category
                              </button>
                            </div>

                            {svcError && (
                              <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-[13px] text-red-700">
                                {svcError}
                              </div>
                            )}

                            <button
                              type="button"
                              onClick={async () => {
                                const itemId = openItemId === 'booking-services' ? 'booking-services' : 'setup-services';
                                await markItemComplete('services-booking', itemId);
                                advanceAfterItem(category, itemId);
                              }}
                              disabled={svcServices.length === 0}
                              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-blue-600 hover:bg-blue-700 transition-colors font-semibold text-white disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <Check className="h-4 w-4" />
                              {svcServices.length === 0 ? 'Create a service to continue' : 'Services done — continue'}
                            </button>
                            <button
                              type="button"
                              onClick={() => skipToNextItem(category)}
                              className="w-full px-4 py-2 text-sm font-medium text-gray-400 hover:text-gray-600 transition-colors"
                            >
                              Skip for now — I'll create services as I go
                            </button>
                          </div>
                        )}
                        {/* Full create-service editor (same one the Services page uses) */}
                        <CreateServiceModal
                          isOpen={serviceModalOpen}
                          onClose={() => {
                            setServiceModalOpen(false);
                            setServiceModalCategoryId(null);
                          }}
                          onSuccess={() => {
                            setServiceModalOpen(false);
                            setServiceModalCategoryId(null);
                            void loadWizardServices();
                            void logResponse(2, 'service:created', {});
                          }}
                          businessId={selectedBusiness?.id}
                          categories={svcCategories}
                          onCategoryCreated={() => void loadWizardServices()}
                          initialCategoryId={serviceModalCategoryId ?? undefined}
                        />
                      </>
                    ) : openItemId === 'connect-stripe' && stripeFullyConnected ? (
                      /* Item: Stripe — already connected */
                      <>
                        <AssistantMessage
                          text={`Great news — your Stripe account is connected and ready to take card payments. Customers can pay invoices online, and you can charge cards right from a booking.`}
                        />
                        <div className="mt-5 ml-12 space-y-3">
                          <div className="flex items-center gap-3 rounded-xl border border-green-200 bg-green-50/60 px-4 py-3">
                            <CreditCard className="h-4 w-4 text-green-600 shrink-0" />
                            <p className="flex-1 text-[13px] font-semibold text-gray-900">Stripe connected — card payments enabled</p>
                            <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-green-100 text-green-700 shrink-0">
                              Connected
                            </span>
                          </div>
                          <button
                            type="button"
                            onClick={async () => {
                              await markItemComplete('payments-invoicing', 'connect-stripe');
                              advanceAfterItem(category, 'connect-stripe');
                            }}
                            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-blue-600 hover:bg-blue-700 transition-colors font-semibold text-white"
                          >
                            <Check className="h-4 w-4" />
                            Stripe connected — continue
                          </button>
                        </div>
                      </>
                    ) : openItemId === 'connect-stripe' && stripePhase === 'card-question' ? (
                      /* Item: Stripe — do they want card payments at all? */
                      <>
                        <AssistantMessage
                          text={`Let's talk about getting paid. Would you like to process actual credit card payments through Nexus? That unlocks online invoice payments, payment links you can text to customers, and card-on-file charges.`}
                        />
                        <div className="mt-5 ml-12 space-y-3">
                          <button
                            type="button"
                            onClick={() => {
                              setStripePhase('stripe-question');
                              void logResponse(2, 'payments:card-question:yes', {});
                            }}
                            className="w-full flex items-center gap-3 rounded-xl border-2 border-gray-200 bg-white hover:border-blue-500 hover:bg-blue-50/50 px-4 py-3.5 text-left transition-colors"
                          >
                            <div className="flex-1">
                              <p className="text-[14px] font-semibold text-gray-900">Yes — I want to accept card payments</p>
                              <p className="text-[12px] text-gray-500">
                                We'll connect Stripe next — it takes a few minutes.
                              </p>
                            </div>
                            <ChevronRight className="h-4 w-4 text-gray-400 shrink-0" />
                          </button>
                          <button
                            type="button"
                            onClick={async () => {
                              void logResponse(2, 'payments:card-question:no', {});
                              await markItemComplete('payments-invoicing', 'connect-stripe');
                              advanceAfterItem(category, 'connect-stripe');
                            }}
                            className="w-full flex items-center gap-3 rounded-xl border-2 border-gray-200 bg-white hover:border-blue-500 hover:bg-blue-50/50 px-4 py-3.5 text-left transition-colors"
                          >
                            <div className="flex-1">
                              <p className="text-[14px] font-semibold text-gray-900">No — cash, cheque, and e-transfer only</p>
                              <p className="text-[12px] text-gray-500">
                                You can always connect Stripe later from Nexus Connect → Integrations.
                              </p>
                            </div>
                            <ChevronRight className="h-4 w-4 text-gray-400 shrink-0" />
                          </button>
                        </div>
                      </>
                    ) : openItemId === 'connect-stripe' && stripePhase === 'stripe-question' ? (
                      /* Item: Stripe — do they already have an account? */
                      <>
                        <AssistantMessage
                          text={`Nexus payments are powered by Stripe — the same payment processor trusted by millions of businesses worldwide. Do you have a Stripe account?`}
                        />
                        <div className="mt-5 ml-12 space-y-3">
                          <button
                            type="button"
                            onClick={() => {
                              setStripeHasAccount(true);
                              setStripePhase('connect');
                              void logResponse(2, 'payments:stripe-account:yes', {});
                            }}
                            className="w-full flex items-center gap-3 rounded-xl border-2 border-gray-200 bg-white hover:border-blue-500 hover:bg-blue-50/50 px-4 py-3.5 text-left transition-colors"
                          >
                            <div className="flex-1">
                              <p className="text-[14px] font-semibold text-gray-900">Yes — I have a Stripe account</p>
                              <p className="text-[12px] text-gray-500">Perfect — we'll link it to Nexus.</p>
                            </div>
                            <ChevronRight className="h-4 w-4 text-gray-400 shrink-0" />
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setStripeHasAccount(false);
                              setStripePhase('connect');
                              void logResponse(2, 'payments:stripe-account:no', {});
                            }}
                            className="w-full flex items-center gap-3 rounded-xl border-2 border-gray-200 bg-white hover:border-blue-500 hover:bg-blue-50/50 px-4 py-3.5 text-left transition-colors"
                          >
                            <div className="flex-1">
                              <p className="text-[14px] font-semibold text-gray-900">No — I'll need to create one</p>
                              <p className="text-[12px] text-gray-500">No problem — it's free and takes about 5 minutes.</p>
                            </div>
                            <ChevronRight className="h-4 w-4 text-gray-400 shrink-0" />
                          </button>
                        </div>
                      </>
                    ) : openItemId === 'connect-stripe' ? (
                      /* Item: Stripe — connect (instructions differ by account answer) */
                      <>
                        <AssistantMessage
                          text={
                            stripeAccountId && !stripeFullyConnected
                              ? `Looks like your Stripe setup isn't finished yet. Tap "Resume Stripe setup" to pick up where you left off — Stripe saves your progress, so it only takes a minute. When you're done you'll land right back here.`
                              : stripeHasAccount === false
                                ? `Here's how to get set up (about 5 minutes total):\n\n1. Create your free Stripe account at https://dashboard.stripe.com/register — use your business email, and have your business details and bank account info handy (that's where payouts go).\n2. Verify your email when Stripe asks.\n3. Come back here and tap "Connect Stripe" below — sign in with your new account and follow Stripe's steps to activate payments.\n\nTip: you can also just tap "Connect Stripe" now — Stripe lets you create the account as part of the connection flow. Either way, you'll land right back here when it's done.`
                                : `Perfect. Tap "Connect Stripe" below and you'll be taken to Stripe to link your account:\n\n1. Sign in with your existing Stripe login.\n2. Confirm your business details and the bank account for payouts.\n3. Stripe brings you right back here when it's done.\n\nThe whole thing usually takes under 2 minutes.`
                          }
                        />
                        <div className="mt-5 ml-12 space-y-3">
                          {stripeHasAccount === false && !stripeAccountId && (
                            <a
                              href="https://dashboard.stripe.com/register"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 border-gray-200 bg-white hover:border-blue-500 hover:bg-blue-50/50 transition-colors font-semibold text-gray-800"
                            >
                              Create a Stripe account
                              <ChevronRight className="h-4 w-4 text-gray-400" />
                            </a>
                          )}
                          <button
                            type="button"
                            onClick={handleConnectStripe}
                            disabled={stripeConnecting || stripeStatusChecking}
                            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-blue-600 hover:bg-blue-700 transition-colors font-semibold text-white disabled:opacity-50"
                          >
                            {stripeConnecting ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white/40 border-t-white" />
                            ) : (
                              <CreditCard className="h-4 w-4" />
                            )}
                            {stripeConnecting
                              ? 'Opening Stripe…'
                              : stripeAccountId && !stripeFullyConnected
                                ? 'Resume Stripe setup'
                                : 'Connect Stripe'}
                          </button>
                          {stripeAccountId && !stripeFullyConnected && (
                            <button
                              type="button"
                              onClick={() => void checkStripeStatus()}
                              disabled={stripeStatusChecking}
                              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 border-gray-200 bg-white hover:border-blue-500 transition-colors font-semibold text-gray-700 disabled:opacity-50"
                            >
                              {stripeStatusChecking ? (
                                <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-300 border-t-gray-600" />
                              ) : (
                                <Check className="h-4 w-4" />
                              )}
                              I finished on Stripe — check status
                            </button>
                          )}
                          {stripeError && (
                            <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-[13px] text-red-700">
                              {stripeError}
                            </div>
                          )}
                          <button
                            type="button"
                            onClick={() => skipToNextItem(category)}
                            className="w-full px-4 py-2 text-sm font-medium text-gray-400 hover:text-gray-600 transition-colors"
                          >
                            Skip for now — I'll connect payments later
                          </button>
                        </div>
                      </>
                    ) : openItemId === 'connect-gmail' ? (
                      /* Item: connect Gmail (same OAuth flow + table as settings) */
                      <>
                        <AssistantMessage
                          text={`First up: email. Connect your Gmail and Nexus will send emails from your real address — plus every customer email conversation syncs into your Nexus inbox automatically.`}
                        />
                        <div className="mt-5 ml-12 space-y-3">
                          {gmailLoading ? (
                            <div className="flex items-center justify-center py-4">
                              <div className="animate-spin rounded-full h-5 w-5 border-2 border-blue-500 border-t-transparent" />
                            </div>
                          ) : gmailConnections.length > 0 ? (
                            <div className="space-y-2">
                              {gmailConnections.map((conn) => (
                                <div key={conn.id} className="flex items-center gap-3 rounded-xl border border-green-200 bg-green-50/60 px-4 py-3">
                                  <Mail className="h-4 w-4 text-green-600 shrink-0" />
                                  <p className="flex-1 text-[13px] font-semibold text-gray-900 truncate">{conn.email}</p>
                                  <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-green-100 text-green-700 shrink-0">
                                    Connected
                                  </span>
                                </div>
                              ))}
                            </div>
                          ) : null}

                          {/* Only one Gmail account is supported for now. */}
                          {gmailConnections.length === 0 && (
                            <button
                              type="button"
                              onClick={handleConnectGmail}
                              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 border-blue-200 bg-blue-50 hover:border-blue-500 transition-colors font-semibold text-blue-800"
                            >
                              <Mail className="h-4 w-4" />
                              Connect Gmail
                            </button>
                          )}

                          <button
                            type="button"
                            onClick={async () => {
                              await markItemComplete('messaging', 'connect-gmail');
                              advanceAfterItem(messagingCategory, 'connect-gmail');
                            }}
                            disabled={gmailConnections.length === 0}
                            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-blue-600 hover:bg-blue-700 transition-colors font-semibold text-white disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <Check className="h-4 w-4" />
                            {gmailConnections.length === 0 ? 'Connect Gmail to continue' : 'Gmail connected — continue'}
                          </button>
                          <button
                            type="button"
                            onClick={() => skipToNextItem(category)}
                            className="w-full px-4 py-2 text-sm font-medium text-gray-400 hover:text-gray-600 transition-colors"
                          >
                            Skip for now — I'll connect email later
                          </button>
                        </div>
                      </>
                    ) : openItemId === 'connect-twilio' ? (
                      /* Item: connect texting via manual Twilio credentials (the
                         automatic Connect flow is paused — revisit soon). */
                      <>
                        <AssistantMessage
                          text={`Next: texting. Connect Twilio and your business gets its own phone number for SMS and calls — customers can text you, and Nexus sends booking confirmations, reminders, and campaigns by text automatically.\n\nYou can even take calls on your business number with automatic call recording and transcription — so you never forget what was said to a customer, and every call lives on their profile.\n\nTwilio is an affordable pay-as-you-go service billed directly to you — a number costs just a few dollars a month, and you only pay for the texts and calls you actually use. Here's how to set it up (takes about 5 minutes):\n\n1. Go to twilio.com and create a free account (then upgrade it with a payment method so you can get a real number).\n2. In the Twilio console, buy a phone number: Phone Numbers → Manage → Buy a number (pick one with SMS + Voice).\n3. On the Twilio console home page, copy your Account SID and Auth Token.\n4. Paste all three below and I'll wire everything up automatically.`}
                        />
                        <div className="mt-5 ml-12 space-y-3">
                          {twilioLoading ? (
                            <div className="flex items-center justify-center py-4">
                              <div className="animate-spin rounded-full h-5 w-5 border-2 border-blue-500 border-t-transparent" />
                            </div>
                          ) : twilioStatus?.enabled && twilioStatus.phoneNumber ? (
                            /* Fully connected: number active */
                            <div className="flex items-center gap-3 rounded-xl border border-green-200 bg-green-50/60 px-4 py-3">
                              <MessageSquare className="h-4 w-4 text-green-600 shrink-0" />
                              <p className="flex-1 text-[13px] font-semibold text-gray-900 truncate font-mono">
                                {twilioStatus.phoneNumber}
                              </p>
                              <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-green-100 text-green-700 shrink-0">
                                Connected
                              </span>
                            </div>
                          ) : twilioAwaitingNumber ? (
                            /* Authorized — pick the texting number to finish */
                            <div className="space-y-3">
                              <div className="flex items-center gap-2 rounded-xl border border-green-200 bg-green-50/60 px-4 py-3">
                                <Check className="h-4 w-4 text-green-600 shrink-0" />
                                <p className="text-[13px] font-semibold text-gray-900">
                                  Twilio account connected — now choose your texting number
                                </p>
                              </div>

                              {twilioExistingNumbers.length > 0 && (
                                <div className="space-y-2">
                                  {twilioExistingNumbers.map((n) => (
                                    <div
                                      key={n.sid}
                                      className="flex items-center justify-between gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3"
                                    >
                                      <div className="min-w-0">
                                        <p className="text-[13px] font-semibold text-gray-900 font-mono">{n.phoneNumber}</p>
                                        <p className="text-xs text-gray-400 truncate">
                                          {n.friendlyName || 'Your number'}
                                          {!n.smsCapable ? ' · no SMS' : ''}
                                        </p>
                                      </div>
                                      <button
                                        type="button"
                                        onClick={() => handleTwilioPickNumber({ phoneNumber: n.phoneNumber, sid: n.sid })}
                                        disabled={!!twilioPurchasing}
                                        className="shrink-0 px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white text-xs font-semibold transition-colors"
                                      >
                                        {twilioPurchasing === n.phoneNumber ? 'Connecting…' : 'Use this number'}
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              )}

                              <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 space-y-3">
                                <p className="text-[13px] font-semibold text-gray-700">
                                  {twilioExistingNumbers.length > 0 ? 'Or get a new number' : 'Get a phone number'}
                                </p>
                                <div className="flex items-center gap-2">
                                  <input
                                    type="text"
                                    inputMode="numeric"
                                    value={twilioAreaCode}
                                    onChange={(e) => setTwilioAreaCode(e.target.value.replace(/\D/g, '').slice(0, 3))}
                                    placeholder="Area code (e.g. 604)"
                                    className="flex-1 bg-white border border-gray-300 rounded-lg px-3 py-2 text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  />
                                  <button
                                    type="button"
                                    onClick={handleTwilioSearchNumbers}
                                    disabled={twilioSearching}
                                    className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white text-sm font-semibold transition-colors"
                                  >
                                    {twilioSearching ? 'Searching…' : 'Search'}
                                  </button>
                                </div>
                                {twilioAvailableNumbers.length > 0 && (
                                  <div className="space-y-2 max-h-56 overflow-y-auto">
                                    {twilioAvailableNumbers.map((n) => (
                                      <div
                                        key={n.phoneNumber}
                                        className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white px-3 py-2.5"
                                      >
                                        <div className="min-w-0">
                                          <p className="text-[13px] font-semibold text-gray-900 font-mono">{n.phoneNumber}</p>
                                          <p className="text-xs text-gray-400 truncate">
                                            {[n.locality, n.region].filter(Boolean).join(', ') || 'Local number'}
                                          </p>
                                        </div>
                                        <button
                                          type="button"
                                          onClick={() => handleTwilioPickNumber({ phoneNumber: n.phoneNumber })}
                                          disabled={!!twilioPurchasing}
                                          className="shrink-0 px-3 py-1.5 rounded-lg bg-green-600 hover:bg-green-700 disabled:bg-gray-300 text-white text-xs font-semibold transition-colors"
                                        >
                                          {twilioPurchasing === n.phoneNumber ? 'Setting up…' : 'Use this number'}
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          ) : (
                            /* Not connected yet: manual credential form */
                            <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 space-y-3">
                              <p className="text-[13px] font-semibold text-gray-700">Connect your Twilio account</p>
                              <div>
                                <label className="block text-xs font-semibold text-gray-500 mb-1">Account SID</label>
                                <input
                                  type="text"
                                  value={twilioManualSid}
                                  onChange={(e) => setTwilioManualSid(e.target.value)}
                                  placeholder="AC..."
                                  autoComplete="off"
                                  className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-gray-900 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-semibold text-gray-500 mb-1">Auth Token</label>
                                <input
                                  type="password"
                                  value={twilioManualToken}
                                  onChange={(e) => setTwilioManualToken(e.target.value)}
                                  placeholder="Your Twilio auth token"
                                  autoComplete="off"
                                  className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-gray-900 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-semibold text-gray-500 mb-1">Your Twilio phone number</label>
                                <input
                                  type="tel"
                                  value={twilioManualPhone}
                                  onChange={(e) => setTwilioManualPhone(e.target.value)}
                                  placeholder="+1 604 555 1234"
                                  autoComplete="off"
                                  className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-gray-900 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                              </div>
                              <button
                                type="button"
                                onClick={async () => {
                                  if (!selectedBusiness?.id) return;
                                  setTwilioError(null);
                                  setTwilioActivating(true);
                                  try {
                                    await activateManualTwilio({
                                      businessId: selectedBusiness.id,
                                      accountSid: twilioManualSid,
                                      authToken: twilioManualToken,
                                      phoneNumber: twilioManualPhone,
                                    });
                                    toast.success('Texting connected!');
                                    await loadTwilioStatus();
                                  } catch (e) {
                                    setTwilioError(e instanceof Error ? e.message : 'Could not connect Twilio');
                                  } finally {
                                    setTwilioActivating(false);
                                  }
                                }}
                                disabled={twilioActivating || !twilioManualSid.trim() || !twilioManualToken.trim() || !twilioManualPhone.trim()}
                                className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-blue-600 hover:bg-blue-700 transition-colors font-semibold text-white disabled:opacity-50"
                              >
                                <MessageSquare className="h-4 w-4" />
                                {twilioActivating ? 'Connecting & configuring…' : 'Connect texting'}
                              </button>
                            </div>
                          )}

                          {twilioError && (
                            <p className="text-[13px] font-medium text-red-600">{twilioError}</p>
                          )}

                          <button
                            type="button"
                            onClick={async () => {
                              await markItemComplete('messaging', 'connect-twilio');
                              advanceAfterItem(messagingCategory, 'connect-twilio');
                            }}
                            disabled={!(twilioStatus?.enabled && twilioStatus.phoneNumber)}
                            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-blue-600 hover:bg-blue-700 transition-colors font-semibold text-white disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <Check className="h-4 w-4" />
                            {twilioStatus?.enabled && twilioStatus.phoneNumber
                              ? 'Texting connected — continue'
                              : 'Connect texting to continue'}
                          </button>
                          <button
                            type="button"
                            onClick={() => skipToNextItem(category)}
                            className="w-full px-4 py-2 text-sm font-medium text-gray-400 hover:text-gray-600 transition-colors"
                          >
                            Skip for now — I'll connect texting later
                          </button>
                        </div>
                      </>
                    ) : openItemId === 'booking-types' ? (
                      /* Item: confirm booking types (mobile / location — same
                         calendar app settings the signup wizard wrote) */
                      <>
                        <AssistantMessage
                          text={`Time to set up your calendar. First, let's confirm the types of bookings you take — you answered this at signup, so here's what you picked. Change it or turn on both if you do both.\n\nThis controls which booking tools show up across Nexus, so everything matches how you actually work.`}
                        />
                        <div className="mt-5 ml-12 space-y-3">
                          {bookingTypesLoading || !bookingTypes ? (
                            <div className="flex items-center justify-center py-4">
                              <div className="animate-spin rounded-full h-5 w-5 border-2 border-blue-500 border-t-transparent" />
                            </div>
                          ) : (
                            <div className="grid grid-cols-2 gap-3">
                              <button
                                type="button"
                                onClick={() => setBookingTypes((prev) => prev && { ...prev, mobile: !prev.mobile })}
                                className={`relative text-left p-4 rounded-xl border-2 transition-colors ${
                                  bookingTypes.mobile
                                    ? 'border-blue-500 bg-blue-50'
                                    : 'border-gray-200 bg-gray-50 hover:border-gray-300'
                                }`}
                              >
                                {bookingTypes.mobile && (
                                  <span className="absolute top-2.5 right-2.5 flex h-5 w-5 items-center justify-center rounded-full bg-blue-600">
                                    <Check className="h-3 w-3 text-white" />
                                  </span>
                                )}
                                <Car className={`h-5 w-5 mb-2 ${bookingTypes.mobile ? 'text-blue-600' : 'text-gray-400'}`} />
                                <p className="text-sm font-semibold text-gray-900">Mobile</p>
                                <p className="text-xs text-gray-500 mt-1">You travel to the customer — jobs happen at their address</p>
                              </button>
                              <button
                                type="button"
                                onClick={() => setBookingTypes((prev) => prev && { ...prev, location: !prev.location })}
                                className={`relative text-left p-4 rounded-xl border-2 transition-colors ${
                                  bookingTypes.location
                                    ? 'border-blue-500 bg-blue-50'
                                    : 'border-gray-200 bg-gray-50 hover:border-gray-300'
                                }`}
                              >
                                {bookingTypes.location && (
                                  <span className="absolute top-2.5 right-2.5 flex h-5 w-5 items-center justify-center rounded-full bg-blue-600">
                                    <Check className="h-3 w-3 text-white" />
                                  </span>
                                )}
                                <MapPin className={`h-5 w-5 mb-2 ${bookingTypes.location ? 'text-blue-600' : 'text-gray-400'}`} />
                                <p className="text-sm font-semibold text-gray-900">Location</p>
                                <p className="text-xs text-gray-500 mt-1">Customers come to you — jobs happen at your place of business</p>
                              </button>
                            </div>
                          )}

                          {bookingTypes && !bookingTypes.mobile && !bookingTypes.location && (
                            <p className="text-[13px] font-medium text-amber-600">
                              Select at least one booking type to continue.
                            </p>
                          )}
                          {bookingTypesError && (
                            <p className="text-[13px] font-medium text-red-600">{bookingTypesError}</p>
                          )}

                          <button
                            type="button"
                            onClick={async () => {
                              const saved = await saveBookingTypes();
                              if (!saved) return;
                              await markItemComplete('calendar', 'booking-types');
                              advanceAfterItem(category, 'booking-types');
                            }}
                            disabled={
                              bookingTypesLoading ||
                              bookingTypesSaving ||
                              !bookingTypes ||
                              (!bookingTypes.mobile && !bookingTypes.location)
                            }
                            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-blue-600 hover:bg-blue-700 transition-colors font-semibold text-white disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <Check className="h-4 w-4" />
                            {bookingTypesSaving ? 'Saving…' : 'Save & continue'}
                          </button>
                          <button
                            type="button"
                            onClick={() => skipToNextItem(category)}
                            className="w-full px-4 py-2 text-sm font-medium text-gray-400 hover:text-gray-600 transition-colors"
                          >
                            Skip for now
                          </button>
                        </div>
                      </>
                    ) : openItemId === 'enable-tasks' ? (
                      /* Item: enable events (show_tasks_in_fab calendar setting) */
                      <>
                        <AssistantMessage
                          text={`Next up: events. Events are great for putting non-booking items on your calendar — supply runs, meetings, prep work — or blocking out days off so nothing gets booked over them.\n\nWant events on your calendar? You can always change this later in your Calendar settings.`}
                        />
                        <div className="mt-5 ml-12 space-y-3">
                          {tasksLoading || tasksEnabled === null ? (
                            <div className="flex items-center justify-center py-4">
                              <div className="animate-spin rounded-full h-5 w-5 border-2 border-blue-500 border-t-transparent" />
                            </div>
                          ) : (
                            <div className="grid grid-cols-2 gap-3">
                              <button
                                type="button"
                                disabled={tasksSaving}
                                onClick={async () => {
                                  const saved = await saveTasksEnabled(true);
                                  if (!saved) return;
                                  await markItemComplete('calendar', 'enable-tasks');
                                  advanceAfterItem(category, 'enable-tasks');
                                }}
                                className={`relative text-left p-4 rounded-xl border-2 transition-colors disabled:opacity-60 ${
                                  tasksEnabled
                                    ? 'border-blue-500 bg-blue-50'
                                    : 'border-gray-200 bg-gray-50 hover:border-blue-300'
                                }`}
                              >
                                {tasksEnabled && (
                                  <span className="absolute top-2.5 right-2.5 flex h-5 w-5 items-center justify-center rounded-full bg-blue-600">
                                    <Check className="h-3 w-3 text-white" />
                                  </span>
                                )}
                                <p className="text-sm font-semibold text-gray-900">Yes, enable events</p>
                                <p className="text-xs text-gray-500 mt-1">Add non-booking items and block off time on your calendar</p>
                              </button>
                              <button
                                type="button"
                                disabled={tasksSaving}
                                onClick={async () => {
                                  const saved = await saveTasksEnabled(false);
                                  if (!saved) return;
                                  await markItemComplete('calendar', 'enable-tasks');
                                  advanceAfterItem(category, 'enable-tasks');
                                }}
                                className={`relative text-left p-4 rounded-xl border-2 transition-colors disabled:opacity-60 ${
                                  tasksEnabled === false
                                    ? 'border-blue-500 bg-blue-50'
                                    : 'border-gray-200 bg-gray-50 hover:border-gray-300'
                                }`}
                              >
                                {tasksEnabled === false && (
                                  <span className="absolute top-2.5 right-2.5 flex h-5 w-5 items-center justify-center rounded-full bg-blue-600">
                                    <Check className="h-3 w-3 text-white" />
                                  </span>
                                )}
                                <p className="text-sm font-semibold text-gray-900">No, bookings only</p>
                                <p className="text-xs text-gray-500 mt-1">Keep the calendar just for customer bookings</p>
                              </button>
                            </div>
                          )}

                          {tasksSaving && (
                            <p className="text-[13px] font-medium text-gray-400">Saving…</p>
                          )}
                          {tasksError && (
                            <p className="text-[13px] font-medium text-red-600">{tasksError}</p>
                          )}

                          <button
                            type="button"
                            onClick={() => skipToNextItem(category)}
                            className="w-full px-4 py-2 text-sm font-medium text-gray-400 hover:text-gray-600 transition-colors"
                          >
                            Skip for now
                          </button>
                        </div>
                      </>
                    ) : openItemId === 'field-quotes' ? (
                      /* Item: mobile estimates / field quotes (show_field_quotes_in_fab) */
                      <>
                        <AssistantMessage
                          text={`One more calendar question: do you perform mobile estimates?\n\nIf you drive out to look at jobs before quoting them, you can schedule those visits as field quotes — they go right on your calendar, so every scheduled estimate is tracked alongside your bookings and nothing slips through the cracks.`}
                        />
                        <div className="mt-5 ml-12 space-y-3">
                          {fieldQuotesLoading || fieldQuotesEnabled === null ? (
                            <div className="flex items-center justify-center py-4">
                              <div className="animate-spin rounded-full h-5 w-5 border-2 border-blue-500 border-t-transparent" />
                            </div>
                          ) : (
                            <div className="grid grid-cols-2 gap-3">
                              <button
                                type="button"
                                disabled={fieldQuotesSaving}
                                onClick={async () => {
                                  const saved = await saveFieldQuotesEnabled(true);
                                  if (!saved) return;
                                  await markItemComplete('calendar', 'field-quotes');
                                  advanceAfterItem(category, 'field-quotes');
                                }}
                                className={`relative text-left p-4 rounded-xl border-2 transition-colors disabled:opacity-60 ${
                                  fieldQuotesEnabled
                                    ? 'border-blue-500 bg-blue-50'
                                    : 'border-gray-200 bg-gray-50 hover:border-blue-300'
                                }`}
                              >
                                {fieldQuotesEnabled && (
                                  <span className="absolute top-2.5 right-2.5 flex h-5 w-5 items-center justify-center rounded-full bg-blue-600">
                                    <Check className="h-3 w-3 text-white" />
                                  </span>
                                )}
                                <p className="text-sm font-semibold text-gray-900">Yes, I do mobile estimates</p>
                                <p className="text-xs text-gray-500 mt-1">Schedule field quotes on the calendar to track every estimate visit</p>
                              </button>
                              <button
                                type="button"
                                disabled={fieldQuotesSaving}
                                onClick={async () => {
                                  const saved = await saveFieldQuotesEnabled(false);
                                  if (!saved) return;
                                  await markItemComplete('calendar', 'field-quotes');
                                  advanceAfterItem(category, 'field-quotes');
                                }}
                                className={`relative text-left p-4 rounded-xl border-2 transition-colors disabled:opacity-60 ${
                                  fieldQuotesEnabled === false
                                    ? 'border-blue-500 bg-blue-50'
                                    : 'border-gray-200 bg-gray-50 hover:border-gray-300'
                                }`}
                              >
                                {fieldQuotesEnabled === false && (
                                  <span className="absolute top-2.5 right-2.5 flex h-5 w-5 items-center justify-center rounded-full bg-blue-600">
                                    <Check className="h-3 w-3 text-white" />
                                  </span>
                                )}
                                <p className="text-sm font-semibold text-gray-900">No, I don't</p>
                                <p className="text-xs text-gray-500 mt-1">Keep field quotes off — you can turn them on later in Calendar settings</p>
                              </button>
                            </div>
                          )}

                          {fieldQuotesSaving && (
                            <p className="text-[13px] font-medium text-gray-400">Saving…</p>
                          )}
                          {fieldQuotesError && (
                            <p className="text-[13px] font-medium text-red-600">{fieldQuotesError}</p>
                          )}

                          <button
                            type="button"
                            onClick={() => skipToNextItem(category)}
                            className="w-full px-4 py-2 text-sm font-medium text-gray-400 hover:text-gray-600 transition-colors"
                          >
                            Skip for now
                          </button>
                        </div>
                      </>
                    ) : openItemId === 'import-customers' ? (
                      /* Item: import existing customers (same AI CSV flow as the Customers page) */
                      <>
                        <AssistantMessage
                          text={
                            custImportResult
                              ? `Import complete! Your customers are in Nexus now — every booking, message, and invoice will automatically link to them from here on.`
                              : custWantsImport
                                ? `Great — upload your CSV below. Don't worry about the column layout: my AI reads the file and figures out names, emails, phones, and addresses automatically. You'll get to review everything before anything is added.`
                                : `Let's talk customers. Do you have an existing customer list you'd like to bring into Nexus — from a spreadsheet, another booking system, or your phone contacts exported as a CSV?`
                          }
                        />
                        <div className="mt-5 ml-12 space-y-3">
                          {custImportResult ? (
                            /* Import summary + continue */
                            <>
                              <div className="rounded-xl border border-green-200 bg-green-50/60 px-4 py-3 space-y-1">
                                <p className="text-[13px] font-semibold text-gray-900">
                                  {custImportResult.imported} customer{custImportResult.imported !== 1 ? 's' : ''} imported
                                </p>
                                {custImportResult.duplicates > 0 && (
                                  <p className="text-xs text-gray-500">
                                    {custImportResult.duplicates} duplicate{custImportResult.duplicates !== 1 ? 's' : ''} skipped
                                  </p>
                                )}
                                {custImportResult.errors > 0 && (
                                  <p className="text-xs text-red-600">
                                    {custImportResult.errors} row{custImportResult.errors !== 1 ? 's' : ''} couldn't be imported
                                  </p>
                                )}
                              </div>
                              <button
                                type="button"
                                onClick={async () => {
                                  await markItemComplete('customers', 'import-customers');
                                  advanceAfterItem(category, 'import-customers');
                                }}
                                className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-blue-600 hover:bg-blue-700 transition-colors font-semibold text-white"
                              >
                                <Check className="h-4 w-4" />
                                Continue
                              </button>
                            </>
                          ) : custWantsImport ? (
                            /* CSV upload + parse */
                            <>
                              <label
                                className={`flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-4 py-6 cursor-pointer transition-colors ${
                                  custFile ? 'border-blue-400 bg-blue-50' : 'border-gray-300 bg-gray-50 hover:border-blue-300'
                                }`}
                              >
                                <input
                                  type="file"
                                  accept=".csv,text/csv"
                                  className="hidden"
                                  onChange={(e) => {
                                    setCustFile(e.target.files?.[0] ?? null);
                                    setCustError(null);
                                  }}
                                />
                                <Upload className={`h-5 w-5 ${custFile ? 'text-blue-600' : 'text-gray-400'}`} />
                                <p className="text-sm font-semibold text-gray-900">
                                  {custFile ? custFile.name : 'Choose your CSV file'}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {custFile ? 'Tap to choose a different file' : 'Exported from a spreadsheet or another system'}
                                </p>
                              </label>

                              {custError && (
                                <p className="text-[13px] font-medium text-red-600">{custError}</p>
                              )}

                              <button
                                type="button"
                                onClick={handleParseCustomerCsv}
                                disabled={!custFile || custParsing}
                                className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-blue-600 hover:bg-blue-700 transition-colors font-semibold text-white disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {custParsing ? (
                                  <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                                    Reading your file…
                                  </>
                                ) : (
                                  <>
                                    <Sparkles className="h-4 w-4" />
                                    Parse & preview customers
                                  </>
                                )}
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  setCustWantsImport(false);
                                  setCustFile(null);
                                  setCustError(null);
                                }}
                                className="w-full px-4 py-2 text-sm font-medium text-gray-400 hover:text-gray-600 transition-colors"
                              >
                                Back
                              </button>
                            </>
                          ) : (
                            /* Question: any customers to import? */
                            <div className="space-y-3">
                              <button
                                type="button"
                                onClick={() => setCustWantsImport(true)}
                                className="w-full text-left p-4 rounded-xl border-2 border-gray-200 bg-gray-50 hover:border-blue-400 transition-colors"
                              >
                                <p className="text-sm font-semibold text-gray-900">Yes — I have a CSV file</p>
                                <p className="text-xs text-gray-500 mt-1">Import your existing customers so their history starts on day one</p>
                              </button>
                              <button
                                type="button"
                                onClick={async () => {
                                  await markItemComplete('customers', 'import-customers');
                                  advanceAfterItem(category, 'import-customers');
                                }}
                                className="w-full text-left p-4 rounded-xl border-2 border-gray-200 bg-gray-50 hover:border-gray-300 transition-colors"
                              >
                                <p className="text-sm font-semibold text-gray-900">No — I have no existing customers to add</p>
                                <p className="text-xs text-gray-500 mt-1">No problem — Nexus builds your customer list automatically as bookings come in</p>
                              </button>
                            </div>
                          )}

                          {!custImportResult && (
                            <button
                              type="button"
                              onClick={() => skipToNextItem(category)}
                              className="w-full px-4 py-2 text-sm font-medium text-gray-400 hover:text-gray-600 transition-colors"
                            >
                              Skip for now
                            </button>
                          )}
                        </div>

                        {/* Review + import modal (same component the Customers page uses) */}
                        {selectedBusiness?.id && (
                          <CustomerImportPreviewModal
                            isOpen={custPreviewOpen}
                            onClose={() => setCustPreviewOpen(false)}
                            customers={custParsed}
                            onImport={handleImportCustomers}
                            businessId={selectedBusiness.id}
                          />
                        )}
                      </>
                    ) : category.hasIntro && category.items.length > 0 ? (
                      /* Category intro/pitch screen (shown while untouched) */
                      <>
                        <AssistantMessage
                          text={`Now for one of the most powerful parts of Nexus: messaging.\n\nWhen you connect your email and texting, every customer conversation flows into ONE inbox — and Nexus automatically links each message to the right customer and their bookings. That unlocks:\n\n• Booking confirmations, reminders, and follow-ups that send themselves\n• Fully automated marketing campaigns — email and text blasts, promotions, and re-engagement sequences sent to your customer list on autopilot\n• Full conversation history on every customer, so nothing gets lost\n• AI-assisted replies and automations working on your behalf\n\nBusinesses that connect messaging book more jobs with less back-and-forth. Let's set yours up.`}
                        />
                        <div className="mt-6">
                          <button
                            type="button"
                            onClick={() => setOpenItemId(firstActionableItem(category)?.id ?? null)}
                            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-blue-600 hover:bg-blue-700 transition-colors font-semibold text-white"
                          >
                            Get started
                            <ChevronRight className="h-4 w-4" />
                          </button>
                        </div>
                      </>
                    ) : (
                      /* Empty category (items not built yet) */
                      <>
                        <AssistantMessage
                          text={`Let's configure your ${category.title.toLowerCase()}. ${category.description}.`}
                        />
                        <div className="mt-6 rounded-xl border border-dashed border-gray-300 p-6 text-center text-sm text-gray-400">
                          Configuration items for {category.title} coming here next.
                        </div>
                      </>
                    )}
                  </>
                );
              })()
            ) : (
              /* ── Step 2: the setup hub — category timeline ── */
              <>
                <AssistantMessage
                  text={
                    basicSetupDone
                      ? `Congratulations — your basic setup is complete!\n\nNexus is now configured and ready for business: you can take bookings, message customers, and run your day-to-day starting right now.\n\nWhen you're ready for more, continue through the Advanced Setup below to unlock Nexus's true potential. And if you skipped anything, just tap a category to expand it and revisit those steps anytime.`
                      : `Now for the fun part — let's get your system configured. Work through these in order, or jump around; you can always come back to any step later.`
                  }
                />
                {/* Once basic setup is done, categories 1–6 collapse into a
                    single summary row; expanding it brings the full cards back
                    so skipped steps can be revisited. */}
                {basicSetupDone && (
                  <button
                    type="button"
                    onClick={() => setBasicListExpanded((v) => !v)}
                    className="mt-6 w-full flex items-center gap-3 rounded-xl border-2 border-green-300 bg-green-50/60 px-4 py-3 text-left hover:bg-green-50 transition-colors"
                  >
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-green-500">
                      <Check className="h-5 w-5 text-white" />
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-[15px] font-semibold text-gray-900">
                        Basic setup — {setupCategories.length}/{setupCategories.length} completed
                      </span>
                      <span className={`block text-[12px] ${totalSkippedSteps > 0 ? 'text-amber-600' : 'text-gray-500'}`}>
                        {totalSkippedSteps > 0
                          ? `${totalSkippedSteps} step${totalSkippedSteps === 1 ? ' was' : 's were'} skipped — expand to revisit them anytime`
                          : 'Every step done — expand to revisit any of them'}
                      </span>
                    </span>
                    <ChevronDown
                      className={`h-4 w-4 text-gray-400 shrink-0 transition-transform ${basicListExpanded ? 'rotate-180' : ''}`}
                    />
                  </button>
                )}
                {(!basicSetupDone || basicListExpanded) && (
                <div className={`${basicSetupDone ? 'mt-3' : 'mt-6'} space-y-3`}>
                  {setupCategories.map((category, index) => {
                    const { doneIds, done, total, skippedCount, allDone, doneWithSkips } =
                      categoryProgress(category);
                    const expanded = expandedCategories.has(category.id);
                    const Icon = category.icon;
                    return (
                      <div
                        key={category.id}
                        className={`rounded-xl border-2 transition-colors ${
                          allDone
                            ? 'border-green-300 bg-green-50/60'
                            : doneWithSkips
                              ? 'border-amber-300 bg-amber-50/50'
                              : 'border-gray-200 bg-white'
                        }`}
                      >
                        {/* Header — clicking picks up at the step they left off on */}
                        <button
                          type="button"
                          onClick={() => openCategory(category)}
                          className="w-full px-4 pt-4 pb-1 text-left"
                        >
                          <div className="flex items-center gap-4">
                            <div
                              className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${
                                allDone
                                  ? 'bg-green-500'
                                  : doneWithSkips
                                    ? 'bg-amber-400'
                                    : 'bg-gradient-to-br from-blue-500 to-indigo-600'
                              }`}
                            >
                              {allDone ? (
                                <Check className="h-5 w-5 text-white" />
                              ) : doneWithSkips ? (
                                <Minus className="h-5 w-5 text-white" />
                              ) : (
                                <Icon className="h-5 w-5 text-white" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="text-[15px] font-semibold text-gray-900">
                                  {index + 1}. {category.title}
                                </p>
                                {allDone && (
                                  <span className="px-2 py-0.5 rounded-full bg-green-500 text-white text-[10px] font-bold uppercase tracking-wide">
                                    Completed
                                  </span>
                                )}
                                {doneWithSkips && (
                                  <span className="px-2 py-0.5 rounded-full bg-amber-400 text-white text-[10px] font-bold uppercase tracking-wide whitespace-nowrap">
                                    Done · {skippedCount} skipped
                                  </span>
                                )}
                              </div>
                              <p className="text-[12px] text-gray-500 truncate">
                                {doneWithSkips
                                  ? `Finished — ${skippedCount} skipped step${skippedCount === 1 ? '' : 's'} you may want to come back to`
                                  : category.description}
                              </p>
                            </div>
                            <ChevronRight className="h-4 w-4 text-gray-400 shrink-0" />
                          </div>

                          {total > 0 && (
                            <div className="mt-3 ml-14 flex items-center gap-2">
                              <div className="h-1.5 flex-1 rounded-full bg-gray-100 overflow-hidden">
                                <div
                                  className={`h-full rounded-full transition-all ${
                                    allDone ? 'bg-green-500' : doneWithSkips ? 'bg-amber-400' : 'bg-blue-500'
                                  }`}
                                  style={{ width: `${((done + (doneWithSkips ? skippedCount : 0)) / total) * 100}%` }}
                                />
                              </div>
                              <span className="text-[11px] font-medium text-gray-400 whitespace-nowrap">
                                {done}/{total} completed
                                {skippedCount > 0 ? ` · ${skippedCount} skipped` : ''}
                              </span>
                            </div>
                          )}
                        </button>

                        {/* Expandable step list — steps deep-link into the wizard */}
                        {total > 0 && (
                          <div className="px-4 pb-3">
                            <button
                              type="button"
                              onClick={() =>
                                setExpandedCategories((prev) => {
                                  const next = new Set(prev);
                                  if (next.has(category.id)) next.delete(category.id);
                                  else next.add(category.id);
                                  return next;
                                })
                              }
                              className="ml-14 mt-1.5 inline-flex items-center gap-1 text-[12px] font-medium text-gray-400 hover:text-gray-600 transition-colors"
                            >
                              <ChevronDown
                                className={`h-3.5 w-3.5 transition-transform ${expanded ? 'rotate-180' : ''}`}
                              />
                              {expanded ? 'Hide steps' : `Show steps (${total})`}
                            </button>

                            {expanded && (
                              <div className="mt-2 ml-14 space-y-1">
                                {category.items.map((item) => {
                                  const itemDone = doneIds.has(item.id);
                                  const itemSkipped =
                                    !itemDone && (skippedItems[category.id] || []).includes(item.id);
                                  return (
                                    <button
                                      key={item.id}
                                      type="button"
                                      onClick={() => {
                                        setOpenCategoryId(category.id);
                                        setOpenItemId(item.id);
                                      }}
                                      className="w-full flex items-center gap-2 rounded-lg px-2 py-1.5 text-left hover:bg-gray-100/80 transition-colors"
                                    >
                                      <span
                                        className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full ${
                                          itemDone
                                            ? 'bg-green-500'
                                            : itemSkipped
                                              ? 'bg-amber-400'
                                              : 'border-2 border-gray-300 bg-white'
                                        }`}
                                      >
                                        {itemDone && <Check className="h-2.5 w-2.5 text-white" />}
                                        {itemSkipped && <Minus className="h-2.5 w-2.5 text-white" />}
                                      </span>
                                      <span
                                        className={`flex-1 text-[12px] ${
                                          itemDone ? 'text-gray-400 line-through' : itemSkipped ? 'text-amber-600' : 'text-gray-600'
                                        }`}
                                      >
                                        {item.title}
                                      </span>
                                      {itemSkipped && (
                                        <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 text-[10px] font-semibold uppercase">
                                          Skipped
                                        </span>
                                      )}
                                      <ChevronRight className="h-3 w-3 text-gray-300 shrink-0" />
                                    </button>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                )}

                {/* ── Advanced Setup — unlocked once the basic categories are done ── */}
                {basicSetupDone && (
                  <div className="mt-8">
                    <div className="flex items-center gap-2 mb-1">
                      <Sparkles className="h-4 w-4 text-indigo-500" />
                      <span className="text-[11px] font-bold uppercase tracking-wider text-gray-400">
                        Advanced setup
                      </span>
                    </div>
                    <p className="text-[13px] text-gray-500 mb-3">
                      Take Nexus beyond the basics — automation, marketing, and the tools that do the work for you.
                    </p>
                    <div className="rounded-xl border-2 border-dashed border-indigo-200 bg-indigo-50/40 p-6 text-center">
                      <p className="text-sm font-semibold text-gray-700">Advanced steps are coming here next</p>
                      <p className="text-xs text-gray-500 mt-1">
                        This is where you'll unlock Nexus's true potential.
                      </p>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
